/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql, useQuery } from '@apollo/client';

type Language = {
  isoCode: string;
  name: string;
  native: string;
};

type Currency = {
  isoCode: string;
  name: string;
};

type Country = {
  isoCode: string;
  name: string;
  native: string;
  flag: string;
  phonePrefix: string;
  regions: {
    name: string;
    code: string;
  }[];
  languages: Language[];
  currencies: Currency[];
};


export const useRegionsca = (isoCode: string, code: string, skipCondition : boolean) => {
  const { loading, error, data } = useQuery<{
    countries: Country[];
  }>(
    gql`
      query Regionsca($isoCode: String!, $code: String!) {
        regionsca(isoCode: $isoCode, code: $code) {
          code
          name
        }
      }
    `,
    {
      variables: {isoCode, code},
      skip : skipCondition,
      context:{
        clientName:"locale"
      }
    }
  );
  return {
    loading,
    error,
    data
  }

}


export const useLocales = () => {
  const data  = {
    "data": {
      "allCountries": [
        {
          "isoCode": "AD",
          "name": "Andorra",
          "native": "Andorra",
          "flag": "🇦🇩",
          "phonePrefix": "+376",
          "regions": [
            {
              "code": "07",
              "name": "Andorra la Vella"
            },
            {
              "code": "02",
              "name": "Canillo"
            },
            {
              "code": "03",
              "name": "Encamp"
            },
            {
              "code": "08",
              "name": "Escaldes-Engordany"
            },
            {
              "code": "04",
              "name": "La Massana"
            },
            {
              "code": "05",
              "name": "Ordino"
            },
            {
              "code": "06",
              "name": "Sant Julià de Lòria"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "AE",
          "name": "United Arab Emirates",
          "native": "دولة الإمارات العربية المتحدة",
          "flag": "🇦🇪",
          "phonePrefix": "+971",
          "regions": [
            {
              "code": "AZ",
              "name": "Abu Dhabi"
            },
            {
              "code": "AJ",
              "name": "Ajman"
            },
            {
              "code": "DU",
              "name": "Dubai"
            },
            {
              "code": "FU",
              "name": "Fujairah"
            },
            {
              "code": "RK",
              "name": "Ras al Khaimah"
            },
            {
              "code": "SH",
              "name": "Sharjah"
            },
            {
              "code": "UQ",
              "name": "Umm Al Quwain"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AF",
          "name": "Afghanistan",
          "native": "افغانستان",
          "flag": "🇦🇫",
          "phonePrefix": "+93",
          "regions": [
            {
              "code": "BDS",
              "name": "Badakhshan"
            },
            {
              "code": "BDG",
              "name": "Badghis"
            },
            {
              "code": "BGL",
              "name": "Baghlan"
            },
            {
              "code": "BAL",
              "name": "Balkh"
            },
            {
              "code": "BAM",
              "name": "Bamyan"
            },
            {
              "code": "DAY",
              "name": "Daykundi"
            },
            {
              "code": "FRA",
              "name": "Farah"
            },
            {
              "code": "FYB",
              "name": "Faryab"
            },
            {
              "code": "GHA",
              "name": "Ghazni"
            },
            {
              "code": "GHO",
              "name": "Ghor"
            },
            {
              "code": "HEL",
              "name": "Helmand"
            },
            {
              "code": "HER",
              "name": "Herat"
            },
            {
              "code": "JOW",
              "name": "Jowzjan"
            },
            {
              "code": "KAB",
              "name": "Kabul"
            },
            {
              "code": "KAN",
              "name": "Kandahar"
            },
            {
              "code": "KAP",
              "name": "Kapisa"
            },
            {
              "code": "KHO",
              "name": "Khost"
            },
            {
              "code": "KNR",
              "name": "Kunar"
            },
            {
              "code": "KDZ",
              "name": "Kunduz"
            },
            {
              "code": "LAG",
              "name": "Laghman"
            },
            {
              "code": "LOW",
              "name": "Logar"
            },
            {
              "code": "WAR",
              "name": "Maidan Wardak"
            },
            {
              "code": "NAN",
              "name": "Nangarhar"
            },
            {
              "code": "NIM",
              "name": "Nimruz"
            },
            {
              "code": "NUR",
              "name": "Nuristan"
            },
            {
              "code": "PIA",
              "name": "Paktia"
            },
            {
              "code": "PKA",
              "name": "Paktika"
            },
            {
              "code": "PAN",
              "name": "Panjshir"
            },
            {
              "code": "PAR",
              "name": "Parwan"
            },
            {
              "code": "SAM",
              "name": "Samangan"
            },
            {
              "code": "SAR",
              "name": "Sar-e Pol"
            },
            {
              "code": "TAK",
              "name": "Takhar"
            },
            {
              "code": "ORU",
              "name": "Urozgan"
            },
            {
              "code": "ZAB",
              "name": "Zabul"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AG",
          "name": "Antigua and Barbuda",
          "native": "Antigua and Barbuda",
          "flag": "🇦🇬",
          "phonePrefix": "+1268",
          "regions": [
            {
              "code": "01",
              "name": "Antigua Island"
            },
            {
              "code": "02",
              "name": "Barbuda Island"
            },
            {
              "code": "04",
              "name": "Bird Island"
            },
            {
              "code": "05",
              "name": "Bishop Island"
            },
            {
              "code": "06",
              "name": "Blake Island"
            },
            {
              "code": "09",
              "name": "Crump Island"
            },
            {
              "code": "10",
              "name": "Dulcina Island"
            },
            {
              "code": "11",
              "name": "Exchange Island"
            },
            {
              "code": "12",
              "name": "Five Islands"
            },
            {
              "code": "13",
              "name": "Great Bird Island"
            },
            {
              "code": "14",
              "name": "Green Island"
            },
            {
              "code": "15",
              "name": "Guiana Island"
            },
            {
              "code": "17",
              "name": "Hawes Island"
            },
            {
              "code": "16",
              "name": "Hells Gate Island"
            },
            {
              "code": "18",
              "name": "Henry Island"
            },
            {
              "code": "19",
              "name": "Johnson Island"
            },
            {
              "code": "20",
              "name": "Kid Island"
            },
            {
              "code": "22",
              "name": "Lobster Island"
            },
            {
              "code": "24",
              "name": "Maiden Island"
            },
            {
              "code": "25",
              "name": "Moor Island"
            },
            {
              "code": "26",
              "name": "Nanny Island"
            },
            {
              "code": "27",
              "name": "Pelican Island"
            },
            {
              "code": "28",
              "name": "Prickly Pear Island"
            },
            {
              "code": "29",
              "name": "Rabbit Island"
            },
            {
              "code": "31",
              "name": "Red Head Island"
            },
            {
              "code": "03",
              "name": "Redonda Island"
            },
            {
              "code": "32",
              "name": "Sandy Island"
            },
            {
              "code": "33",
              "name": "Smith Island"
            },
            {
              "code": "34",
              "name": "The Sisters"
            },
            {
              "code": "35",
              "name": "Vernon Island"
            },
            {
              "code": "36",
              "name": "Wicked Will Island"
            },
            {
              "code": "37",
              "name": "York Island"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AI",
          "name": "Anguilla",
          "native": "Anguilla",
          "flag": "🇦🇮",
          "phonePrefix": "+1264",
          "regions": [
            {
              "code": "01",
              "name": "Anguilla"
            },
            {
              "code": "02",
              "name": "Anguillita Island"
            },
            {
              "code": "03",
              "name": "Blowing Rock"
            },
            {
              "code": "04",
              "name": "Cove Cay"
            },
            {
              "code": "05",
              "name": "Crocus Cay"
            },
            {
              "code": "06",
              "name": "Deadman's Cay"
            },
            {
              "code": "07",
              "name": "Dog Island"
            },
            {
              "code": "08",
              "name": "East Cay"
            },
            {
              "code": "09",
              "name": "Little Island"
            },
            {
              "code": "10",
              "name": "Little Scrub Island"
            },
            {
              "code": "11",
              "name": "Mid Cay"
            },
            {
              "code": "12",
              "name": "North Cay"
            },
            {
              "code": "13",
              "name": "Prickly Pear Cays"
            },
            {
              "code": "14",
              "name": "Rabbit Island"
            },
            {
              "code": "15",
              "name": "Sandy Island/Sand Island"
            },
            {
              "code": "16",
              "name": "Scilly Cay"
            },
            {
              "code": "17",
              "name": "Scrub Island"
            },
            {
              "code": "18",
              "name": "Seal Island"
            },
            {
              "code": "19",
              "name": "Sombrero/Hat Island"
            },
            {
              "code": "20",
              "name": "South Cay"
            },
            {
              "code": "21",
              "name": "South Wager Island"
            },
            {
              "code": "22",
              "name": "West Cay"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AL",
          "name": "Albania",
          "native": "Shqipëria",
          "flag": "🇦🇱",
          "phonePrefix": "+355",
          "regions": [
            {
              "code": "01",
              "name": "Berat"
            },
            {
              "code": "09",
              "name": "Dibër"
            },
            {
              "code": "02",
              "name": "Durrës"
            },
            {
              "code": "03",
              "name": "Elbasan"
            },
            {
              "code": "04",
              "name": "Fier"
            },
            {
              "code": "05",
              "name": "Gjirokastër"
            },
            {
              "code": "06",
              "name": "Korçë"
            },
            {
              "code": "07",
              "name": "Kukës"
            },
            {
              "code": "08",
              "name": "Lezhë"
            },
            {
              "code": "10",
              "name": "Shkodër"
            },
            {
              "code": "11",
              "name": "Tirana"
            },
            {
              "code": "12",
              "name": "Vlorë"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AM",
          "name": "Armenia",
          "native": "Հայաստան",
          "flag": "🇦🇲",
          "phonePrefix": "+374",
          "regions": [
            {
              "code": "AG",
              "name": "Aragatsotn"
            },
            {
              "code": "AR",
              "name": "Ararat"
            },
            {
              "code": "AV",
              "name": "Armavir"
            },
            {
              "code": "GR",
              "name": "Gegharkunik"
            },
            {
              "code": "KT",
              "name": "Kotayk"
            },
            {
              "code": "LO",
              "name": "Lori"
            },
            {
              "code": "SH",
              "name": "Shirak"
            },
            {
              "code": "SU",
              "name": "Syunik"
            },
            {
              "code": "TV",
              "name": "Tavush"
            },
            {
              "code": "VD",
              "name": "Vayots Dzor"
            },
            {
              "code": "ER",
              "name": "Yerevan"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AO",
          "name": "Angola",
          "native": "Angola",
          "flag": "🇦🇴",
          "phonePrefix": "+244",
          "regions": [
            {
              "code": "BGO",
              "name": "Bengo"
            },
            {
              "code": "BGU",
              "name": "Benguela"
            },
            {
              "code": "BIE",
              "name": "Bié"
            },
            {
              "code": "CAB",
              "name": "Cabinda"
            },
            {
              "code": "CCU",
              "name": "Cuando Cubango"
            },
            {
              "code": "CNO",
              "name": "Cuanza Norte"
            },
            {
              "code": "CUS",
              "name": "Cuanza Sul"
            },
            {
              "code": "CNN",
              "name": "Cunene"
            },
            {
              "code": "HUA",
              "name": "Huambo"
            },
            {
              "code": "HUI",
              "name": "Huíla"
            },
            {
              "code": "LUA",
              "name": "Luanda"
            },
            {
              "code": "LNO",
              "name": "Lunda Norte"
            },
            {
              "code": "LSU",
              "name": "Lunda Sul"
            },
            {
              "code": "MAL",
              "name": "Malanje"
            },
            {
              "code": "MOX",
              "name": "Moxico"
            },
            {
              "code": "NAM",
              "name": "Namibe"
            },
            {
              "code": "UIG",
              "name": "Uíge"
            },
            {
              "code": "ZAI",
              "name": "Zaire"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AQ",
          "name": "Antarctica",
          "native": "Antarctica",
          "flag": "🇦🇶",
          "phonePrefix": "+672",
          "regions": [
            {
              "code": "AQ",
              "name": "Antarctica"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AR",
          "name": "Argentina",
          "native": "Argentina",
          "flag": "🇦🇷",
          "phonePrefix": "+54",
          "regions": [
            {
              "code": "B",
              "name": "Buenos Aires"
            },
            {
              "code": "C",
              "name": "Capital Federal"
            },
            {
              "code": "K",
              "name": "Catamarca"
            },
            {
              "code": "H",
              "name": "Chaco"
            },
            {
              "code": "U",
              "name": "Chubut"
            },
            {
              "code": "X",
              "name": "Córdoba"
            },
            {
              "code": "W",
              "name": "Corrientes"
            },
            {
              "code": "E",
              "name": "Entre Ríos"
            },
            {
              "code": "P",
              "name": "Formosa"
            },
            {
              "code": "Y",
              "name": "Jujuy"
            },
            {
              "code": "L",
              "name": "La Pampa"
            },
            {
              "code": "F",
              "name": "La Rioja"
            },
            {
              "code": "M",
              "name": "Mendoza"
            },
            {
              "code": "N",
              "name": "Misiones"
            },
            {
              "code": "Q",
              "name": "Neuquén"
            },
            {
              "code": "R",
              "name": "Río Negro"
            },
            {
              "code": "A",
              "name": "Salta"
            },
            {
              "code": "J",
              "name": "San Juan"
            },
            {
              "code": "D",
              "name": "San Luis"
            },
            {
              "code": "Z",
              "name": "Santa Cruz"
            },
            {
              "code": "S",
              "name": "Santa Fe"
            },
            {
              "code": "G",
              "name": "Santiago del Estero"
            },
            {
              "code": "V",
              "name": "Tierra del Fuego"
            },
            {
              "code": "T",
              "name": "Tucumán"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AS",
          "name": "American Samoa",
          "native": "American Samoa",
          "flag": "🇦🇸",
          "phonePrefix": "+1684",
          "regions": [
            {
              "code": "01",
              "name": "Tutuila"
            },
            {
              "code": "02",
              "name": "Aunu'u"
            },
            {
              "code": "03",
              "name": "Ta'ū"
            },
            {
              "code": "04",
              "name": "Ofu‑Olosega"
            },
            {
              "code": "21",
              "name": "Rose Atoll"
            },
            {
              "code": "22",
              "name": "Swains Island"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "AT",
          "name": "Austria",
          "native": "Österreich",
          "flag": "🇦🇹",
          "phonePrefix": "+43",
          "regions": [
            {
              "code": "1",
              "name": "Burgenland"
            },
            {
              "code": "2",
              "name": "Kärnten"
            },
            {
              "code": "3",
              "name": "Niederösterreich"
            },
            {
              "code": "4",
              "name": "Oberösterreich"
            },
            {
              "code": "5",
              "name": "Salzburg"
            },
            {
              "code": "6",
              "name": "Steiermark"
            },
            {
              "code": "7",
              "name": "Tirol"
            },
            {
              "code": "8",
              "name": "Vorarlberg"
            },
            {
              "code": "9",
              "name": "Wien"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "AU",
          "name": "Australia",
          "native": "Australia",
          "flag": "🇦🇺",
          "phonePrefix": "+61",
          "regions": [
            {
              "code": "ACT",
              "name": "Australian Capital Territory"
            },
            {
              "code": "NSW",
              "name": "New South Wales"
            },
            {
              "code": "NT",
              "name": "Northern Territory"
            },
            {
              "code": "QLD",
              "name": "Queensland"
            },
            {
              "code": "SA",
              "name": "South Australia"
            },
            {
              "code": "TAS",
              "name": "Tasmania"
            },
            {
              "code": "VIC",
              "name": "Victoria"
            },
            {
              "code": "WA",
              "name": "Western Australia"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AW",
          "name": "Aruba",
          "native": "Aruba",
          "flag": "🇦🇼",
          "phonePrefix": "+297",
          "regions": [
            {
              "code": "AW",
              "name": "Aruba"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "AX",
          "name": "Åland Islands",
          "native": "Åland",
          "flag": "🇦🇽",
          "phonePrefix": "+358",
          "regions": [
            {
              "code": "BR",
              "name": "Brändö"
            },
            {
              "code": "EC",
              "name": "Eckerö"
            },
            {
              "code": "FN",
              "name": "Finström"
            },
            {
              "code": "FG",
              "name": "Föglö"
            },
            {
              "code": "GT",
              "name": "Geta"
            },
            {
              "code": "HM",
              "name": "Hammarland"
            },
            {
              "code": "JM",
              "name": "Jomala"
            },
            {
              "code": "KM",
              "name": "Kumlinge"
            },
            {
              "code": "KK",
              "name": "Kökar"
            },
            {
              "code": "LE",
              "name": "Lemland"
            },
            {
              "code": "LU",
              "name": "Lumparland"
            },
            {
              "code": "MH",
              "name": "Mariehamn"
            },
            {
              "code": "SV",
              "name": "Saltvik"
            },
            {
              "code": "ST",
              "name": "Sottunga"
            },
            {
              "code": "SD",
              "name": "Sund"
            },
            {
              "code": "VR",
              "name": "Vårdö"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "AZ",
          "name": "Azerbaijan",
          "native": "Azərbaycan",
          "flag": "🇦🇿",
          "phonePrefix": "+994",
          "regions": [
            {
              "code": "ABS",
              "name": "Abşeron"
            },
            {
              "code": "AGC",
              "name": "Ağcabədi"
            },
            {
              "code": "AGM",
              "name": "Ağdam"
            },
            {
              "code": "AGS",
              "name": "Ağdaş"
            },
            {
              "code": "AGA",
              "name": "Ağstafa"
            },
            {
              "code": "AGU",
              "name": "Ağsu"
            },
            {
              "code": "AST",
              "name": "Astara"
            },
            {
              "code": "BAK",
              "name": "Bakı"
            },
            {
              "code": "BAB",
              "name": "Babək"
            },
            {
              "code": "BAL",
              "name": "Balakən"
            },
            {
              "code": "BAR",
              "name": "Bərdə"
            },
            {
              "code": "BEY",
              "name": "Beyləqan"
            },
            {
              "code": "BIL",
              "name": "Biləsuvar"
            },
            {
              "code": "CAB",
              "name": "Cəbrayıl"
            },
            {
              "code": "CAL",
              "name": "Cəlilabad"
            },
            {
              "code": "CUL",
              "name": "Culfa"
            },
            {
              "code": "DAS",
              "name": "Daşkəsən"
            },
            {
              "code": "FUZ",
              "name": "Füzuli"
            },
            {
              "code": "GAD",
              "name": "Gədəbəy"
            },
            {
              "code": "GOR",
              "name": "Goranboy"
            },
            {
              "code": "GOY",
              "name": "Göyçay"
            },
            {
              "code": "GYG",
              "name": "Göygöl"
            },
            {
              "code": "HAC",
              "name": "Hacıqabul"
            },
            {
              "code": "IMI",
              "name": "İmişli"
            },
            {
              "code": "ISM",
              "name": "İsmayıllı"
            },
            {
              "code": "KAL",
              "name": "Kəlbəcər"
            },
            {
              "code": "KAN",
              "name": "Kǝngǝrli"
            },
            {
              "code": "KUR",
              "name": "Kürdəmir"
            },
            {
              "code": "LAC",
              "name": "Laçın"
            },
            {
              "code": "LAN",
              "name": "Lənkəran"
            },
            {
              "code": "LER",
              "name": "Lerik"
            },
            {
              "code": "MAS",
              "name": "Masallı"
            },
            {
              "code": "NEF",
              "name": "Neftçala"
            },
            {
              "code": "OGU",
              "name": "Oğuz"
            },
            {
              "code": "ORD",
              "name": "Ordubad"
            },
            {
              "code": "QAB",
              "name": "Qəbələ"
            },
            {
              "code": "QAX",
              "name": "Qax"
            },
            {
              "code": "QAZ",
              "name": "Qazax"
            },
            {
              "code": "QOB",
              "name": "Qobustan"
            },
            {
              "code": "QBA",
              "name": "Quba"
            },
            {
              "code": "QBI",
              "name": "Qubadli"
            },
            {
              "code": "QUS",
              "name": "Qusar"
            },
            {
              "code": "SAT",
              "name": "Saatlı"
            },
            {
              "code": "SAB",
              "name": "Sabirabad"
            },
            {
              "code": "SBN",
              "name": "Şabran"
            },
            {
              "code": "SAD",
              "name": "Sədərək"
            },
            {
              "code": "SAH",
              "name": "Şahbuz"
            },
            {
              "code": "SAK",
              "name": "Şəki"
            },
            {
              "code": "SAL",
              "name": "Salyan"
            },
            {
              "code": "SMI",
              "name": "Şamaxı"
            },
            {
              "code": "SKR",
              "name": "Şəmkir"
            },
            {
              "code": "SMX",
              "name": "Samux"
            },
            {
              "code": "SAR",
              "name": "Şərur"
            },
            {
              "code": "SIY",
              "name": "Siyəzən"
            },
            {
              "code": "SUS",
              "name": "Şuşa"
            },
            {
              "code": "TAR",
              "name": "Tərtər"
            },
            {
              "code": "TOV",
              "name": "Tovuz"
            },
            {
              "code": "UCA",
              "name": "Ucar"
            },
            {
              "code": "XAC",
              "name": "Xaçmaz"
            },
            {
              "code": "XIZ",
              "name": "Xızı"
            },
            {
              "code": "XCI",
              "name": "Xocalı"
            },
            {
              "code": "XVD",
              "name": "Xocavənd"
            },
            {
              "code": "YAR",
              "name": "Yardımlı"
            },
            {
              "code": "YEV",
              "name": "Yevlax"
            },
            {
              "code": "ZAN",
              "name": "Zəngilan"
            },
            {
              "code": "ZAQ",
              "name": "Zaqatala"
            },
            {
              "code": "ZAR",
              "name": "Zərdab"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BA",
          "name": "Bosnia and Herzegovina",
          "native": "Bosna i Hercegovina",
          "flag": "🇧🇦",
          "phonePrefix": "+387",
          "regions": [
            {
              "code": "BRC",
              "name": "Brčko Distrikt"
            },
            {
              "code": "BIH",
              "name": "Federacija Bosne i Hercegovine"
            },
            {
              "code": "SRP",
              "name": "Republika Srpska"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BB",
          "name": "Barbados",
          "native": "Barbados",
          "flag": "🇧🇧",
          "phonePrefix": "+1246",
          "regions": [
            {
              "code": "01",
              "name": "Christ Church"
            },
            {
              "code": "02",
              "name": "Saint Andrew"
            },
            {
              "code": "03",
              "name": "Saint George"
            },
            {
              "code": "04",
              "name": "Saint James"
            },
            {
              "code": "05",
              "name": "Saint John"
            },
            {
              "code": "06",
              "name": "Saint Joseph"
            },
            {
              "code": "07",
              "name": "Saint Lucy"
            },
            {
              "code": "08",
              "name": "Saint Michael"
            },
            {
              "code": "09",
              "name": "Saint Peter"
            },
            {
              "code": "10",
              "name": "Saint Philip"
            },
            {
              "code": "11",
              "name": "Saint Thomas"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BD",
          "name": "Bangladesh",
          "native": "Bangladesh",
          "flag": "🇧🇩",
          "phonePrefix": "+880",
          "regions": [
            {
              "code": "A",
              "name": "Barisal"
            },
            {
              "code": "B",
              "name": "Chittagong"
            },
            {
              "code": "C",
              "name": "Dhaka"
            },
            {
              "code": "D",
              "name": "Khulna"
            },
            {
              "code": "M",
              "name": "Mymensingh"
            },
            {
              "code": "E",
              "name": "Rajshahi"
            },
            {
              "code": "F",
              "name": "Rangpur"
            },
            {
              "code": "G",
              "name": "Sylhet"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BE",
          "name": "Belgium",
          "native": "België",
          "flag": "🇧🇪",
          "phonePrefix": "+32",
          "regions": [
            {
              "code": "BRU",
              "name": "Brussels"
            },
            {
              "code": "VLG",
              "name": "Flanders"
            },
            {
              "code": "WAL",
              "name": "Wallonia"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "BF",
          "name": "Burkina Faso",
          "native": "Burkina Faso",
          "flag": "🇧🇫",
          "phonePrefix": "+226",
          "regions": [
            {
              "code": "BAL",
              "name": "Balé"
            },
            {
              "code": "BAM",
              "name": "Bam/Lake Bam"
            },
            {
              "code": "BAN",
              "name": "Banwa Province"
            },
            {
              "code": "BAZ",
              "name": "Bazèga"
            },
            {
              "code": "BGR",
              "name": "Bougouriba"
            },
            {
              "code": "BLG",
              "name": "Boulgou Province"
            },
            {
              "code": "BLK",
              "name": "Boulkiemdé"
            },
            {
              "code": "COM",
              "name": "Comoé/Komoe"
            },
            {
              "code": "GAN",
              "name": "Ganzourgou Province"
            },
            {
              "code": "GNA",
              "name": "Gnagna"
            },
            {
              "code": "GOU",
              "name": "Gourma Province"
            },
            {
              "code": "HOU",
              "name": "Houet"
            },
            {
              "code": "IOB",
              "name": "Ioba"
            },
            {
              "code": "KAD",
              "name": "Kadiogo"
            },
            {
              "code": "KEN",
              "name": "Kénédougou"
            },
            {
              "code": "KMD",
              "name": "Komondjari"
            },
            {
              "code": "KMP",
              "name": "Kompienga"
            },
            {
              "code": "KOS",
              "name": "Kossi Province"
            },
            {
              "code": "KOP",
              "name": "Koulpélogo"
            },
            {
              "code": "KOT",
              "name": "Kouritenga"
            },
            {
              "code": "KOW",
              "name": "Kourwéogo"
            },
            {
              "code": "LER",
              "name": "Léraba"
            },
            {
              "code": "LOR",
              "name": "Loroum"
            },
            {
              "code": "MOU",
              "name": "Mouhoun"
            },
            {
              "code": "NAM",
              "name": "Namentenga"
            },
            {
              "code": "NAO",
              "name": "Naouri/Nahouri"
            },
            {
              "code": "NAY",
              "name": "Nayala"
            },
            {
              "code": "NOU",
              "name": "Noumbiel"
            },
            {
              "code": "OUB",
              "name": "Oubritenga"
            },
            {
              "code": "OUD",
              "name": "Oudalan"
            },
            {
              "code": "PAS",
              "name": "Passoré"
            },
            {
              "code": "PON",
              "name": "Poni"
            },
            {
              "code": "SNG",
              "name": "Sanguié"
            },
            {
              "code": "SMT",
              "name": "Sanmatenga"
            },
            {
              "code": "SEN",
              "name": "Séno"
            },
            {
              "code": "SIS",
              "name": "Sissili"
            },
            {
              "code": "SOM",
              "name": "Soum"
            },
            {
              "code": "SOR",
              "name": "Sourou"
            },
            {
              "code": "TAP",
              "name": "Tapoa"
            },
            {
              "code": "TUI",
              "name": "Tui/Tuy"
            },
            {
              "code": "YAG",
              "name": "Yagha"
            },
            {
              "code": "YAT",
              "name": "Yatenga"
            },
            {
              "code": "ZIR",
              "name": "Ziro"
            },
            {
              "code": "ZON",
              "name": "Zondoma"
            },
            {
              "code": "ZOU",
              "name": "Zoundwéogo"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BG",
          "name": "Bulgaria",
          "native": "България",
          "flag": "🇧🇬",
          "phonePrefix": "+359",
          "regions": [
            {
              "code": "01",
              "name": "Blagoevgrad"
            },
            {
              "code": "02",
              "name": "Burgas"
            },
            {
              "code": "08",
              "name": "Dobrich"
            },
            {
              "code": "07",
              "name": "Gabrovo"
            },
            {
              "code": "28",
              "name": "Jambol"
            },
            {
              "code": "26",
              "name": "Khaskovo"
            },
            {
              "code": "10",
              "name": "Kjustendil"
            },
            {
              "code": "09",
              "name": "Kurdzhali"
            },
            {
              "code": "11",
              "name": "Lovech"
            },
            {
              "code": "12",
              "name": "Montana"
            },
            {
              "code": "13",
              "name": "Pazardzhik"
            },
            {
              "code": "14",
              "name": "Pernik"
            },
            {
              "code": "15",
              "name": "Pleven"
            },
            {
              "code": "16",
              "name": "Plovdiv"
            },
            {
              "code": "17",
              "name": "Razgrad"
            },
            {
              "code": "18",
              "name": "Ruse"
            },
            {
              "code": "27",
              "name": "Shumen"
            },
            {
              "code": "19",
              "name": "Silistra"
            },
            {
              "code": "20",
              "name": "Sliven"
            },
            {
              "code": "21",
              "name": "Smoljan"
            },
            {
              "code": "23",
              "name": "Sofija"
            },
            {
              "code": "22",
              "name": "Sofija-Grad"
            },
            {
              "code": "24",
              "name": "Stara Zagora"
            },
            {
              "code": "25",
              "name": "Turgovishhe"
            },
            {
              "code": "03",
              "name": "Varna"
            },
            {
              "code": "04",
              "name": "Veliko Turnovo"
            },
            {
              "code": "05",
              "name": "Vidin"
            },
            {
              "code": "06",
              "name": "Vraca"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BH",
          "name": "Bahrain",
          "native": "‏البحرين",
          "flag": "🇧🇭",
          "phonePrefix": "+973",
          "regions": [
            {
              "code": "14",
              "name": "Al Janūbīyah"
            },
            {
              "code": "13",
              "name": "Al Manāmah"
            },
            {
              "code": "15",
              "name": "Al Muḩarraq"
            },
            {
              "code": "16",
              "name": "Al Wusţá"
            },
            {
              "code": "17",
              "name": "Ash Shamālīyah"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BI",
          "name": "Burundi",
          "native": "Burundi",
          "flag": "🇧🇮",
          "phonePrefix": "+257",
          "regions": [
            {
              "code": "BB",
              "name": "Bubanza"
            },
            {
              "code": "BM",
              "name": "Bujumbura Mairie"
            },
            {
              "code": "BL",
              "name": "Bujumbura Rural"
            },
            {
              "code": "BR",
              "name": "Bururi"
            },
            {
              "code": "CA",
              "name": "Cankuzo"
            },
            {
              "code": "CI",
              "name": "Cibitoke"
            },
            {
              "code": "GI",
              "name": "Gitega"
            },
            {
              "code": "KR",
              "name": "Karuzi"
            },
            {
              "code": "KY",
              "name": "Kayanza"
            },
            {
              "code": "KI",
              "name": "Kirundo"
            },
            {
              "code": "MA",
              "name": "Makamba"
            },
            {
              "code": "MU",
              "name": "Muramvya"
            },
            {
              "code": "MY",
              "name": "Muyinga"
            },
            {
              "code": "MW",
              "name": "Mwaro"
            },
            {
              "code": "NG",
              "name": "Ngozi"
            },
            {
              "code": "RT",
              "name": "Rutana"
            },
            {
              "code": "RY",
              "name": "Ruyigi"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BJ",
          "name": "Benin",
          "native": "Bénin",
          "flag": "🇧🇯",
          "phonePrefix": "+229",
          "regions": [
            {
              "code": "AL",
              "name": "Alibori"
            },
            {
              "code": "AK",
              "name": "Atakora"
            },
            {
              "code": "AQ",
              "name": "Atlantique"
            },
            {
              "code": "BO",
              "name": "Borgou"
            },
            {
              "code": "CO",
              "name": "Collines Department"
            },
            {
              "code": "DO",
              "name": "Donga"
            },
            {
              "code": "KO",
              "name": "Kouffo"
            },
            {
              "code": "LI",
              "name": "Littoral Department"
            },
            {
              "code": "MO",
              "name": "Mono Department"
            },
            {
              "code": "OU",
              "name": "Ouémé"
            },
            {
              "code": "PL",
              "name": "Plateau"
            },
            {
              "code": "ZO",
              "name": "Zou"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BL",
          "name": "Saint Barthélemy",
          "native": "Saint-Barthélemy",
          "flag": "🇧🇱",
          "phonePrefix": "+590",
          "regions": [
            {
              "code": "02",
              "name": "Au Vent"
            },
            {
              "code": "01",
              "name": "Sous le Vent"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "BM",
          "name": "Bermuda",
          "native": "Bermuda",
          "flag": "🇧🇲",
          "phonePrefix": "+1441",
          "regions": [
            {
              "code": "03",
              "name": "City of Hamilton"
            },
            {
              "code": "01",
              "name": "Devonshire Parish"
            },
            {
              "code": "02",
              "name": "Hamilton Parish"
            },
            {
              "code": "04",
              "name": "Paget Parish"
            },
            {
              "code": "05",
              "name": "Pembroke Parish"
            },
            {
              "code": "08",
              "name": "Sandys Parish"
            },
            {
              "code": "09",
              "name": "Smith's Parish"
            },
            {
              "code": "10",
              "name": "Southampton Parish"
            },
            {
              "code": "07",
              "name": "St. George's Parish"
            },
            {
              "code": "06",
              "name": "Town of St. George"
            },
            {
              "code": "11",
              "name": "Warwick Parish"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BN",
          "name": "Brunei Darussalam",
          "native": "Negara Brunei Darussalam",
          "flag": "🇧🇳",
          "phonePrefix": "+673",
          "regions": [
            {
              "code": "BE",
              "name": "Belait"
            },
            {
              "code": "BM",
              "name": "Brunei Muara"
            },
            {
              "code": "TE",
              "name": "Temburong"
            },
            {
              "code": "TU",
              "name": "Tutong"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BO",
          "name": "Bolivia",
          "native": "Bolivia",
          "flag": "🇧🇴",
          "phonePrefix": "+591",
          "regions": [
            {
              "code": "BE",
              "name": "Beni"
            },
            {
              "code": "CH",
              "name": "Chuquisaca"
            },
            {
              "code": "CB",
              "name": "Cochabamba"
            },
            {
              "code": "LP",
              "name": "La Paz"
            },
            {
              "code": "OR",
              "name": "Oruro"
            },
            {
              "code": "PD",
              "name": "Pando"
            },
            {
              "code": "PT",
              "name": "Potosí"
            },
            {
              "code": "SC",
              "name": "Santa Cruz"
            },
            {
              "code": "TJ",
              "name": "Tarija"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BQ",
          "name": "Bonaire, Sint Eustatius and Saba",
          "native": "Bonaire",
          "flag": "🇧🇶",
          "phonePrefix": "+5997",
          "regions": [
            {
              "code": "BO",
              "name": "Bonaire"
            },
            {
              "code": "SA",
              "name": "Saba Isand"
            },
            {
              "code": "SE",
              "name": "Sint Eustatius"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "BR",
          "name": "Brazil",
          "native": "Brasil",
          "flag": "🇧🇷",
          "phonePrefix": "+55",
          "regions": [
            {
              "code": "AC",
              "name": "Acre"
            },
            {
              "code": "AL",
              "name": "Alagoas"
            },
            {
              "code": "AP",
              "name": "Amapá"
            },
            {
              "code": "AM",
              "name": "Amazonas"
            },
            {
              "code": "BA",
              "name": "Bahia"
            },
            {
              "code": "CE",
              "name": "Ceará"
            },
            {
              "code": "DF",
              "name": "Distrito Federal"
            },
            {
              "code": "ES",
              "name": "Espírito Santo"
            },
            {
              "code": "GO",
              "name": "Goiás"
            },
            {
              "code": "MA",
              "name": "Maranhão"
            },
            {
              "code": "MT",
              "name": "Mato Grosso"
            },
            {
              "code": "MS",
              "name": "Mato Grosso do Sul"
            },
            {
              "code": "MG",
              "name": "Minas Gerais"
            },
            {
              "code": "PA",
              "name": "Pará"
            },
            {
              "code": "PB",
              "name": "Paraíba"
            },
            {
              "code": "PR",
              "name": "Paraná"
            },
            {
              "code": "PE",
              "name": "Pernambuco"
            },
            {
              "code": "PI",
              "name": "Piauí"
            },
            {
              "code": "RJ",
              "name": "Rio de Janeiro"
            },
            {
              "code": "RN",
              "name": "Rio Grande do Norte"
            },
            {
              "code": "RS",
              "name": "Rio Grande do Sul"
            },
            {
              "code": "RO",
              "name": "Rondônia"
            },
            {
              "code": "RR",
              "name": "Roraima"
            },
            {
              "code": "SC",
              "name": "Santa Catarina"
            },
            {
              "code": "SP",
              "name": "São Paulo"
            },
            {
              "code": "SE",
              "name": "Sergipe"
            },
            {
              "code": "TO",
              "name": "Tocantins"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BS",
          "name": "Bahamas",
          "native": "Bahamas",
          "flag": "🇧🇸",
          "phonePrefix": "+1242",
          "regions": [
            {
              "code": "01",
              "name": "Acklins Island"
            },
            {
              "code": "22",
              "name": "Berry Islands"
            },
            {
              "code": "02",
              "name": "Bimini"
            },
            {
              "code": "23",
              "name": "Black Point"
            },
            {
              "code": "03",
              "name": "Cat Island"
            },
            {
              "code": "24",
              "name": "Central Abaco"
            },
            {
              "code": "28",
              "name": "Crooked Island and Long Cay"
            },
            {
              "code": "29",
              "name": "East Grand Bahama"
            },
            {
              "code": "04",
              "name": "Exuma"
            },
            {
              "code": "05",
              "name": "Freeport"
            },
            {
              "code": "06",
              "name": "Fresh Creek"
            },
            {
              "code": "07",
              "name": "Governor's Harbour"
            },
            {
              "code": "08",
              "name": "Green Turtle Cay"
            },
            {
              "code": "09",
              "name": "Harbour Island"
            },
            {
              "code": "10",
              "name": "High Rock"
            },
            {
              "code": "11",
              "name": "Inagua"
            },
            {
              "code": "12",
              "name": "Kemps Bay"
            },
            {
              "code": "13",
              "name": "Long Island"
            },
            {
              "code": "14",
              "name": "Marsh Harbour"
            },
            {
              "code": "15",
              "name": "Mayaguana"
            },
            {
              "code": "40",
              "name": "Moore’s Island"
            },
            {
              "code": "16",
              "name": "New Providence"
            },
            {
              "code": "17",
              "name": "Nichollstown and Berry Islands"
            },
            {
              "code": "42",
              "name": "North Abaco"
            },
            {
              "code": "41",
              "name": "North Andros"
            },
            {
              "code": "33",
              "name": "North Eleuthera"
            },
            {
              "code": "18",
              "name": "Ragged Island"
            },
            {
              "code": "19",
              "name": "Rock Sound"
            },
            {
              "code": "20",
              "name": "San Salvador and Rum Cay"
            },
            {
              "code": "21",
              "name": "Sandy Point"
            },
            {
              "code": "35",
              "name": "South Abaco"
            },
            {
              "code": "36",
              "name": "South Andros"
            },
            {
              "code": "37",
              "name": "South Eleuthera"
            },
            {
              "code": "39",
              "name": "West Grand Bahama"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BT",
          "name": "Bhutan",
          "native": "ʼbrug-yul",
          "flag": "🇧🇹",
          "phonePrefix": "+975",
          "regions": [
            {
              "code": "33",
              "name": "Bumthang"
            },
            {
              "code": "12",
              "name": "Chhukha"
            },
            {
              "code": "22",
              "name": "Dagana"
            },
            {
              "code": "GA",
              "name": "Gasa"
            },
            {
              "code": "13",
              "name": "Haa"
            },
            {
              "code": "44",
              "name": "Lhuntse"
            },
            {
              "code": "42",
              "name": "Mongar"
            },
            {
              "code": "11",
              "name": "Paro"
            },
            {
              "code": "43",
              "name": "Pemagatshel"
            },
            {
              "code": "23",
              "name": "Punakha"
            },
            {
              "code": "45",
              "name": "Samdrup Jongkhar"
            },
            {
              "code": "14",
              "name": "Samtse"
            },
            {
              "code": "31",
              "name": "Sarpang"
            },
            {
              "code": "15",
              "name": "Thimphu"
            },
            {
              "code": "41",
              "name": "Trashigang"
            },
            {
              "code": "TY",
              "name": "Trashiyangtse"
            },
            {
              "code": "32",
              "name": "Trongsa"
            },
            {
              "code": "21",
              "name": "Tsirang"
            },
            {
              "code": "24",
              "name": "Wangdue Phodrang"
            },
            {
              "code": "34",
              "name": "Zhemgang"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BV",
          "name": "Bouvet Island",
          "native": "Bouvetøya",
          "flag": "🇧🇻",
          "phonePrefix": "+47",
          "regions": [
            {
              "code": "BV",
              "name": "Bouvet Island"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BW",
          "name": "Botswana",
          "native": "Botswana",
          "flag": "🇧🇼",
          "phonePrefix": "+267",
          "regions": [
            {
              "code": "CE",
              "name": "Central"
            },
            {
              "code": "GH",
              "name": "Ghanzi"
            },
            {
              "code": "KG",
              "name": "Kgalagadi"
            },
            {
              "code": "KL",
              "name": "Kgatleng"
            },
            {
              "code": "KW",
              "name": "Kweneng"
            },
            {
              "code": "NW",
              "name": "North West"
            },
            {
              "code": "NE",
              "name": "North-East"
            },
            {
              "code": "SE",
              "name": "South East"
            },
            {
              "code": "SO",
              "name": "Southern"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BY",
          "name": "Belarus",
          "native": "Белару́сь",
          "flag": "🇧🇾",
          "phonePrefix": "+375",
          "regions": [
            {
              "code": "BR",
              "name": "Brest voblast"
            },
            {
              "code": "HM",
              "name": "Gorod Minsk"
            },
            {
              "code": "HO",
              "name": "Homiel voblast"
            },
            {
              "code": "HR",
              "name": "Hrodna voblast"
            },
            {
              "code": "MA",
              "name": "Mahilyow voblast"
            },
            {
              "code": "MI",
              "name": "Minsk voblast"
            },
            {
              "code": "VI",
              "name": "Vitsebsk voblast"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "BZ",
          "name": "Belize",
          "native": "Belize",
          "flag": "🇧🇿",
          "phonePrefix": "+501",
          "regions": [
            {
              "code": "BZ",
              "name": "Belize District"
            },
            {
              "code": "CY",
              "name": "Cayo District"
            },
            {
              "code": "CZL",
              "name": "Corozal District"
            },
            {
              "code": "OW",
              "name": "Orange Walk District"
            },
            {
              "code": "SC",
              "name": "Stann Creek District"
            },
            {
              "code": "TOL",
              "name": "Toledo District"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CA",
          "name": "Canada",
          "native": "Canada",
          "flag": "🇨🇦",
          "phonePrefix": "+1",
          "regions": [
            {
              "code": "AB",
              "name": "Alberta"
            },
            {
              "code": "BC",
              "name": "British Columbia"
            },
            {
              "code": "MB",
              "name": "Manitoba"
            },
            {
              "code": "NB",
              "name": "New Brunswick"
            },
            {
              "code": "NL",
              "name": "Newfoundland and Labrador"
            },
            {
              "code": "NT",
              "name": "Northwest Territories"
            },
            {
              "code": "NS",
              "name": "Nova Scotia"
            },
            {
              "code": "NU",
              "name": "Nunavut"
            },
            {
              "code": "ON",
              "name": "Ontario"
            },
            {
              "code": "PE",
              "name": "Prince Edward Island"
            },
            {
              "code": "QC",
              "name": "Quebec"
            },
            {
              "code": "SK",
              "name": "Saskatchewan"
            },
            {
              "code": "YT",
              "name": "Yukon"
            }
          ],
          "languages": [
            {
              "isoCode": "en",
              "name": "English",
              "native": "English"
            },
            {
              "isoCode": "fr",
              "name": "French",
              "native": "Français"
            }
          ],
          "currencies": [
            {
              "isoCode": "CAD",
              "name": "Canadian Dollar"
            }
          ]
        },
        {
          "isoCode": "CC",
          "name": "Cocos (Keeling) Islands",
          "native": "Cocos (Keeling) Islands",
          "flag": "🇨🇨",
          "phonePrefix": "+61",
          "regions": [
            {
              "code": "DI",
              "name": "Direction Island"
            },
            {
              "code": "HM",
              "name": "Home Island"
            },
            {
              "code": "HR",
              "name": "Horsburgh Island"
            },
            {
              "code": "NK",
              "name": "North Keeling Island"
            },
            {
              "code": "SI",
              "name": "South Island"
            },
            {
              "code": "WI",
              "name": "West Island"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CD",
          "name": "Democratic Republic of the Congo",
          "native": "République démocratique du Congo",
          "flag": "🇨🇩",
          "phonePrefix": "+243",
          "regions": [
            {
              "code": "BN",
              "name": "Bandundu"
            },
            {
              "code": "BC",
              "name": "Bas-Congo"
            },
            {
              "code": "EQ",
              "name": "Équateur"
            },
            {
              "code": "KE",
              "name": "Kasaï-Occidental"
            },
            {
              "code": "KW",
              "name": "Kasaï-Oriental"
            },
            {
              "code": "KA",
              "name": "Katanga"
            },
            {
              "code": "KN",
              "name": "Kinshasa"
            },
            {
              "code": "MA",
              "name": "Maniema"
            },
            {
              "code": "NK",
              "name": "Nord-Kivu"
            },
            {
              "code": "OR",
              "name": "Orientale"
            },
            {
              "code": "SK",
              "name": "Sud-Kivu"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CF",
          "name": "Central African Republic",
          "native": "Ködörösêse tî Bêafrîka",
          "flag": "🇨🇫",
          "phonePrefix": "+236",
          "regions": [
            {
              "code": "BB",
              "name": "Bamingui-Bangoran"
            },
            {
              "code": "BGF",
              "name": "Bangui"
            },
            {
              "code": "BK",
              "name": "Basse-Kotto"
            },
            {
              "code": "HK",
              "name": "Haute-Kotto"
            },
            {
              "code": "HM",
              "name": "Haut-Mbomou"
            },
            {
              "code": "KG",
              "name": "Kémo"
            },
            {
              "code": "LB",
              "name": "Lobaye"
            },
            {
              "code": "HS",
              "name": "Mambéré-Kadéï"
            },
            {
              "code": "MB",
              "name": "Mbomou"
            },
            {
              "code": "10",
              "name": "Nana-Grebizi"
            },
            {
              "code": "NM",
              "name": "Nana-Mambéré"
            },
            {
              "code": "MP",
              "name": "Ombella-M'Poko"
            },
            {
              "code": "UK",
              "name": "Ouaka"
            },
            {
              "code": "AC",
              "name": "Ouham"
            },
            {
              "code": "OP",
              "name": "Ouham Péndé"
            },
            {
              "code": "SE",
              "name": "Sangha-Mbaéré"
            },
            {
              "code": "VK",
              "name": "Vakaga"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CG",
          "name": "Republic of the Congo",
          "native": "République du Congo",
          "flag": "🇨🇬",
          "phonePrefix": "+242",
          "regions": [
            {
              "code": "11",
              "name": "Bouenza"
            },
            {
              "code": "BZV",
              "name": "Brazzaville"
            },
            {
              "code": "8",
              "name": "Cuvette"
            },
            {
              "code": "15",
              "name": "Cuvette-Ouest"
            },
            {
              "code": "5",
              "name": "Kouilou"
            },
            {
              "code": "2",
              "name": "Lékoumou"
            },
            {
              "code": "7",
              "name": "Likouala"
            },
            {
              "code": "9",
              "name": "Niari"
            },
            {
              "code": "14",
              "name": "Plateaux"
            },
            {
              "code": "16",
              "name": "Pointe-Noire"
            },
            {
              "code": "12",
              "name": "Pool"
            },
            {
              "code": "13",
              "name": "Sangha"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CH",
          "name": "Switzerland",
          "native": "Schweiz",
          "flag": "🇨🇭",
          "phonePrefix": "+41",
          "regions": [
            {
              "code": "AG",
              "name": "Aargau"
            },
            {
              "code": "AR",
              "name": "Appenzell Ausserrhoden"
            },
            {
              "code": "AI",
              "name": "Appenzell Innerhoden"
            },
            {
              "code": "BL",
              "name": "Basel-Landschaft"
            },
            {
              "code": "BS",
              "name": "Basel-Stadt"
            },
            {
              "code": "BE",
              "name": "Bern"
            },
            {
              "code": "FR",
              "name": "Fribourg"
            },
            {
              "code": "GE",
              "name": "Genève"
            },
            {
              "code": "GL",
              "name": "Glarus"
            },
            {
              "code": "GR",
              "name": "Graubünden"
            },
            {
              "code": "JU",
              "name": "Jura"
            },
            {
              "code": "LU",
              "name": "Luzern"
            },
            {
              "code": "NE",
              "name": "Neuchâtel"
            },
            {
              "code": "NW",
              "name": "Nidwalden"
            },
            {
              "code": "OW",
              "name": "Obwalden"
            },
            {
              "code": "SG",
              "name": "Sankt Gallen"
            },
            {
              "code": "SH",
              "name": "Schaffhausen"
            },
            {
              "code": "SZ",
              "name": "Schwyz"
            },
            {
              "code": "SO",
              "name": "Solothurn"
            },
            {
              "code": "TG",
              "name": "Thurgau"
            },
            {
              "code": "TI",
              "name": "Ticino"
            },
            {
              "code": "UR",
              "name": "Uri"
            },
            {
              "code": "VS",
              "name": "Valais"
            },
            {
              "code": "VD",
              "name": "Vaud"
            },
            {
              "code": "ZG",
              "name": "Zug"
            },
            {
              "code": "ZH",
              "name": "Zürich"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CI",
          "name": "Cote D'Ivoire",
          "native": "Côte d'Ivoire",
          "flag": "🇨🇮",
          "phonePrefix": "+225",
          "regions": [
            {
              "code": "16",
              "name": "Agnéby"
            },
            {
              "code": "17",
              "name": "Bafing"
            },
            {
              "code": "09",
              "name": "Bas-Sassandra"
            },
            {
              "code": "10",
              "name": "Denguélé"
            },
            {
              "code": "06",
              "name": "Dix-Huit Montagnes"
            },
            {
              "code": "18",
              "name": "Fromager"
            },
            {
              "code": "02",
              "name": "Haut-Sassandra"
            },
            {
              "code": "07",
              "name": "Lacs"
            },
            {
              "code": "01",
              "name": "Lagunes"
            },
            {
              "code": "12",
              "name": "Marahoué"
            },
            {
              "code": "19",
              "name": "Moyen-Cavally"
            },
            {
              "code": "05",
              "name": "Moyen-Comoé"
            },
            {
              "code": "11",
              "name": "N'zi-Comoé"
            },
            {
              "code": "03",
              "name": "Savanes"
            },
            {
              "code": "15",
              "name": "Sud-Bandama"
            },
            {
              "code": "13",
              "name": "Sud-Comoé"
            },
            {
              "code": "04",
              "name": "Vallée du Bandama"
            },
            {
              "code": "14",
              "name": "Worodougou"
            },
            {
              "code": "08",
              "name": "Zanzan"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CK",
          "name": "Cook Islands",
          "native": "Cook Islands",
          "flag": "🇨🇰",
          "phonePrefix": "+682",
          "regions": [
            {
              "code": null,
              "name": "Aitutaki"
            },
            {
              "code": null,
              "name": "Atiu"
            },
            {
              "code": null,
              "name": "Avarua"
            },
            {
              "code": null,
              "name": "Mangaia"
            },
            {
              "code": null,
              "name": "Manihiki"
            },
            {
              "code": null,
              "name": "Ma'uke"
            },
            {
              "code": null,
              "name": "Mitiaro"
            },
            {
              "code": null,
              "name": "Nassau"
            },
            {
              "code": null,
              "name": "Palmerston"
            },
            {
              "code": null,
              "name": "Penrhyn"
            },
            {
              "code": null,
              "name": "Pukapuka"
            },
            {
              "code": null,
              "name": "Rakahanga"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CL",
          "name": "Chile",
          "native": "Chile",
          "flag": "🇨🇱",
          "phonePrefix": "+56",
          "regions": [
            {
              "code": "AI",
              "name": "Aisén del General Carlos Ibáñez del Campo"
            },
            {
              "code": "AN",
              "name": "Antofagasta"
            },
            {
              "code": "AR",
              "name": "Araucanía"
            },
            {
              "code": "AP",
              "name": "Arica y Parinacota"
            },
            {
              "code": "AT",
              "name": "Atacama"
            },
            {
              "code": "BI",
              "name": "Bío-Bío"
            },
            {
              "code": "CO",
              "name": "Coquimbo"
            },
            {
              "code": "LI",
              "name": "Libertador General Bernardo O'Higgins"
            },
            {
              "code": "LL",
              "name": "Los Lagos"
            },
            {
              "code": "LR",
              "name": "Los Ríos"
            },
            {
              "code": "MA",
              "name": "Magallanes y Antartica Chilena"
            },
            {
              "code": "",
              "name": "Marga-Marga"
            },
            {
              "code": "ML",
              "name": "Maule"
            },
            {
              "code": "NB",
              "name": "Ñuble"
            },
            {
              "code": "RM",
              "name": "Región Metropolitana de Santiago"
            },
            {
              "code": "TA",
              "name": "Tarapacá"
            },
            {
              "code": "VS",
              "name": "Valparaíso"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CM",
          "name": "Cameroon",
          "native": "Cameroon",
          "flag": "🇨🇲",
          "phonePrefix": "+237",
          "regions": [
            {
              "code": "AD",
              "name": "Adamaoua"
            },
            {
              "code": "CE",
              "name": "Centre"
            },
            {
              "code": "ES",
              "name": "Est"
            },
            {
              "code": "EN",
              "name": "Extrême-Nord"
            },
            {
              "code": "LT",
              "name": "Littoral"
            },
            {
              "code": "NO",
              "name": "Nord"
            },
            {
              "code": "NW",
              "name": "Nord-Ouest"
            },
            {
              "code": "OU",
              "name": "Ouest"
            },
            {
              "code": "SU",
              "name": "Sud"
            },
            {
              "code": "SW",
              "name": "Sud-Ouest"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CN",
          "name": "People's Republic of China",
          "native": "中国",
          "flag": "🇨🇳",
          "phonePrefix": "+86",
          "regions": [
            {
              "code": "AH",
              "name": "Anhui"
            },
            {
              "code": "BJ",
              "name": "Beijing"
            },
            {
              "code": "CQ",
              "name": "Chongqing"
            },
            {
              "code": "FJ",
              "name": "Fujian"
            },
            {
              "code": "GS",
              "name": "Gansu"
            },
            {
              "code": "GD",
              "name": "Guangdong"
            },
            {
              "code": "GX",
              "name": "Guangxi"
            },
            {
              "code": "GZ",
              "name": "Guizhou"
            },
            {
              "code": "HI",
              "name": "Hainan"
            },
            {
              "code": "HE",
              "name": "Hebei"
            },
            {
              "code": "HL",
              "name": "Heilongjiang"
            },
            {
              "code": "HA",
              "name": "Henan"
            },
            {
              "code": "HK",
              "name": "Hong Kong"
            },
            {
              "code": "HB",
              "name": "Hubei"
            },
            {
              "code": "HN",
              "name": "Hunan"
            },
            {
              "code": "NM",
              "name": "Inner Mongolia"
            },
            {
              "code": "JS",
              "name": "Jiangsu"
            },
            {
              "code": "JX",
              "name": "Jiangxi"
            },
            {
              "code": "JL",
              "name": "Jilin"
            },
            {
              "code": "LN",
              "name": "Liaoning"
            },
            {
              "code": "MO",
              "name": "Macau"
            },
            {
              "code": "NX",
              "name": "Ningxia"
            },
            {
              "code": "QH",
              "name": "Qinghai"
            },
            {
              "code": "SN",
              "name": "Shaanxi"
            },
            {
              "code": "SD",
              "name": "Shandong"
            },
            {
              "code": "SH",
              "name": "Shanghai"
            },
            {
              "code": "SX",
              "name": "Shanxi"
            },
            {
              "code": "SC",
              "name": "Sichuan"
            },
            {
              "code": "TJ",
              "name": "Tianjin"
            },
            {
              "code": "XZ",
              "name": "Tibet"
            },
            {
              "code": "XJ",
              "name": "Xinjiang"
            },
            {
              "code": "YN",
              "name": "Yunnan"
            },
            {
              "code": "ZJ",
              "name": "Zhejiang"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CO",
          "name": "Colombia",
          "native": "Colombia",
          "flag": "🇨🇴",
          "phonePrefix": "+57",
          "regions": [
            {
              "code": "AMA",
              "name": "Amazonas"
            },
            {
              "code": "ANT",
              "name": "Antioquia"
            },
            {
              "code": "ARA",
              "name": "Arauca"
            },
            {
              "code": "SAP",
              "name": "Archipiélago de San Andrés"
            },
            {
              "code": "ATL",
              "name": "Atlántico"
            },
            {
              "code": "DC",
              "name": "Bogotá D.C."
            },
            {
              "code": "BOL",
              "name": "Bolívar"
            },
            {
              "code": "BOY",
              "name": "Boyacá"
            },
            {
              "code": "CAL",
              "name": "Caldas"
            },
            {
              "code": "CAQ",
              "name": "Caquetá"
            },
            {
              "code": "CAS",
              "name": "Casanare"
            },
            {
              "code": "CAU",
              "name": "Cauca"
            },
            {
              "code": "CES",
              "name": "Cesar"
            },
            {
              "code": "CHO",
              "name": "Chocó"
            },
            {
              "code": "COR",
              "name": "Córdoba"
            },
            {
              "code": "CUN",
              "name": "Cundinamarca"
            },
            {
              "code": "GUA",
              "name": "Guainía"
            },
            {
              "code": "GUV",
              "name": "Guaviare"
            },
            {
              "code": "HUI",
              "name": "Huila"
            },
            {
              "code": "LAG",
              "name": "La Guajira"
            },
            {
              "code": "MAG",
              "name": "Magdalena"
            },
            {
              "code": "MET",
              "name": "Meta"
            },
            {
              "code": "NAR",
              "name": "Nariño"
            },
            {
              "code": "NSA",
              "name": "Norte de Santander"
            },
            {
              "code": "PUT",
              "name": "Putumayo"
            },
            {
              "code": "QUI",
              "name": "Quindío"
            },
            {
              "code": "RIS",
              "name": "Risaralda"
            },
            {
              "code": "SAN",
              "name": "Santander"
            },
            {
              "code": "SUC",
              "name": "Sucre"
            },
            {
              "code": "TOL",
              "name": "Tolima"
            },
            {
              "code": "VAC",
              "name": "Valle del Cauca"
            },
            {
              "code": "VAU",
              "name": "Vaupés"
            },
            {
              "code": "VID",
              "name": "Vichada"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CR",
          "name": "Costa Rica",
          "native": "Costa Rica",
          "flag": "🇨🇷",
          "phonePrefix": "+506",
          "regions": [
            {
              "code": "2",
              "name": "Alajuela"
            },
            {
              "code": "3",
              "name": "Cartago"
            },
            {
              "code": "5",
              "name": "Guanacaste"
            },
            {
              "code": "4",
              "name": "Heredia"
            },
            {
              "code": "7",
              "name": "Limón"
            },
            {
              "code": "6",
              "name": "Puntarenas"
            },
            {
              "code": "1",
              "name": "San José"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CU",
          "name": "Cuba",
          "native": "Cuba",
          "flag": "🇨🇺",
          "phonePrefix": "+53",
          "regions": [
            {
              "code": "15",
              "name": "Artemisa"
            },
            {
              "code": "09",
              "name": "Camagüey"
            },
            {
              "code": "08",
              "name": "Ciego de Ávila"
            },
            {
              "code": "06",
              "name": "Cienfuegos"
            },
            {
              "code": "12",
              "name": "Granma"
            },
            {
              "code": "14",
              "name": "Guantánamo"
            },
            {
              "code": "11",
              "name": "Holguín"
            },
            {
              "code": "99",
              "name": "Isla de la Juventud"
            },
            {
              "code": "03",
              "name": "La Habana"
            },
            {
              "code": "10",
              "name": "Las Tunas"
            },
            {
              "code": "04",
              "name": "Matanzas"
            },
            {
              "code": "16",
              "name": "Mayabeque"
            },
            {
              "code": "01",
              "name": "Pinar del Río"
            },
            {
              "code": "07",
              "name": "Sancti Spíritus"
            },
            {
              "code": "13",
              "name": "Santiago de Cuba"
            },
            {
              "code": "05",
              "name": "Villa Clara"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CV",
          "name": "Cape Verde",
          "native": "Cabo Verde",
          "flag": "🇨🇻",
          "phonePrefix": "+238",
          "regions": [
            {
              "code": "BV",
              "name": "Boa Vista"
            },
            {
              "code": "BR",
              "name": "Brava"
            },
            {
              "code": "CS",
              "name": "Calheta de São Miguel"
            },
            {
              "code": "MA",
              "name": "Maio"
            },
            {
              "code": "MO",
              "name": "Mosteiros"
            },
            {
              "code": "PA",
              "name": "Paúl"
            },
            {
              "code": "PN",
              "name": "Porto Novo"
            },
            {
              "code": "PR",
              "name": "Praia"
            },
            {
              "code": "RB",
              "name": "Ribeira Brava"
            },
            {
              "code": "RG",
              "name": "Ribeira Grande"
            },
            {
              "code": "SL",
              "name": "Sal"
            },
            {
              "code": "CA",
              "name": "Santa Catarina"
            },
            {
              "code": "CR",
              "name": "Santa Cruz"
            },
            {
              "code": "SD",
              "name": "São Domingos"
            },
            {
              "code": "SF",
              "name": "São Filipe"
            },
            {
              "code": "SN",
              "name": "São Nicolau"
            },
            {
              "code": "SV",
              "name": "São Vicente"
            },
            {
              "code": "TA",
              "name": "Tarrafal"
            },
            {
              "code": "TS",
              "name": "Tarrafal de São Nicolau"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CW",
          "name": "Curaçao",
          "native": "Curaçao",
          "flag": "🇨🇼",
          "phonePrefix": "+5999",
          "regions": [
            {
              "code": "CW",
              "name": "Curaçao"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CX",
          "name": "Christmas Island",
          "native": "Christmas Island",
          "flag": "🇨🇽",
          "phonePrefix": "+61",
          "regions": [
            {
              "code": "CX",
              "name": "Christmas Island"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "CY",
          "name": "Cyprus",
          "native": "Κύπρος",
          "flag": "🇨🇾",
          "phonePrefix": "+357",
          "regions": [
            {
              "code": "04",
              "name": "Ammochostos"
            },
            {
              "code": "06",
              "name": "Keryneia"
            },
            {
              "code": "03",
              "name": "Larnaka"
            },
            {
              "code": "01",
              "name": "Lefkosia"
            },
            {
              "code": "02",
              "name": "Lemesos"
            },
            {
              "code": "05",
              "name": "Pafos"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "CZ",
          "name": "Czech Republic",
          "native": "Česká republika",
          "flag": "🇨🇿",
          "phonePrefix": "+420",
          "regions": [
            {
              "code": "10",
              "name": "Hlavní město Praha"
            },
            {
              "code": "31",
              "name": "Jihočeský kraj"
            },
            {
              "code": "64",
              "name": "Jihomoravský kraj"
            },
            {
              "code": "41",
              "name": "Karlovarský kraj"
            },
            {
              "code": "52",
              "name": "Královéhradecký kraj"
            },
            {
              "code": "51",
              "name": "Liberecký kraj"
            },
            {
              "code": "80",
              "name": "Moravskoslezský kraj"
            },
            {
              "code": "71",
              "name": "Olomoucký kraj"
            },
            {
              "code": "53",
              "name": "Pardubický kraj"
            },
            {
              "code": "32",
              "name": "Plzeňský kraj"
            },
            {
              "code": "20",
              "name": "Středočeský kraj"
            },
            {
              "code": "42",
              "name": "Ústecký kraj"
            },
            {
              "code": "63",
              "name": "Vysočina"
            },
            {
              "code": "72",
              "name": "Zlínský kraj"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "DE",
          "name": "Germany",
          "native": "Deutschland",
          "flag": "🇩🇪",
          "phonePrefix": "+49",
          "regions": [
            {
              "code": "BW",
              "name": "Baden-Württemberg"
            },
            {
              "code": "BY",
              "name": "Bayern"
            },
            {
              "code": "BE",
              "name": "Berlin"
            },
            {
              "code": "BB",
              "name": "Brandenburg"
            },
            {
              "code": "HB",
              "name": "Bremen"
            },
            {
              "code": "HH",
              "name": "Hamburg"
            },
            {
              "code": "HE",
              "name": "Hessen"
            },
            {
              "code": "MV",
              "name": "Mecklenburg-Vorpommern"
            },
            {
              "code": "NI",
              "name": "Niedersachsen"
            },
            {
              "code": "NW",
              "name": "Nordrhein-Westfalen"
            },
            {
              "code": "RP",
              "name": "Rheinland-Pfalz"
            },
            {
              "code": "SL",
              "name": "Saarland"
            },
            {
              "code": "SN",
              "name": "Sachsen"
            },
            {
              "code": "ST",
              "name": "Sachsen-Anhalt"
            },
            {
              "code": "SH",
              "name": "Schleswig-Holstein"
            },
            {
              "code": "TH",
              "name": "Thüringen"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "DJ",
          "name": "Djibouti",
          "native": "Djibouti",
          "flag": "🇩🇯",
          "phonePrefix": "+253",
          "regions": [
            {
              "code": "AS",
              "name": "Ali Sabieh"
            },
            {
              "code": "AR",
              "name": "Arta"
            },
            {
              "code": "DI",
              "name": "Dikhil"
            },
            {
              "code": "OB",
              "name": "Obock"
            },
            {
              "code": "TA",
              "name": "Tadjourah"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "DK",
          "name": "Denmark",
          "native": "Danmark",
          "flag": "🇩🇰",
          "phonePrefix": "+45",
          "regions": [
            {
              "code": "DK-84",
              "name": "Hovedstaden"
            },
            {
              "code": "DK-82",
              "name": "Midtjylland"
            },
            {
              "code": "DK-81",
              "name": "Nordjylland"
            },
            {
              "code": "DK-85",
              "name": "Sjælland"
            },
            {
              "code": "DK-83",
              "name": "Syddanmark"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "DM",
          "name": "Dominica",
          "native": "Dominica",
          "flag": "🇩🇲",
          "phonePrefix": "+1767",
          "regions": [
            {
              "code": "02",
              "name": "Saint Andrew Parish"
            },
            {
              "code": "03",
              "name": "Saint David Parish"
            },
            {
              "code": "04",
              "name": "Saint George Parish"
            },
            {
              "code": "05",
              "name": "Saint John Parish"
            },
            {
              "code": "06",
              "name": "Saint Joseph Parish"
            },
            {
              "code": "07",
              "name": "Saint Luke Parish"
            },
            {
              "code": "08",
              "name": "Saint Mark Parish"
            },
            {
              "code": "09",
              "name": "Saint Patrick Parish"
            },
            {
              "code": "10",
              "name": "Saint Paul Parish"
            },
            {
              "code": "11",
              "name": "Saint Peter Parish"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "DO",
          "name": "Dominican Republic",
          "native": "República Dominicana",
          "flag": "🇩🇴",
          "phonePrefix": "+1809,1829,1849",
          "regions": [
            {
              "code": "02",
              "name": "Cibao Central"
            },
            {
              "code": "37",
              "name": "Del Valle"
            },
            {
              "code": "01",
              "name": "Distrito Nacional"
            },
            {
              "code": "38",
              "name": "Enriquillo"
            },
            {
              "code": "04",
              "name": "Norcentral"
            },
            {
              "code": "33",
              "name": "Nordeste"
            },
            {
              "code": "34",
              "name": "Noroeste"
            },
            {
              "code": "35",
              "name": "Norte"
            },
            {
              "code": "42",
              "name": "Valdesia"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "DZ",
          "name": "Algeria",
          "native": "الجزائر",
          "flag": "🇩🇿",
          "phonePrefix": "+213",
          "regions": [
            {
              "code": "01",
              "name": "Adrar"
            },
            {
              "code": "44",
              "name": "Aïn Defla"
            },
            {
              "code": "46",
              "name": "Aïn Témouchent"
            },
            {
              "code": "16",
              "name": "Algiers"
            },
            {
              "code": "23",
              "name": "Annaba"
            },
            {
              "code": "05",
              "name": "Batna"
            },
            {
              "code": "08",
              "name": "Béchar"
            },
            {
              "code": "06",
              "name": "Béjaïa"
            },
            {
              "code": "07",
              "name": "Biskra"
            },
            {
              "code": "09",
              "name": "Blida"
            },
            {
              "code": "34",
              "name": "Bordj Bou Arréridj"
            },
            {
              "code": "10",
              "name": "Bouïra"
            },
            {
              "code": "35",
              "name": "Boumerdès"
            },
            {
              "code": "02",
              "name": "Chlef"
            },
            {
              "code": "25",
              "name": "Constantine"
            },
            {
              "code": "17",
              "name": "Djelfa"
            },
            {
              "code": "32",
              "name": "El Bayadh"
            },
            {
              "code": "39",
              "name": "El Oued"
            },
            {
              "code": "36",
              "name": "El Tarf"
            },
            {
              "code": "47",
              "name": "Ghardaïa"
            },
            {
              "code": "24",
              "name": "Guelma"
            },
            {
              "code": "33",
              "name": "Illizi"
            },
            {
              "code": "18",
              "name": "Jijel"
            },
            {
              "code": "40",
              "name": "Khenchela"
            },
            {
              "code": "03",
              "name": "Laghouat"
            },
            {
              "code": "29",
              "name": "Mascara"
            },
            {
              "code": "26",
              "name": "Médéa"
            },
            {
              "code": "43",
              "name": "Mila"
            },
            {
              "code": "27",
              "name": "Mostaganem"
            },
            {
              "code": "28",
              "name": "Msila"
            },
            {
              "code": "45",
              "name": "Naâma"
            },
            {
              "code": "31",
              "name": "Oran"
            },
            {
              "code": "30",
              "name": "Ouargla"
            },
            {
              "code": "04",
              "name": "Oum el Bouaghi"
            },
            {
              "code": "48",
              "name": "Relizane"
            },
            {
              "code": "20",
              "name": "Saïda"
            },
            {
              "code": "19",
              "name": "Sétif"
            },
            {
              "code": "22",
              "name": "Sidi Bel Abbès"
            },
            {
              "code": "21",
              "name": "Skikda"
            },
            {
              "code": "41",
              "name": "Souk Ahras"
            },
            {
              "code": "11",
              "name": "Tamanghasset"
            },
            {
              "code": "12",
              "name": "Tébessa"
            },
            {
              "code": "14",
              "name": "Tiaret"
            },
            {
              "code": "37",
              "name": "Tindouf"
            },
            {
              "code": "42",
              "name": "Tipaza"
            },
            {
              "code": "38",
              "name": "Tissemsilt"
            },
            {
              "code": "15",
              "name": "Tizi Ouzou"
            },
            {
              "code": "13",
              "name": "Tlemcen"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "EC",
          "name": "Ecuador",
          "native": "Ecuador",
          "flag": "🇪🇨",
          "phonePrefix": "+593",
          "regions": [
            {
              "code": "A",
              "name": "Azuay"
            },
            {
              "code": "B",
              "name": "Bolívar"
            },
            {
              "code": "F",
              "name": "Cañar"
            },
            {
              "code": "C",
              "name": "Carchi"
            },
            {
              "code": "H",
              "name": "Chimborazo"
            },
            {
              "code": "X",
              "name": "Cotopaxi"
            },
            {
              "code": "O",
              "name": "El Oro"
            },
            {
              "code": "E",
              "name": "Esmeraldas"
            },
            {
              "code": "W",
              "name": "Galápagos"
            },
            {
              "code": "G",
              "name": "Guayas"
            },
            {
              "code": "I",
              "name": "Imbabura"
            },
            {
              "code": "L",
              "name": "Loja"
            },
            {
              "code": "R",
              "name": "Los Ríos"
            },
            {
              "code": "M",
              "name": "Manabí"
            },
            {
              "code": "S",
              "name": "Morona-Santiago"
            },
            {
              "code": "N",
              "name": "Napo"
            },
            {
              "code": "D",
              "name": "Orellana"
            },
            {
              "code": "Y",
              "name": "Pastaza"
            },
            {
              "code": "P",
              "name": "Pichincha"
            },
            {
              "code": "SE",
              "name": "Santa Elena"
            },
            {
              "code": "SD",
              "name": "Santo Domingo de los Tsáchilas"
            },
            {
              "code": "U",
              "name": "Sucumbíos"
            },
            {
              "code": "T",
              "name": "Tungurahua"
            },
            {
              "code": "Z",
              "name": "Zamora-Chinchipe"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "EE",
          "name": "Estonia",
          "native": "Eesti",
          "flag": "🇪🇪",
          "phonePrefix": "+372",
          "regions": [
            {
              "code": "37",
              "name": "Harjumaa (Tallinn)"
            },
            {
              "code": "39",
              "name": "Hiiumaa (Kardla)"
            },
            {
              "code": "44",
              "name": "Ida-Virumaa (Johvi)"
            },
            {
              "code": "41",
              "name": "Järvamaa (Paide)"
            },
            {
              "code": "49",
              "name": "Jõgevamaa (Jogeva)"
            },
            {
              "code": "57",
              "name": "Läänemaa"
            },
            {
              "code": "59",
              "name": "Lääne-Virumaa (Rakvere)"
            },
            {
              "code": "67",
              "name": "Pärnumaa (Parnu)"
            },
            {
              "code": "65",
              "name": "Põlvamaa (Polva)"
            },
            {
              "code": "70",
              "name": "Raplamaa (Rapla)"
            },
            {
              "code": "74",
              "name": "Saaremaa (Kuessaare)"
            },
            {
              "code": "78",
              "name": "Tartumaa (Tartu)"
            },
            {
              "code": "82",
              "name": "Valgamaa (Valga)"
            },
            {
              "code": "84",
              "name": "Viljandimaa (Viljandi)"
            },
            {
              "code": "86",
              "name": "Võrumaa (Voru)"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "EG",
          "name": "Egypt",
          "native": "مصر‎",
          "flag": "🇪🇬",
          "phonePrefix": "+20",
          "regions": [
            {
              "code": "ALX",
              "name": "Alexandria"
            },
            {
              "code": "ASN",
              "name": "Aswan"
            },
            {
              "code": "AST",
              "name": "Asyout"
            },
            {
              "code": "BNS",
              "name": "Bani Sueif"
            },
            {
              "code": "BH",
              "name": "Beheira"
            },
            {
              "code": "C",
              "name": "Cairo"
            },
            {
              "code": "DK",
              "name": "Daqahlia"
            },
            {
              "code": "DT",
              "name": "Dumiat"
            },
            {
              "code": "BA",
              "name": "El Bahr El Ahmar"
            },
            {
              "code": "IS",
              "name": "El Ismailia"
            },
            {
              "code": "SUZ",
              "name": "El Suez"
            },
            {
              "code": "WAD",
              "name": "El Wadi El Gedeed"
            },
            {
              "code": "FYM",
              "name": "Fayoum"
            },
            {
              "code": "GH",
              "name": "Gharbia"
            },
            {
              "code": "GZ",
              "name": "Giza"
            },
            {
              "code": "HU",
              "name": "Helwan"
            },
            {
              "code": "KFS",
              "name": "Kafr El Sheikh"
            },
            {
              "code": "LX",
              "name": "Luxor"
            },
            {
              "code": "MT",
              "name": "Matrouh"
            },
            {
              "code": "MN",
              "name": "Menia"
            },
            {
              "code": "MNF",
              "name": "Menofia"
            },
            {
              "code": "SIN",
              "name": "North Sinai"
            },
            {
              "code": "PTS",
              "name": "Port Said"
            },
            {
              "code": "KB",
              "name": "Qalubia"
            },
            {
              "code": "KN",
              "name": "Qena"
            },
            {
              "code": "SHR",
              "name": "Sharqia"
            },
            {
              "code": "SU",
              "name": "Sixth of October"
            },
            {
              "code": "SHG",
              "name": "Sohag"
            },
            {
              "code": "JS",
              "name": "South Sinai"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "EH",
          "name": "Western Sahara",
          "native": "الصحراء الغربية",
          "flag": "🇪🇭",
          "phonePrefix": "+212",
          "regions": [
            {
              "code": "ESM",
              "name": "Es Smara"
            },
            {
              "code": "BOD",
              "name": "Boujdour"
            },
            {
              "code": "LAA",
              "name": "Laâyoune"
            },
            {
              "code": "AOU",
              "name": "Aousserd"
            },
            {
              "code": "OUD",
              "name": "Oued ed Dahab"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ER",
          "name": "Eritrea",
          "native": "ኤርትራ",
          "flag": "🇪🇷",
          "phonePrefix": "+291",
          "regions": [
            {
              "code": "AN",
              "name": "Anseba"
            },
            {
              "code": "DU",
              "name": "Debub"
            },
            {
              "code": "DK",
              "name": "Debub-Keih-Bahri"
            },
            {
              "code": "GB",
              "name": "Gash-Barka"
            },
            {
              "code": "MA",
              "name": "Maekel"
            },
            {
              "code": "SK",
              "name": "Semien-Keih-Bahri"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ES",
          "name": "Spain",
          "native": "España",
          "flag": "🇪🇸",
          "phonePrefix": "+34",
          "regions": [
            {
              "code": "AB",
              "name": "Albacete"
            },
            {
              "code": "A",
              "name": "Alicante"
            },
            {
              "code": "AN",
              "name": "Almería"
            },
            {
              "code": "VI",
              "name": "Araba/Álava"
            },
            {
              "code": "O",
              "name": "Asturias"
            },
            {
              "code": "AV",
              "name": "Ávila"
            },
            {
              "code": "BA",
              "name": "Badajoz"
            },
            {
              "code": "B",
              "name": "Barcelona"
            },
            {
              "code": "BI",
              "name": "Bizkaia"
            },
            {
              "code": "BU",
              "name": "Burgos"
            },
            {
              "code": "CC",
              "name": "Cáceres"
            },
            {
              "code": "CA",
              "name": "Cádiz"
            },
            {
              "code": "S",
              "name": "Cantabria"
            },
            {
              "code": "CS",
              "name": "Castellón/Castelló"
            },
            {
              "code": "CE",
              "name": "Ceuta"
            },
            {
              "code": "CR",
              "name": "Ciudad Real"
            },
            {
              "code": "CO",
              "name": "Córdoba"
            },
            {
              "code": "C",
              "name": "A Coruña"
            },
            {
              "code": "CU",
              "name": "Cuenca"
            },
            {
              "code": "SS",
              "name": "Gipuzkoa"
            },
            {
              "code": "GI",
              "name": "Girona"
            },
            {
              "code": "GR",
              "name": "Granada"
            },
            {
              "code": "GU",
              "name": "Guadalajara"
            },
            {
              "code": "H",
              "name": "Huelva"
            },
            {
              "code": "HU",
              "name": "Huesca"
            },
            {
              "code": "PM",
              "name": "Illes Balears"
            },
            {
              "code": "J",
              "name": "Jaén"
            },
            {
              "code": "LE",
              "name": "León"
            },
            {
              "code": "L",
              "name": "Lleida"
            },
            {
              "code": "LU",
              "name": "Lugo"
            },
            {
              "code": "M",
              "name": "Madrid"
            },
            {
              "code": "MA",
              "name": "Málaga"
            },
            {
              "code": "ML",
              "name": "Melilla"
            },
            {
              "code": "MU",
              "name": "Murcia"
            },
            {
              "code": "NA",
              "name": "Navarra/Nafarroa"
            },
            {
              "code": "OR",
              "name": "Ourense"
            },
            {
              "code": "P",
              "name": "Palencia"
            },
            {
              "code": "GC",
              "name": "Las Palmas"
            },
            {
              "code": "PO",
              "name": "Pontevedra"
            },
            {
              "code": "LO",
              "name": "La Rioja"
            },
            {
              "code": "SA",
              "name": "Salamanca"
            },
            {
              "code": "TF",
              "name": "Santa Cruz de Tenerife"
            },
            {
              "code": "SG",
              "name": "Segovia"
            },
            {
              "code": "SE",
              "name": "Sevilla"
            },
            {
              "code": "SO",
              "name": "Soria"
            },
            {
              "code": "T",
              "name": "Tarragona"
            },
            {
              "code": "TE",
              "name": "Teruel"
            },
            {
              "code": "TO",
              "name": "Toledo"
            },
            {
              "code": "V",
              "name": "Valencia/València"
            },
            {
              "code": "VA",
              "name": "Valladolid"
            },
            {
              "code": "ZA",
              "name": "Zamora"
            },
            {
              "code": "Z",
              "name": "Zaragoza"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "ET",
          "name": "Ethiopia",
          "native": "ኢትዮጵያ",
          "flag": "🇪🇹",
          "phonePrefix": "+251",
          "regions": [
            {
              "code": "AA",
              "name": "Addis Ababa"
            },
            {
              "code": "AF",
              "name": "Afar"
            },
            {
              "code": "AM",
              "name": "Amhara"
            },
            {
              "code": "BE",
              "name": "Benshangul-Gumaz"
            },
            {
              "code": "DD",
              "name": "Dire Dawa"
            },
            {
              "code": "GA",
              "name": "Gambela"
            },
            {
              "code": "HA",
              "name": "Harari"
            },
            {
              "code": "OR",
              "name": "Oromia"
            },
            {
              "code": "SO",
              "name": "Somali"
            },
            {
              "code": "SN",
              "name": "Southern Nations Nationalities and People's Region"
            },
            {
              "code": "TI",
              "name": "Tigray"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "FI",
          "name": "Finland",
          "native": "Suomi",
          "flag": "🇫🇮",
          "phonePrefix": "+358",
          "regions": [
            {
              "code": "FI-01",
              "name": "Ahvenanmaan maakunta"
            },
            {
              "code": "FI-02",
              "name": "Etelä-Karjala"
            },
            {
              "code": "FI-03",
              "name": "Etelä-Pohjanmaa"
            },
            {
              "code": "FI-04",
              "name": "Etelä-Savo"
            },
            {
              "code": "FI-05",
              "name": "Kainuu"
            },
            {
              "code": "FI-06",
              "name": "Kanta-Häme"
            },
            {
              "code": "FI-07",
              "name": "Keski-Pohjanmaa"
            },
            {
              "code": "FI-08",
              "name": "Keski-Suomi"
            },
            {
              "code": "FI-09",
              "name": "Kymenlaakso"
            },
            {
              "code": "FI-10",
              "name": "Lappi"
            },
            {
              "code": "FI-11",
              "name": "Pirkanmaa"
            },
            {
              "code": "FI-12",
              "name": "Pohjanmaa"
            },
            {
              "code": "FI-13",
              "name": "Pohjois-Karjala"
            },
            {
              "code": "FI-14",
              "name": "Pohjois-Pohjanmaa"
            },
            {
              "code": "FI-15",
              "name": "Pohjois-Savo"
            },
            {
              "code": "FI-16",
              "name": "Päijät-Häme"
            },
            {
              "code": "FI-17",
              "name": "Satakunta"
            },
            {
              "code": "FI-18",
              "name": "Uusimaa"
            },
            {
              "code": "FI-19",
              "name": "Varsinais-Suomi"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "FJ",
          "name": "Fiji",
          "native": "Fiji",
          "flag": "🇫🇯",
          "phonePrefix": "+679",
          "regions": [
            {
              "code": "01",
              "name": "Ba"
            },
            {
              "code": "02",
              "name": "Bua"
            },
            {
              "code": "03",
              "name": "Cakaudrove"
            },
            {
              "code": "04",
              "name": "Kadavu"
            },
            {
              "code": "05",
              "name": "Lau"
            },
            {
              "code": "06",
              "name": "Lomaiviti"
            },
            {
              "code": "07",
              "name": "Macuata"
            },
            {
              "code": "08",
              "name": "Nadroga and Navosa"
            },
            {
              "code": "09",
              "name": "Naitasiri"
            },
            {
              "code": "10",
              "name": "Namosi"
            },
            {
              "code": "11",
              "name": "Ra"
            },
            {
              "code": "12",
              "name": "Rewa"
            },
            {
              "code": "R",
              "name": "Rotuma"
            },
            {
              "code": "13",
              "name": "Serua"
            },
            {
              "code": "14",
              "name": "Tailevu"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "FK",
          "name": "Falkland Islands (Malvinas)",
          "native": "Falkland Islands",
          "flag": "🇫🇰",
          "phonePrefix": "+500",
          "regions": [
            {
              "code": null,
              "name": "Falkland Islands (Islas Malvinas)"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "FM",
          "name": "Micronesia, Federated States of",
          "native": "Micronesia",
          "flag": "🇫🇲",
          "phonePrefix": "+691",
          "regions": [
            {
              "code": "TRK",
              "name": "Chuuk (Truk)"
            },
            {
              "code": "KSA",
              "name": "Kosrae"
            },
            {
              "code": "PNI",
              "name": "Pohnpei"
            },
            {
              "code": "YAP",
              "name": "Yap"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "FO",
          "name": "Faroe Islands",
          "native": "Føroyar",
          "flag": "🇫🇴",
          "phonePrefix": "+298",
          "regions": [
            {
              "code": null,
              "name": "Bordoy"
            },
            {
              "code": null,
              "name": "Eysturoy"
            },
            {
              "code": null,
              "name": "Mykines"
            },
            {
              "code": null,
              "name": "Sandoy"
            },
            {
              "code": null,
              "name": "Skuvoy"
            },
            {
              "code": null,
              "name": "Streymoy"
            },
            {
              "code": null,
              "name": "Suduroy"
            },
            {
              "code": null,
              "name": "Tvoroyri"
            },
            {
              "code": null,
              "name": "Vagar"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "FR",
          "name": "France",
          "native": "France",
          "flag": "🇫🇷",
          "phonePrefix": "+33",
          "regions": [
            {
              "code": "ARA",
              "name": "Auvergne-Rhône-Alpes"
            },
            {
              "code": "BFC",
              "name": "Bourgogne-Franche-Comté"
            },
            {
              "code": "BRE",
              "name": "Bretagne"
            },
            {
              "code": "CVL",
              "name": "Centre-Val de Loire"
            },
            {
              "code": "COR",
              "name": "Corse"
            },
            {
              "code": "GES",
              "name": "Grand Est"
            },
            {
              "code": "HDF",
              "name": "Hauts-de-France"
            },
            {
              "code": "IDF",
              "name": "Île-de-France"
            },
            {
              "code": "NOR",
              "name": "Normandie"
            },
            {
              "code": "NAQ",
              "name": "Nouvelle-Aquitaine"
            },
            {
              "code": "OCC",
              "name": "Occitanie"
            },
            {
              "code": "PDL",
              "name": "Pays de la Loire"
            },
            {
              "code": "PAC",
              "name": "Provence-Alpes-Cote d'Azur"
            },
            {
              "code": "CP",
              "name": "Clipperton"
            },
            {
              "code": "GP",
              "name": "Guadeloupe"
            },
            {
              "code": "GF",
              "name": "Guyane"
            },
            {
              "code": "MQ",
              "name": "Martinique"
            },
            {
              "code": "YT",
              "name": "Mayotte"
            },
            {
              "code": "NC",
              "name": "Nouvelle-Calédonie"
            },
            {
              "code": "PF",
              "name": "Polynésie"
            },
            {
              "code": "PM",
              "name": "Saint-Pierre-et-Miquelon"
            },
            {
              "code": "BL",
              "name": "Saint Barthélemy"
            },
            {
              "code": "MF",
              "name": "Saint Martin"
            },
            {
              "code": "RE",
              "name": "Réunion"
            },
            {
              "code": "TF",
              "name": "Terres Australes Françaises"
            },
            {
              "code": "WF",
              "name": "Wallis-et-Futuna"
            }
          ],
          "languages": [
            {
              "isoCode": "fr",
              "name": "French",
              "native": "Français"
            }
          ],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "GA",
          "name": "Gabon",
          "native": "Gabon",
          "flag": "🇬🇦",
          "phonePrefix": "+241",
          "regions": [
            {
              "code": "1",
              "name": "Estuaire"
            },
            {
              "code": "2",
              "name": "Haut-Ogooué"
            },
            {
              "code": "3",
              "name": "Moyen-Ogooué"
            },
            {
              "code": "4",
              "name": "Ngounié"
            },
            {
              "code": "5",
              "name": "Nyanga"
            },
            {
              "code": "6",
              "name": "Ogooué-Ivindo"
            },
            {
              "code": "7",
              "name": "Ogooué-Lolo"
            },
            {
              "code": "8",
              "name": "Ogooué-Maritime"
            },
            {
              "code": "9",
              "name": "Woleu-Ntem"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GB",
          "name": "United Kingdom",
          "native": "United Kingdom",
          "flag": "🇬🇧",
          "phonePrefix": "+44",
          "regions": [
            {
              "code": "ABE",
              "name": "Aberdeen City"
            },
            {
              "code": "ABD",
              "name": "Aberdeenshire"
            },
            {
              "code": "ANS",
              "name": "Angus"
            },
            {
              "code": "ANN",
              "name": "Antrim and Newtownabbey"
            },
            {
              "code": "AND",
              "name": "Ards and North Down"
            },
            {
              "code": "AGB",
              "name": "Argyll and Bute"
            },
            {
              "code": "ABC",
              "name": "Armagh City, Banbridge and Craigavon"
            },
            {
              "code": "BDG",
              "name": "Barking and Dagenham"
            },
            {
              "code": "BNE",
              "name": "Barnet"
            },
            {
              "code": "BNS",
              "name": "Barnsley"
            },
            {
              "code": "BAS",
              "name": "Bath and North East Somerset"
            },
            {
              "code": "BDF",
              "name": "Bedford"
            },
            {
              "code": "BFS",
              "name": "Belfast City"
            },
            {
              "code": "BRK",
              "name": "Berkshire"
            },
            {
              "code": "BEX",
              "name": "Bexley"
            },
            {
              "code": "BIR",
              "name": "Birmingham"
            },
            {
              "code": "BBD",
              "name": "Blackburn with Darwen"
            },
            {
              "code": "BPL",
              "name": "Blackpool"
            },
            {
              "code": "BGW",
              "name": "Blaenau Gwent"
            },
            {
              "code": "BOL",
              "name": "Bolton"
            },
            {
              "code": "BCP",
              "name": "Bournemouth, Christchurch and Poole"
            },
            {
              "code": "BRC",
              "name": "Bracknell Forest"
            },
            {
              "code": "BRD",
              "name": "Bradford"
            },
            {
              "code": "BEN",
              "name": "Brent"
            },
            {
              "code": "BGE",
              "name": "Bridgend"
            },
            {
              "code": "BNH",
              "name": "Brighton and Hove"
            },
            {
              "code": "BST",
              "name": "Bristol, City of"
            },
            {
              "code": "BRY",
              "name": "Bromley"
            },
            {
              "code": "BKM",
              "name": "Buckinghamshire"
            },
            {
              "code": "BUR",
              "name": "Bury"
            },
            {
              "code": "CAY",
              "name": "Caerphilly"
            },
            {
              "code": "CLD",
              "name": "Calderdale"
            },
            {
              "code": "CAM",
              "name": "Cambridgeshire"
            },
            {
              "code": "CMD",
              "name": "Camden"
            },
            {
              "code": "CRF",
              "name": "Cardiff"
            },
            {
              "code": "CMN",
              "name": "Carmarthenshire"
            },
            {
              "code": "CCG",
              "name": "Causeway Coast and Glens"
            },
            {
              "code": "CBF",
              "name": "Central Bedfordshire"
            },
            {
              "code": "CGN",
              "name": "Ceredigion"
            },
            {
              "code": "CHE",
              "name": "Cheshire East"
            },
            {
              "code": "CHW",
              "name": "Cheshire West and Chester"
            },
            {
              "code": "CLK",
              "name": "Clackmannanshire"
            },
            {
              "code": "CWY",
              "name": "Conwy"
            },
            {
              "code": "CON",
              "name": "Cornwall"
            },
            {
              "code": "COV",
              "name": "Coventry"
            },
            {
              "code": "CRY",
              "name": "Croydon"
            },
            {
              "code": "CMA",
              "name": "Cumbria"
            },
            {
              "code": "DAL",
              "name": "Darlington"
            },
            {
              "code": "DEN",
              "name": "Denbighshire"
            },
            {
              "code": "DER",
              "name": "Derby"
            },
            {
              "code": "DBY",
              "name": "Derbyshire"
            },
            {
              "code": "DRS",
              "name": "Derry and Strabane"
            },
            {
              "code": "DEV",
              "name": "Devon"
            },
            {
              "code": "DNC",
              "name": "Doncaster"
            },
            {
              "code": "DOR",
              "name": "Dorset"
            },
            {
              "code": "DUD",
              "name": "Dudley"
            },
            {
              "code": "DGY",
              "name": "Dumfries and Galloway"
            },
            {
              "code": "DND",
              "name": "Dundee City"
            },
            {
              "code": "DUR",
              "name": "Durham, County"
            },
            {
              "code": "EAL",
              "name": "Ealing"
            },
            {
              "code": "EAY",
              "name": "East Ayrshire"
            },
            {
              "code": "EDU",
              "name": "East Dunbartonshire"
            },
            {
              "code": "ELN",
              "name": "East Lothian"
            },
            {
              "code": "ERW",
              "name": "East Renfrewshire"
            },
            {
              "code": "ERY",
              "name": "East Riding of Yorkshire"
            },
            {
              "code": "ESX",
              "name": "East Sussex"
            },
            {
              "code": "EDH",
              "name": "Edinburgh, City of"
            },
            {
              "code": "ELS",
              "name": "Eilean Siar"
            },
            {
              "code": "ENF",
              "name": "Enfield"
            },
            {
              "code": "ESS",
              "name": "Essex"
            },
            {
              "code": "FAL",
              "name": "Falkirk"
            },
            {
              "code": "FMO",
              "name": "Fermanagh and Omagh"
            },
            {
              "code": "FIF",
              "name": "Fife"
            },
            {
              "code": "FLN",
              "name": "Flintshire"
            },
            {
              "code": "GAT",
              "name": "Gateshead"
            },
            {
              "code": "GLG",
              "name": "Glasgow City"
            },
            {
              "code": "GLS",
              "name": "Gloucestershire"
            },
            {
              "code": "GRE",
              "name": "Greenwich"
            },
            {
              "code": "GWN",
              "name": "Gwynedd"
            },
            {
              "code": "HCK",
              "name": "Hackney"
            },
            {
              "code": "HAL",
              "name": "Halton"
            },
            {
              "code": "HMF",
              "name": "Hammersmith and Fulham"
            },
            {
              "code": "HAM",
              "name": "Hampshire"
            },
            {
              "code": "HRY",
              "name": "Haringey"
            },
            {
              "code": "HRW",
              "name": "Harrow"
            },
            {
              "code": "HPL",
              "name": "Hartlepool"
            },
            {
              "code": "HAV",
              "name": "Havering"
            },
            {
              "code": "HEF",
              "name": "Herefordshire"
            },
            {
              "code": "HRT",
              "name": "Hertfordshire"
            },
            {
              "code": "HLD",
              "name": "Highland"
            },
            {
              "code": "HIL",
              "name": "Hillingdon"
            },
            {
              "code": "HNS",
              "name": "Hounslow"
            },
            {
              "code": "IVC",
              "name": "Inverclyde"
            },
            {
              "code": "AGY",
              "name": "Isle of Anglesey"
            },
            {
              "code": "IOW",
              "name": "Isle of Wight"
            },
            {
              "code": "IOS",
              "name": "Isles of Scilly"
            },
            {
              "code": "ISL",
              "name": "Islington"
            },
            {
              "code": "KEC",
              "name": "Kensington and Chelsea"
            },
            {
              "code": "KEN",
              "name": "Kent"
            },
            {
              "code": "KHL",
              "name": "Kingston upon Hull"
            },
            {
              "code": "KTT",
              "name": "Kingston upon Thames"
            },
            {
              "code": "KIR",
              "name": "Kirklees"
            },
            {
              "code": "KWL",
              "name": "Knowsley"
            },
            {
              "code": "LBH",
              "name": "Lambeth"
            },
            {
              "code": "LAN",
              "name": "Lancashire"
            },
            {
              "code": "LDS",
              "name": "Leeds"
            },
            {
              "code": "LCE",
              "name": "Leicester"
            },
            {
              "code": "LEC",
              "name": "Leicestershire"
            },
            {
              "code": "LEW",
              "name": "Lewisham"
            },
            {
              "code": "LIN",
              "name": "Lincolnshire"
            },
            {
              "code": "LBC",
              "name": "Lisburn and Castlereagh"
            },
            {
              "code": "LIV",
              "name": "Liverpool"
            },
            {
              "code": "LND",
              "name": "London, City of"
            },
            {
              "code": "LUT",
              "name": "Luton"
            },
            {
              "code": "MAN",
              "name": "Manchester"
            },
            {
              "code": "MDW",
              "name": "Medway"
            },
            {
              "code": "MTY",
              "name": "Merthyr Tydfil"
            },
            {
              "code": "MRT",
              "name": "Merton"
            },
            {
              "code": "MEA",
              "name": "Mid and East Antrim"
            },
            {
              "code": "MUL",
              "name": "Mid-Ulster"
            },
            {
              "code": "MDB",
              "name": "Middlesbrough"
            },
            {
              "code": "MLN",
              "name": "Midlothian"
            },
            {
              "code": "MIK",
              "name": "Milton Keynes"
            },
            {
              "code": "MON",
              "name": "Monmouthshire"
            },
            {
              "code": "MRY",
              "name": "Moray"
            },
            {
              "code": "NTL",
              "name": "Neath Port Talbot"
            },
            {
              "code": "NET",
              "name": "Newcastle upon Tyne"
            },
            {
              "code": "NWM",
              "name": "Newham"
            },
            {
              "code": "NWP",
              "name": "Newport"
            },
            {
              "code": "NMD",
              "name": "Newry, Mourne and Down"
            },
            {
              "code": "NFK",
              "name": "Norfolk"
            },
            {
              "code": "NAY",
              "name": "North Ayrshire"
            },
            {
              "code": "NEL",
              "name": "North East Lincolnshire"
            },
            {
              "code": "NLK",
              "name": "North Lanarkshire"
            },
            {
              "code": "NLN",
              "name": "North Lincolnshire"
            },
            {
              "code": "NSM",
              "name": "North Somerset"
            },
            {
              "code": "NTY",
              "name": "North Tyneside"
            },
            {
              "code": "NYK",
              "name": "North Yorkshire"
            },
            {
              "code": "NTH",
              "name": "Northamptonshire"
            },
            {
              "code": "NBL",
              "name": "Northumberland"
            },
            {
              "code": "NGM",
              "name": "Nottingham"
            },
            {
              "code": "NTT",
              "name": "Nottinghamshire"
            },
            {
              "code": "OLD",
              "name": "Oldham"
            },
            {
              "code": "ORK",
              "name": "Orkney Islands"
            },
            {
              "code": "OXF",
              "name": "Oxfordshire"
            },
            {
              "code": "PEM",
              "name": "Pembrokeshire"
            },
            {
              "code": "PKN",
              "name": "Perth and Kinross"
            },
            {
              "code": "PTE",
              "name": "Peterborough"
            },
            {
              "code": "PLY",
              "name": "Plymouth"
            },
            {
              "code": "POR",
              "name": "Portsmouth"
            },
            {
              "code": "POW",
              "name": "Powys"
            },
            {
              "code": "RDG",
              "name": "Reading"
            },
            {
              "code": "RDB",
              "name": "Redbridge"
            },
            {
              "code": "RCC",
              "name": "Redcar and Cleveland"
            },
            {
              "code": "RFW",
              "name": "Renfrewshire"
            },
            {
              "code": "RCT",
              "name": "Rhondda Cynon Taff"
            },
            {
              "code": "RIC",
              "name": "Richmond upon Thames"
            },
            {
              "code": "RCH",
              "name": "Rochdale"
            },
            {
              "code": "ROT",
              "name": "Rotherham"
            },
            {
              "code": "RUT",
              "name": "Rutland"
            },
            {
              "code": "SLF",
              "name": "Salford"
            },
            {
              "code": "SAW",
              "name": "Sandwell"
            },
            {
              "code": "SCB",
              "name": "Scottish Borders"
            },
            {
              "code": "SFT",
              "name": "Sefton"
            },
            {
              "code": "SHF",
              "name": "Sheffield"
            },
            {
              "code": "ZET",
              "name": "Shetland Islands"
            },
            {
              "code": "SHR",
              "name": "Shropshire"
            },
            {
              "code": "SLG",
              "name": "Slough"
            },
            {
              "code": "SOL",
              "name": "Solihull"
            },
            {
              "code": "SOM",
              "name": "Somerset"
            },
            {
              "code": "SAY",
              "name": "South Ayrshire"
            },
            {
              "code": "SGC",
              "name": "South Gloucestershire"
            },
            {
              "code": "SLK",
              "name": "South Lanarkshire"
            },
            {
              "code": "STY",
              "name": "South Tyneside"
            },
            {
              "code": "STH",
              "name": "Southampton"
            },
            {
              "code": "SOS",
              "name": "Southend-on-Sea"
            },
            {
              "code": "SWK",
              "name": "Southwark"
            },
            {
              "code": "SHN",
              "name": "St. Helens"
            },
            {
              "code": "STS",
              "name": "Staffordshire"
            },
            {
              "code": "STG",
              "name": "Stirling"
            },
            {
              "code": "SKP",
              "name": "Stockport"
            },
            {
              "code": "STT",
              "name": "Stockton-on-Tees"
            },
            {
              "code": "STE",
              "name": "Stoke-on-Trent"
            },
            {
              "code": "SFK",
              "name": "Suffolk"
            },
            {
              "code": "SND",
              "name": "Sunderland"
            },
            {
              "code": "SRY",
              "name": "Surrey"
            },
            {
              "code": "STN",
              "name": "Sutton"
            },
            {
              "code": "SWA",
              "name": "Swansea"
            },
            {
              "code": "SWD",
              "name": "Swindon"
            },
            {
              "code": "TAM",
              "name": "Tameside"
            },
            {
              "code": "TFW",
              "name": "Telford and Wrekin"
            },
            {
              "code": "THR",
              "name": "Thurrock"
            },
            {
              "code": "TOB",
              "name": "Torbay"
            },
            {
              "code": "TOF",
              "name": "Torfaen"
            },
            {
              "code": "TWH",
              "name": "Tower Hamlets"
            },
            {
              "code": "TRF",
              "name": "Trafford"
            },
            {
              "code": "VGL",
              "name": "Vale of Glamorgan, The"
            },
            {
              "code": "WKF",
              "name": "Wakefield"
            },
            {
              "code": "WLL",
              "name": "Walsall"
            },
            {
              "code": "WFT",
              "name": "Waltham Forest"
            },
            {
              "code": "WND",
              "name": "Wandsworth"
            },
            {
              "code": "WRT",
              "name": "Warrington"
            },
            {
              "code": "WAR",
              "name": "Warwickshire"
            },
            {
              "code": "WBK",
              "name": "West Berkshire"
            },
            {
              "code": "WDU",
              "name": "West Dunbartonshire"
            },
            {
              "code": "WLN",
              "name": "West Lothian"
            },
            {
              "code": "WSX",
              "name": "West Sussex"
            },
            {
              "code": "WSM",
              "name": "Westminster"
            },
            {
              "code": "WGN",
              "name": "Wigan"
            },
            {
              "code": "WIL",
              "name": "Wiltshire"
            },
            {
              "code": "WNM",
              "name": "Windsor and Maidenhead"
            },
            {
              "code": "WRL",
              "name": "Wirral"
            },
            {
              "code": "WOK",
              "name": "Wokingham"
            },
            {
              "code": "WLV",
              "name": "Wolverhampton"
            },
            {
              "code": "WOR",
              "name": "Worcestershire"
            },
            {
              "code": "WRX",
              "name": "Wrexham"
            },
            {
              "code": "YOR",
              "name": "York"
            }
          ],
          "languages": [
            {
              "isoCode": "en",
              "name": "English",
              "native": "English"
            }
          ],
          "currencies": [
            {
              "isoCode": "GBP",
              "name": "Pound Sterling"
            }
          ]
        },
        {
          "isoCode": "GD",
          "name": "Grenada",
          "native": "Grenada",
          "flag": "🇬🇩",
          "phonePrefix": "+1473",
          "regions": [
            {
              "code": "01",
              "name": "Saint Andrew"
            },
            {
              "code": "02",
              "name": "Saint David"
            },
            {
              "code": "03",
              "name": "Saint George"
            },
            {
              "code": "04",
              "name": "Saint John"
            },
            {
              "code": "05",
              "name": "Saint Mark"
            },
            {
              "code": "06",
              "name": "Saint Patrick"
            },
            {
              "code": "10",
              "name": "Southern Grenadine Islands"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GE",
          "name": "Georgia",
          "native": "საქართველო",
          "flag": "🇬🇪",
          "phonePrefix": "+995",
          "regions": [
            {
              "code": "AB",
              "name": "Abkhazia (Sokhumi)"
            },
            {
              "code": "AJ",
              "name": "Ajaria (Bat'umi)"
            },
            {
              "code": "GU",
              "name": "Guria"
            },
            {
              "code": "IM",
              "name": "Imereti"
            },
            {
              "code": "KA",
              "name": "K'akheti"
            },
            {
              "code": "KK",
              "name": "Kvemo Kartli"
            },
            {
              "code": "MM",
              "name": "Mtshkheta-Mtianeti"
            },
            {
              "code": "RL",
              "name": "Rach'a-Lexhkumi-KvemoSvaneti"
            },
            {
              "code": "SZ",
              "name": "Samegrelo-Zemo Svaneti"
            },
            {
              "code": "SJ",
              "name": "Samtskhe-Javakheti"
            },
            {
              "code": "SK",
              "name": "Shida Kartli"
            },
            {
              "code": "TB",
              "name": "Tbilisi"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GF",
          "name": "French Guiana",
          "native": "Guyane française",
          "flag": "🇬🇫",
          "phonePrefix": "+594",
          "regions": [
            {
              "code": null,
              "name": "French Guiana"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "GG",
          "name": "Guernsey",
          "native": "Guernsey",
          "flag": "🇬🇬",
          "phonePrefix": "+44",
          "regions": [
            {
              "code": null,
              "name": "Castel"
            },
            {
              "code": null,
              "name": "Forest"
            },
            {
              "code": null,
              "name": "St. Andrew"
            },
            {
              "code": null,
              "name": "St. Martin"
            },
            {
              "code": null,
              "name": "St. Peter Port"
            },
            {
              "code": null,
              "name": "St. Pierre du Bois"
            },
            {
              "code": null,
              "name": "St. Sampson"
            },
            {
              "code": null,
              "name": "St. Saviour"
            },
            {
              "code": null,
              "name": "Torteval"
            },
            {
              "code": null,
              "name": "Vale"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "GBP",
              "name": "Pound Sterling"
            }
          ]
        },
        {
          "isoCode": "GH",
          "name": "Ghana",
          "native": "Ghana",
          "flag": "🇬🇭",
          "phonePrefix": "+233",
          "regions": [
            {
              "code": null,
              "name": "Ahafo"
            },
            {
              "code": "AH",
              "name": "Ashanti"
            },
            {
              "code": null,
              "name": "Bono"
            },
            {
              "code": null,
              "name": "Bono East"
            },
            {
              "code": "CP",
              "name": "Central"
            },
            {
              "code": "EP",
              "name": "Eastern"
            },
            {
              "code": "AA",
              "name": "Greater Accra"
            },
            {
              "code": "NP",
              "name": "Northern"
            },
            {
              "code": null,
              "name": "North East"
            },
            {
              "code": null,
              "name": "Oti"
            },
            {
              "code": null,
              "name": "Savannah"
            },
            {
              "code": "UE",
              "name": "Upper East"
            },
            {
              "code": "UW",
              "name": "Upper West"
            },
            {
              "code": "TV",
              "name": "Volta"
            },
            {
              "code": "WP",
              "name": "Western"
            },
            {
              "code": null,
              "name": "Western North"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GI",
          "name": "Gibraltar",
          "native": "Gibraltar",
          "flag": "🇬🇮",
          "phonePrefix": "+350",
          "regions": [
            {
              "code": null,
              "name": "Gibraltar"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GL",
          "name": "Greenland",
          "native": "Kalaallit Nunaat",
          "flag": "🇬🇱",
          "phonePrefix": "+299",
          "regions": [
            {
              "code": "KU",
              "name": "Kommune Kujalleq"
            },
            {
              "code": "SM",
              "name": "Kommuneqarfik Sermersooq"
            },
            {
              "code": "QA",
              "name": "Qaasuitsup Kommunia"
            },
            {
              "code": "QE",
              "name": "Qeqqata Kommunia"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GM",
          "name": "Republic of The Gambia",
          "native": "Gambia",
          "flag": "🇬🇲",
          "phonePrefix": "+220",
          "regions": [
            {
              "code": "B",
              "name": "Banjul"
            },
            {
              "code": "M",
              "name": "Central River"
            },
            {
              "code": "L",
              "name": "Lower River"
            },
            {
              "code": "N",
              "name": "North Bank"
            },
            {
              "code": "U",
              "name": "Upper River"
            },
            {
              "code": "W",
              "name": "Western"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GN",
          "name": "Guinea",
          "native": "Guinée",
          "flag": "🇬🇳",
          "phonePrefix": "+224",
          "regions": [
            {
              "code": "B",
              "name": "Boké"
            },
            {
              "code": "C",
              "name": "Conakry"
            },
            {
              "code": "F",
              "name": "Faranah"
            },
            {
              "code": "K",
              "name": "Kankan"
            },
            {
              "code": "D",
              "name": "Kindia"
            },
            {
              "code": "L",
              "name": "Labé"
            },
            {
              "code": "M",
              "name": "Mamou"
            },
            {
              "code": "N",
              "name": "Nzérékoré"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GP",
          "name": "Guadeloupe",
          "native": "Guadeloupe",
          "flag": "🇬🇵",
          "phonePrefix": "+590",
          "regions": [
            {
              "code": null,
              "name": "Guadeloupe"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "GQ",
          "name": "Equatorial Guinea",
          "native": "Guinea Ecuatorial",
          "flag": "🇬🇶",
          "phonePrefix": "+240",
          "regions": [
            {
              "code": "AN",
              "name": "Annobón"
            },
            {
              "code": "BN",
              "name": "Bioko Norte"
            },
            {
              "code": "BS",
              "name": "Bioko Sur"
            },
            {
              "code": "CS",
              "name": "Centro Sur"
            },
            {
              "code": "KN",
              "name": "Kié-Ntem"
            },
            {
              "code": "LI",
              "name": "Litoral"
            },
            {
              "code": "WN",
              "name": "Wele-Nzas"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GR",
          "name": "Greece",
          "native": "Ελλάδα",
          "flag": "🇬🇷",
          "phonePrefix": "+30",
          "regions": [
            {
              "code": "A",
              "name": "Anatolikí Makedonía kai Thráki"
            },
            {
              "code": "I",
              "name": "Attikḯ"
            },
            {
              "code": "G",
              "name": "Dytikí Elláda"
            },
            {
              "code": "C",
              "name": "Dytikí Makedonía"
            },
            {
              "code": "F",
              "name": "Ionía Nísia"
            },
            {
              "code": "B",
              "name": "Kentrikí Makedonía"
            },
            {
              "code": "M",
              "name": "Krítí"
            },
            {
              "code": "L",
              "name": "Notío Aigaío"
            },
            {
              "code": "J",
              "name": "Peloponnísos"
            },
            {
              "code": "H",
              "name": "Stereá Elláda"
            },
            {
              "code": "E",
              "name": "Thessalía"
            },
            {
              "code": "K",
              "name": "Voreío Aigaío"
            },
            {
              "code": "D",
              "name": "Ípeiros"
            },
            {
              "code": "69",
              "name": "Ágion Óros"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "GS",
          "name": "South Georgia and the South Sandwich Islands",
          "native": "South Georgia",
          "flag": "🇬🇸",
          "phonePrefix": "+500",
          "regions": [
            {
              "code": null,
              "name": "Bird Island"
            },
            {
              "code": null,
              "name": "Bristol Island"
            },
            {
              "code": null,
              "name": "Clerke Rocks"
            },
            {
              "code": null,
              "name": "Montagu Island"
            },
            {
              "code": null,
              "name": "Saunders Island"
            },
            {
              "code": null,
              "name": "South Georgia"
            },
            {
              "code": null,
              "name": "Southern Thule"
            },
            {
              "code": null,
              "name": "Traversay Islands"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "GBP",
              "name": "Pound Sterling"
            }
          ]
        },
        {
          "isoCode": "GT",
          "name": "Guatemala",
          "native": "Guatemala",
          "flag": "🇬🇹",
          "phonePrefix": "+502",
          "regions": [
            {
              "code": "AV",
              "name": "Alta Verapaz"
            },
            {
              "code": "BV",
              "name": "Baja Verapaz"
            },
            {
              "code": "CM",
              "name": "Chimaltenango"
            },
            {
              "code": "CQ",
              "name": "Chiquimula"
            },
            {
              "code": "PR",
              "name": "El Progreso"
            },
            {
              "code": "ES",
              "name": "Escuintla"
            },
            {
              "code": "GU",
              "name": "Guatemala"
            },
            {
              "code": "HU",
              "name": "Huehuetenango"
            },
            {
              "code": "IZ",
              "name": "Izabal"
            },
            {
              "code": "JA",
              "name": "Jalapa"
            },
            {
              "code": "JU",
              "name": "Jutiapa"
            },
            {
              "code": "PE",
              "name": "Petén"
            },
            {
              "code": "QZ",
              "name": "Quetzaltenango"
            },
            {
              "code": "QC",
              "name": "Quiché"
            },
            {
              "code": "Re",
              "name": "Retalhuleu"
            },
            {
              "code": "SA",
              "name": "Sacatepéquez"
            },
            {
              "code": "SM",
              "name": "San Marcos"
            },
            {
              "code": "SR",
              "name": "Santa Rosa"
            },
            {
              "code": "SO",
              "name": "Sololá"
            },
            {
              "code": "SU",
              "name": "Suchitepéquez"
            },
            {
              "code": "TO",
              "name": "Totonicapán"
            },
            {
              "code": "ZA",
              "name": "Zacapa"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GU",
          "name": "Guam",
          "native": "Guam",
          "flag": "🇬🇺",
          "phonePrefix": "+1671",
          "regions": [
            {
              "code": null,
              "name": "Guam"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "GW",
          "name": "Guinea-Bissau",
          "native": "Guiné-Bissau",
          "flag": "🇬🇼",
          "phonePrefix": "+245",
          "regions": [
            {
              "code": "BA",
              "name": "Bafatá"
            },
            {
              "code": "BM",
              "name": "Biombo"
            },
            {
              "code": "BS",
              "name": "Bissau"
            },
            {
              "code": "BL",
              "name": "Bolama-Bijagos"
            },
            {
              "code": "CA",
              "name": "Cacheu"
            },
            {
              "code": "GA",
              "name": "Gabú"
            },
            {
              "code": "OI",
              "name": "Oio"
            },
            {
              "code": "QU",
              "name": "Quinara"
            },
            {
              "code": "TO",
              "name": "Tombali"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "GY",
          "name": "Guyana",
          "native": "Guyana",
          "flag": "🇬🇾",
          "phonePrefix": "+592",
          "regions": [
            {
              "code": "BA",
              "name": "Barima-Waini"
            },
            {
              "code": "CU",
              "name": "Cuyuni-Mazaruni"
            },
            {
              "code": "DE",
              "name": "Demerara-Mahaica"
            },
            {
              "code": "EB",
              "name": "East Berbice-Corentyne"
            },
            {
              "code": "ES",
              "name": "Essequibo Islands-West Demerara"
            },
            {
              "code": "MA",
              "name": "Mahaica-Berbice"
            },
            {
              "code": "PM",
              "name": "Pomeroon-Supenaam"
            },
            {
              "code": "PT",
              "name": "Potaro-Siparuni"
            },
            {
              "code": "UD",
              "name": "Upper Demerara-Berbice"
            },
            {
              "code": "UT",
              "name": "Upper Takutu-Upper Essequibo"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "HK",
          "name": "Hong Kong",
          "native": "香港",
          "flag": "🇭🇰",
          "phonePrefix": "+852",
          "regions": [
            {
              "code": "HK",
              "name": "Hong Kong"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "HM",
          "name": "Heard Island and McDonald Islands",
          "native": "Heard Island and McDonald Islands",
          "flag": "🇭🇲",
          "phonePrefix": "+61",
          "regions": [
            {
              "code": null,
              "name": "Heard Island and McDonald Islands"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "HN",
          "name": "Honduras",
          "native": "Honduras",
          "flag": "🇭🇳",
          "phonePrefix": "+504",
          "regions": [
            {
              "code": "AT",
              "name": "Atlántida"
            },
            {
              "code": "CH",
              "name": "Choluteca"
            },
            {
              "code": "CL",
              "name": "Colón"
            },
            {
              "code": "CM",
              "name": "Comayagua"
            },
            {
              "code": "CP",
              "name": "Copán"
            },
            {
              "code": "CR",
              "name": "Cortés"
            },
            {
              "code": "EP",
              "name": "El Paraíso"
            },
            {
              "code": "FM",
              "name": "Francisco Morazán"
            },
            {
              "code": "GD",
              "name": "Gracias a Dios"
            },
            {
              "code": "IN",
              "name": "Intibucá"
            },
            {
              "code": "IB",
              "name": "Islas de la Bahía"
            },
            {
              "code": "LP",
              "name": "La Paz"
            },
            {
              "code": "LE",
              "name": "Lempira"
            },
            {
              "code": "OC",
              "name": "Ocotepeque"
            },
            {
              "code": "OL",
              "name": "Olancho"
            },
            {
              "code": "SB",
              "name": "Santa Bárbara"
            },
            {
              "code": "VA",
              "name": "Valle"
            },
            {
              "code": "YO",
              "name": "Yoro"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "HR",
          "name": "Croatia",
          "native": "Hrvatska",
          "flag": "🇭🇷",
          "phonePrefix": "+385",
          "regions": [
            {
              "code": "07",
              "name": "Bjelovarsko-bilogorska županija"
            },
            {
              "code": "12",
              "name": "Brodsko-posavska županija"
            },
            {
              "code": "19",
              "name": "Dubrovačko-neretvanska županija"
            },
            {
              "code": "21",
              "name": "Grad Zagreb"
            },
            {
              "code": "18",
              "name": "Istarska županija"
            },
            {
              "code": "04",
              "name": "Karlovačka županija"
            },
            {
              "code": "06",
              "name": "Koprivničko-križevačka županija"
            },
            {
              "code": "02",
              "name": "Krapinsko-zagorska županija"
            },
            {
              "code": "09",
              "name": "Ličko-senjska županija"
            },
            {
              "code": "20",
              "name": "Međimurska županija"
            },
            {
              "code": "14",
              "name": "Osječko-baranjska županija"
            },
            {
              "code": "11",
              "name": "Požeško-slavonska županija"
            },
            {
              "code": "08",
              "name": "Primorsko-goranska županija"
            },
            {
              "code": "03",
              "name": "Sisačko-moslavačka županija"
            },
            {
              "code": "17",
              "name": "Splitsko-dalmatinska županija"
            },
            {
              "code": "15",
              "name": "Šibensko-kninska županija"
            },
            {
              "code": "05",
              "name": "Varaždinska županija"
            },
            {
              "code": "10",
              "name": "Virovitičko-podravska županija"
            },
            {
              "code": "16",
              "name": "Vukovarsko-srijemska županija"
            },
            {
              "code": "13",
              "name": "Zadarska županija"
            },
            {
              "code": "01",
              "name": "Zagrebačka županija"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "HT",
          "name": "Haiti",
          "native": "Haïti",
          "flag": "🇭🇹",
          "phonePrefix": "+509",
          "regions": [
            {
              "code": "AR",
              "name": "Artibonite"
            },
            {
              "code": "CE",
              "name": "Centre"
            },
            {
              "code": "GA",
              "name": "Grand'Anse"
            },
            {
              "code": "NI",
              "name": "Nippes"
            },
            {
              "code": "ND",
              "name": "Nord"
            },
            {
              "code": "NE",
              "name": "Nord-Est"
            },
            {
              "code": "NO",
              "name": "Nord-Ouest"
            },
            {
              "code": "OU",
              "name": "Ouest"
            },
            {
              "code": "SD",
              "name": "Sud"
            },
            {
              "code": "SE",
              "name": "Sud-Est"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "HU",
          "name": "Hungary",
          "native": "Magyarország",
          "flag": "🇭🇺",
          "phonePrefix": "+36",
          "regions": [
            {
              "code": "BK",
              "name": "Bács-Kiskun"
            },
            {
              "code": "BA",
              "name": "Baranya"
            },
            {
              "code": "BE",
              "name": "Békés"
            },
            {
              "code": "BC",
              "name": "Békéscsaba"
            },
            {
              "code": "BZ",
              "name": "Borsod-Abauj-Zemplen"
            },
            {
              "code": "BU",
              "name": "Budapest"
            },
            {
              "code": "CS",
              "name": "Csongrád"
            },
            {
              "code": "DE",
              "name": "Debrecen"
            },
            {
              "code": "DU",
              "name": "Dunaújváros"
            },
            {
              "code": "EG",
              "name": "Eger"
            },
            {
              "code": "ER",
              "name": "Érd"
            },
            {
              "code": "FE",
              "name": "Fejér"
            },
            {
              "code": "GY",
              "name": "Győr"
            },
            {
              "code": "GS",
              "name": "Győr-Moson-Sopron"
            },
            {
              "code": "HB",
              "name": "Hajdú-Bihar"
            },
            {
              "code": "HE",
              "name": "Heves"
            },
            {
              "code": "HV",
              "name": "Hódmezővásárhely"
            },
            {
              "code": "N",
              "name": "Jász-Nagykun-Szolnok"
            },
            {
              "code": "KV",
              "name": "Kaposvár"
            },
            {
              "code": "KM",
              "name": "Kecskemét"
            },
            {
              "code": "KE",
              "name": "Komárom-Esztergom"
            },
            {
              "code": "MI",
              "name": "Miskolc"
            },
            {
              "code": "NK",
              "name": "Nagykanizsa"
            },
            {
              "code": "NO",
              "name": "Nógrád"
            },
            {
              "code": "NY",
              "name": "Nyíregyháza"
            },
            {
              "code": "PS",
              "name": "Pécs"
            },
            {
              "code": "PE",
              "name": "Pest"
            },
            {
              "code": "ST",
              "name": "Salgótarján"
            },
            {
              "code": "SO",
              "name": "Somogy"
            },
            {
              "code": "SN",
              "name": "Sopron"
            },
            {
              "code": "SZ",
              "name": "Szabolcs-á-Bereg"
            },
            {
              "code": "SD",
              "name": "Szeged"
            },
            {
              "code": "SF",
              "name": "Székesfehérvár"
            },
            {
              "code": "SS",
              "name": "Szekszárd"
            },
            {
              "code": "SK",
              "name": "Szolnok"
            },
            {
              "code": "SH",
              "name": "Szombathely"
            },
            {
              "code": "TB",
              "name": "Tatabánya"
            },
            {
              "code": "TO",
              "name": "Tolna"
            },
            {
              "code": "VA",
              "name": "Vas"
            },
            {
              "code": "VE",
              "name": "Veszprém"
            },
            {
              "code": "VM",
              "name": "Veszprém (City)"
            },
            {
              "code": "ZA",
              "name": "Zala"
            },
            {
              "code": "ZE",
              "name": "Zalaegerszeg"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ID",
          "name": "Indonesia",
          "native": "Indonesia",
          "flag": "🇮🇩",
          "phonePrefix": "+62",
          "regions": [
            {
              "code": "AC",
              "name": "Aceh"
            },
            {
              "code": "BA",
              "name": "Bali"
            },
            {
              "code": "BB",
              "name": "Bangka Belitung"
            },
            {
              "code": "BT",
              "name": "Banten"
            },
            {
              "code": "BE",
              "name": "Bengkulu"
            },
            {
              "code": "GO",
              "name": "Gorontalo"
            },
            {
              "code": "JK",
              "name": "Jakarta Raya"
            },
            {
              "code": "JA",
              "name": "Jambi"
            },
            {
              "code": "JB",
              "name": "Jawa Barat"
            },
            {
              "code": "JT",
              "name": "Jawa Tengah"
            },
            {
              "code": "JI",
              "name": "Jawa Timur"
            },
            {
              "code": "KB",
              "name": "Kalimantan Barat"
            },
            {
              "code": "KS",
              "name": "Kalimantan Selatan"
            },
            {
              "code": "KT",
              "name": "Kalimantan Tengah"
            },
            {
              "code": "KI",
              "name": "Kalimantan Timur"
            },
            {
              "code": "KU",
              "name": "Kalimantan Utara"
            },
            {
              "code": "KR",
              "name": "Kepulauan Riau"
            },
            {
              "code": "LA",
              "name": "Lampung"
            },
            {
              "code": "MA",
              "name": "Maluku"
            },
            {
              "code": "MU",
              "name": "Maluku Utara"
            },
            {
              "code": "NB",
              "name": "Nusa Tenggara Barat"
            },
            {
              "code": "NT",
              "name": "Nusa Tenggara Timur"
            },
            {
              "code": "PA",
              "name": "Papua"
            },
            {
              "code": "PB",
              "name": "Papua Barat"
            },
            {
              "code": "RI",
              "name": "Riau"
            },
            {
              "code": "SR",
              "name": "Sulawesi Selatan"
            },
            {
              "code": "ST",
              "name": "Sulawesi Tengah"
            },
            {
              "code": "SG",
              "name": "Sulawesi Tenggara"
            },
            {
              "code": "SA",
              "name": "Sulawesi Utara"
            },
            {
              "code": "SB",
              "name": "Sumatera Barat"
            },
            {
              "code": "SS",
              "name": "Sumatera Selatan"
            },
            {
              "code": "SU",
              "name": "Sumatera Utara"
            },
            {
              "code": "YO",
              "name": "Yogyakarta"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "IE",
          "name": "Ireland",
          "native": "Éire",
          "flag": "🇮🇪",
          "phonePrefix": "+353",
          "regions": [
            {
              "code": "CW",
              "name": "Carlow"
            },
            {
              "code": "CN",
              "name": "Cavan"
            },
            {
              "code": "CE",
              "name": "Clare"
            },
            {
              "code": "CO",
              "name": "Cork"
            },
            {
              "code": "DL",
              "name": "Donegal"
            },
            {
              "code": "D",
              "name": "Dublin"
            },
            {
              "code": "G",
              "name": "Galway"
            },
            {
              "code": "KY",
              "name": "Kerry"
            },
            {
              "code": "KE",
              "name": "Kildare"
            },
            {
              "code": "KK",
              "name": "Kilkenny"
            },
            {
              "code": "LS",
              "name": "Laois"
            },
            {
              "code": "LM",
              "name": "Leitrim"
            },
            {
              "code": "LK",
              "name": "Limerick"
            },
            {
              "code": "LD",
              "name": "Longford"
            },
            {
              "code": "LH",
              "name": "Louth"
            },
            {
              "code": "MO",
              "name": "Mayo"
            },
            {
              "code": "MH",
              "name": "Meath"
            },
            {
              "code": "MN",
              "name": "Monaghan"
            },
            {
              "code": "OY",
              "name": "Offaly"
            },
            {
              "code": "RN",
              "name": "Roscommon"
            },
            {
              "code": "SO",
              "name": "Sligo"
            },
            {
              "code": "TA",
              "name": "Tipperary"
            },
            {
              "code": "WD",
              "name": "Waterford"
            },
            {
              "code": "WH",
              "name": "Westmeath"
            },
            {
              "code": "WX",
              "name": "Wexford"
            },
            {
              "code": "WW",
              "name": "Wicklow"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "IL",
          "name": "Israel",
          "native": "יִשְׂרָאֵל",
          "flag": "🇮🇱",
          "phonePrefix": "+972",
          "regions": [
            {
              "code": "D",
              "name": "HaDarom"
            },
            {
              "code": "M",
              "name": "HaMerkaz"
            },
            {
              "code": "Z",
              "name": "HaTsafon"
            },
            {
              "code": "HA",
              "name": "H̱efa"
            },
            {
              "code": "TA",
              "name": "Tel-Aviv"
            },
            {
              "code": "JM",
              "name": "Yerushalayim"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "IM",
          "name": "Isle of Man",
          "native": "Isle of Man",
          "flag": "🇮🇲",
          "phonePrefix": "+44",
          "regions": [
            {
              "code": null,
              "name": "Isle of Man"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "GBP",
              "name": "Pound Sterling"
            }
          ]
        },
        {
          "isoCode": "IN",
          "name": "India",
          "native": "भारत",
          "flag": "🇮🇳",
          "phonePrefix": "+91",
          "regions": [
            {
              "code": "AN",
              "name": "Andaman and Nicobar Islands"
            },
            {
              "code": "AP",
              "name": "Andhra Pradesh"
            },
            {
              "code": "AR",
              "name": "Arunachal Pradesh"
            },
            {
              "code": "AS",
              "name": "Assam"
            },
            {
              "code": "BR",
              "name": "Bihar"
            },
            {
              "code": "CH",
              "name": "Chandigarh"
            },
            {
              "code": "CT",
              "name": "Chhattisgarh"
            },
            {
              "code": "DH",
              "name": "Dadra and Nagar Haveli and Daman and Diu"
            },
            {
              "code": "DL",
              "name": "Delhi"
            },
            {
              "code": "GA",
              "name": "Goa"
            },
            {
              "code": "GJ",
              "name": "Gujarat"
            },
            {
              "code": "HR",
              "name": "Haryana"
            },
            {
              "code": "HP",
              "name": "Himachal Pradesh"
            },
            {
              "code": "JK",
              "name": "Jammu and Kashmir"
            },
            {
              "code": "JH",
              "name": "Jharkhand"
            },
            {
              "code": "KA",
              "name": "Karnataka"
            },
            {
              "code": "KL",
              "name": "Kerala"
            },
            {
              "code": "LA",
              "name": "Ladakh"
            },
            {
              "code": "LD",
              "name": "Lakshadweep"
            },
            {
              "code": "MP",
              "name": "Madhya Pradesh"
            },
            {
              "code": "MH",
              "name": "Maharashtra"
            },
            {
              "code": "MN",
              "name": "Manipur"
            },
            {
              "code": "ML",
              "name": "Meghalaya"
            },
            {
              "code": "MZ",
              "name": "Mizoram"
            },
            {
              "code": "NL",
              "name": "Nagaland"
            },
            {
              "code": "OR",
              "name": "Odisha"
            },
            {
              "code": "PY",
              "name": "Puducherry"
            },
            {
              "code": "PB",
              "name": "Punjab"
            },
            {
              "code": "RJ",
              "name": "Rajasthan"
            },
            {
              "code": "SK",
              "name": "Sikkim"
            },
            {
              "code": "TN",
              "name": "Tamil Nadu"
            },
            {
              "code": "TG",
              "name": "Telangana"
            },
            {
              "code": "TR",
              "name": "Tripura"
            },
            {
              "code": "UT",
              "name": "Uttarakhand"
            },
            {
              "code": "UP",
              "name": "Uttar Pradesh"
            },
            {
              "code": "WB",
              "name": "West Bengal"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "IO",
          "name": "British Indian Ocean Territory",
          "native": "British Indian Ocean Territory",
          "flag": "🇮🇴",
          "phonePrefix": "+246",
          "regions": [
            {
              "code": "IO",
              "name": "British Indian Ocean Territory"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "IQ",
          "name": "Iraq",
          "native": "العراق",
          "flag": "🇮🇶",
          "phonePrefix": "+964",
          "regions": [
            {
              "code": "AN",
              "name": "Al Anbār"
            },
            {
              "code": "BA",
              "name": "Al Başrah"
            },
            {
              "code": "MU",
              "name": "Al Muthanná"
            },
            {
              "code": "QA",
              "name": "Al Qādisīyah"
            },
            {
              "code": "NA",
              "name": "An Najaf"
            },
            {
              "code": "AR",
              "name": "Arbīl"
            },
            {
              "code": "SU",
              "name": "As Sulaymānīyah"
            },
            {
              "code": "BB",
              "name": "Bābil"
            },
            {
              "code": "BG",
              "name": "Baghdād"
            },
            {
              "code": "DA",
              "name": "Dohuk"
            },
            {
              "code": "DQ",
              "name": "Dhī Qār"
            },
            {
              "code": "DI",
              "name": "Diyālá"
            },
            {
              "code": "KA",
              "name": "Karbalā'"
            },
            {
              "code": "KI",
              "name": "Kirkuk"
            },
            {
              "code": "MA",
              "name": "Maysān"
            },
            {
              "code": "NI",
              "name": "Nīnawá"
            },
            {
              "code": "SD",
              "name": "Şalāḩ ad Dīn"
            },
            {
              "code": "WA",
              "name": "Wāsiţ"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "IR",
          "name": "Islamic Republic of Iran",
          "native": "ایران",
          "flag": "🇮🇷",
          "phonePrefix": "+98",
          "regions": [
            {
              "code": "32",
              "name": "Alborz"
            },
            {
              "code": "03",
              "name": "Ardabīl"
            },
            {
              "code": "02",
              "name": "Āz̄arbāyjān-e Gharbī"
            },
            {
              "code": "01",
              "name": "Āz̄arbāyjān-e Sharqī"
            },
            {
              "code": "06",
              "name": "Būshehr"
            },
            {
              "code": "08",
              "name": "Chahār Maḩāl va Bakhtīārī"
            },
            {
              "code": "04",
              "name": "Eşfahān"
            },
            {
              "code": "14",
              "name": "Fārs"
            },
            {
              "code": "19",
              "name": "Gīlān"
            },
            {
              "code": "27",
              "name": "Golestān"
            },
            {
              "code": "24",
              "name": "Hamadān"
            },
            {
              "code": "23",
              "name": "Hormozgān"
            },
            {
              "code": "05",
              "name": "Īlām"
            },
            {
              "code": "15",
              "name": "Kermān"
            },
            {
              "code": "17",
              "name": "Kermānshāh"
            },
            {
              "code": "29",
              "name": "Khorāsān-e Jonūbī"
            },
            {
              "code": "30",
              "name": "Khorāsān-e Raẕavī"
            },
            {
              "code": "61",
              "name": "Khorāsān-e Shomālī"
            },
            {
              "code": "10",
              "name": "Khūzestān"
            },
            {
              "code": "18",
              "name": "Kohgīlūyeh va Bowyer Aḩmad"
            },
            {
              "code": "16",
              "name": "Kordestān"
            },
            {
              "code": "20",
              "name": "Lorestān"
            },
            {
              "code": "22",
              "name": "Markazi"
            },
            {
              "code": "21",
              "name": "Māzandarān"
            },
            {
              "code": "28",
              "name": "Qazvīn"
            },
            {
              "code": "26",
              "name": "Qom"
            },
            {
              "code": "12",
              "name": "Semnān"
            },
            {
              "code": "13",
              "name": "Sīstān va Balūchestān"
            },
            {
              "code": "07",
              "name": "Tehrān"
            },
            {
              "code": "25",
              "name": "Yazd"
            },
            {
              "code": "11",
              "name": "Zanjān"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "IS",
          "name": "Iceland",
          "native": "Ísland",
          "flag": "🇮🇸",
          "phonePrefix": "+354",
          "regions": [
            {
              "code": "7",
              "name": "Austurland"
            },
            {
              "code": "1",
              "name": "Höfuðborgarsvæði"
            },
            {
              "code": "6",
              "name": "Norðurland eystra"
            },
            {
              "code": "5",
              "name": "Norðurland vestra"
            },
            {
              "code": "8",
              "name": "Suðurland"
            },
            {
              "code": "2",
              "name": "Suðurnes"
            },
            {
              "code": "4",
              "name": "Vestfirðir"
            },
            {
              "code": "3",
              "name": "Vesturland"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "IT",
          "name": "Italy",
          "native": "Italia",
          "flag": "🇮🇹",
          "phonePrefix": "+39",
          "regions": [
            {
              "code": "65",
              "name": "Abruzzo"
            },
            {
              "code": "77",
              "name": "Basilicata"
            },
            {
              "code": "78",
              "name": "Calabria"
            },
            {
              "code": "72",
              "name": "Campania"
            },
            {
              "code": "45",
              "name": "Emilia-Romagna"
            },
            {
              "code": "36",
              "name": "Friuli-Venezia Giulia"
            },
            {
              "code": "62",
              "name": "Lazio"
            },
            {
              "code": "42",
              "name": "Liguria"
            },
            {
              "code": "25",
              "name": "Lombardia"
            },
            {
              "code": "57",
              "name": "Marche"
            },
            {
              "code": "67",
              "name": "Molise"
            },
            {
              "code": "21",
              "name": "Piemonte"
            },
            {
              "code": "75",
              "name": "Puglia"
            },
            {
              "code": "88",
              "name": "Sardegna"
            },
            {
              "code": "82",
              "name": "Sicilia"
            },
            {
              "code": "52",
              "name": "Toscana"
            },
            {
              "code": "32",
              "name": "Trentino-Alto Adige"
            },
            {
              "code": "55",
              "name": "Umbria"
            },
            {
              "code": "23",
              "name": "Valle d'Aosta"
            },
            {
              "code": "34",
              "name": "Veneto"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "JE",
          "name": "Jersey",
          "native": "Jersey",
          "flag": "🇯🇪",
          "phonePrefix": "+44",
          "regions": [
            {
              "code": null,
              "name": "Jersey"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "GBP",
              "name": "Pound Sterling"
            }
          ]
        },
        {
          "isoCode": "JM",
          "name": "Jamaica",
          "native": "Jamaica",
          "flag": "🇯🇲",
          "phonePrefix": "+1876",
          "regions": [
            {
              "code": "13",
              "name": "Clarendon"
            },
            {
              "code": "09",
              "name": "Hanover"
            },
            {
              "code": "01",
              "name": "Kingston"
            },
            {
              "code": "12",
              "name": "Manchester"
            },
            {
              "code": "04",
              "name": "Portland"
            },
            {
              "code": "02",
              "name": "Saint Andrew"
            },
            {
              "code": "06",
              "name": "Saint Ann"
            },
            {
              "code": "14",
              "name": "Saint Catherine"
            },
            {
              "code": "11",
              "name": "Saint Elizabeth"
            },
            {
              "code": "08",
              "name": "Saint James"
            },
            {
              "code": "05",
              "name": "Saint Mary"
            },
            {
              "code": "03",
              "name": "Saint Thomas"
            },
            {
              "code": "07",
              "name": "Trelawny"
            },
            {
              "code": "10",
              "name": "Westmoreland"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "JO",
          "name": "Jordan",
          "native": "الأردن",
          "flag": "🇯🇴",
          "phonePrefix": "+962",
          "regions": [
            {
              "code": "AJ",
              "name": "‘Ajlūn"
            },
            {
              "code": "AQ",
              "name": "Al 'Aqabah"
            },
            {
              "code": "BA",
              "name": "Al Balqā’"
            },
            {
              "code": "KA",
              "name": "Al Karak"
            },
            {
              "code": "MA",
              "name": "Al Mafraq"
            },
            {
              "code": "AM",
              "name": "Al ‘A̅şimah"
            },
            {
              "code": "AT",
              "name": "Aţ Ţafīlah"
            },
            {
              "code": "AZ",
              "name": "Az Zarqā’"
            },
            {
              "code": "IR",
              "name": "Irbid"
            },
            {
              "code": "JA",
              "name": "Jarash"
            },
            {
              "code": "MN",
              "name": "Ma‘ān"
            },
            {
              "code": "MD",
              "name": "Mādabā"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "JP",
          "name": "Japan",
          "native": "日本",
          "flag": "🇯🇵",
          "phonePrefix": "+81",
          "regions": [
            {
              "code": "23",
              "name": "Aichi"
            },
            {
              "code": "05",
              "name": "Akita"
            },
            {
              "code": "02",
              "name": "Aomori"
            },
            {
              "code": "12",
              "name": "Chiba"
            },
            {
              "code": "38",
              "name": "Ehime"
            },
            {
              "code": "18",
              "name": "Fukui"
            },
            {
              "code": "40",
              "name": "Fukuoka"
            },
            {
              "code": "07",
              "name": "Fukushima"
            },
            {
              "code": "21",
              "name": "Gifu"
            },
            {
              "code": "10",
              "name": "Gunma"
            },
            {
              "code": "34",
              "name": "Hiroshima"
            },
            {
              "code": "01",
              "name": "Hokkaido"
            },
            {
              "code": "28",
              "name": "Hyogo"
            },
            {
              "code": "08",
              "name": "Ibaraki"
            },
            {
              "code": "17",
              "name": "Ishikawa"
            },
            {
              "code": "03",
              "name": "Iwate"
            },
            {
              "code": "37",
              "name": "Kagawa"
            },
            {
              "code": "46",
              "name": "Kagoshima"
            },
            {
              "code": "14",
              "name": "Kanagawa"
            },
            {
              "code": "39",
              "name": "Kochi"
            },
            {
              "code": "43",
              "name": "Kumamoto"
            },
            {
              "code": "26",
              "name": "Kyoto"
            },
            {
              "code": "24",
              "name": "Mie"
            },
            {
              "code": "04",
              "name": "Miyagi"
            },
            {
              "code": "45",
              "name": "Miyazaki"
            },
            {
              "code": "20",
              "name": "Nagano"
            },
            {
              "code": "42",
              "name": "Nagasaki"
            },
            {
              "code": "29",
              "name": "Nara"
            },
            {
              "code": "15",
              "name": "Niigata"
            },
            {
              "code": "44",
              "name": "Oita"
            },
            {
              "code": "33",
              "name": "Okayama"
            },
            {
              "code": "47",
              "name": "Okinawa"
            },
            {
              "code": "27",
              "name": "Osaka"
            },
            {
              "code": "41",
              "name": "Saga"
            },
            {
              "code": "11",
              "name": "Saitama"
            },
            {
              "code": "25",
              "name": "Shiga"
            },
            {
              "code": "32",
              "name": "Shimane"
            },
            {
              "code": "22",
              "name": "Shizuoka"
            },
            {
              "code": "09",
              "name": "Tochigi"
            },
            {
              "code": "36",
              "name": "Tokushima"
            },
            {
              "code": "13",
              "name": "Tokyo"
            },
            {
              "code": "31",
              "name": "Tottori"
            },
            {
              "code": "16",
              "name": "Toyama"
            },
            {
              "code": "30",
              "name": "Wakayama"
            },
            {
              "code": "06",
              "name": "Yamagata"
            },
            {
              "code": "35",
              "name": "Yamaguchi"
            },
            {
              "code": "19",
              "name": "Yamanashi"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KE",
          "name": "Kenya",
          "native": "Kenya",
          "flag": "🇰🇪",
          "phonePrefix": "+254",
          "regions": [
            {
              "code": "01",
              "name": "Baringo"
            },
            {
              "code": "02",
              "name": "Bomet"
            },
            {
              "code": "03",
              "name": "Bungoma"
            },
            {
              "code": "04",
              "name": "Busia"
            },
            {
              "code": "05",
              "name": "Eleyo/Marakwet"
            },
            {
              "code": "06",
              "name": "Embu"
            },
            {
              "code": "07",
              "name": "Garissa"
            },
            {
              "code": "08",
              "name": "Homa Bay"
            },
            {
              "code": "09",
              "name": "Isiolo"
            },
            {
              "code": "10",
              "name": "Kajiado"
            },
            {
              "code": "11",
              "name": "Kakamega"
            },
            {
              "code": "12",
              "name": "Kericho"
            },
            {
              "code": "13",
              "name": "Kiambu"
            },
            {
              "code": "14",
              "name": "Kilifi"
            },
            {
              "code": "15",
              "name": "Kirinyaga"
            },
            {
              "code": "16",
              "name": "Kisii"
            },
            {
              "code": "17",
              "name": "Kisumu"
            },
            {
              "code": "18",
              "name": "Kitui"
            },
            {
              "code": "19",
              "name": "Kwale"
            },
            {
              "code": "20",
              "name": "Laikipia"
            },
            {
              "code": "21",
              "name": "Lamu"
            },
            {
              "code": "22",
              "name": "Machakos"
            },
            {
              "code": "23",
              "name": "Makueni"
            },
            {
              "code": "24",
              "name": "Mandera"
            },
            {
              "code": "25",
              "name": "Marsabit"
            },
            {
              "code": "26",
              "name": "Meru"
            },
            {
              "code": "27",
              "name": "Migori"
            },
            {
              "code": "28",
              "name": "Mombasa"
            },
            {
              "code": "29",
              "name": "Murang'a"
            },
            {
              "code": "30",
              "name": "Nairobi City"
            },
            {
              "code": "31",
              "name": "Nakuru"
            },
            {
              "code": "32",
              "name": "Nandi"
            },
            {
              "code": "33",
              "name": "Narok"
            },
            {
              "code": "34",
              "name": "Nyamira"
            },
            {
              "code": "35",
              "name": "Nyandarua"
            },
            {
              "code": "36",
              "name": "Nyeri"
            },
            {
              "code": "37",
              "name": "Samburu"
            },
            {
              "code": "38",
              "name": "Siaya"
            },
            {
              "code": "39",
              "name": "Taita/Taveta"
            },
            {
              "code": "40",
              "name": "Tana River"
            },
            {
              "code": "41",
              "name": "Tharaka-Nithi"
            },
            {
              "code": "42",
              "name": "Trans Nzoia"
            },
            {
              "code": "43",
              "name": "Turkana"
            },
            {
              "code": "44",
              "name": "Uasin Gishu"
            },
            {
              "code": "45",
              "name": "Vihiga"
            },
            {
              "code": "46",
              "name": "Wajir"
            },
            {
              "code": "47",
              "name": "West Pokot"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KG",
          "name": "Kyrgyzstan",
          "native": "Кыргызстан",
          "flag": "🇰🇬",
          "phonePrefix": "+996",
          "regions": [
            {
              "code": "B",
              "name": "Batken Oblasty"
            },
            {
              "code": "GB",
              "name": "Bishkek Shaary"
            },
            {
              "code": "C",
              "name": "Chuy Oblasty (Bishkek)"
            },
            {
              "code": "J",
              "name": "Jalal-Abad Oblasty"
            },
            {
              "code": "N",
              "name": "Naryn Oblasty"
            },
            {
              "code": "O",
              "name": "Osh Oblasty"
            },
            {
              "code": "T",
              "name": "Talas Oblasty"
            },
            {
              "code": "Y",
              "name": "Ysyk-Kol Oblasty (Karakol)"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KH",
          "name": "Cambodia",
          "native": "Kâmpŭchéa",
          "flag": "🇰🇭",
          "phonePrefix": "+855",
          "regions": [
            {
              "code": "2",
              "name": "Baat Dambang"
            },
            {
              "code": "1",
              "name": "Banteay Mean Chey"
            },
            {
              "code": "3",
              "name": "Kampong Chaam"
            },
            {
              "code": "4",
              "name": "Kampong Chhnang"
            },
            {
              "code": "5",
              "name": "Kampong Spueu"
            },
            {
              "code": "6",
              "name": "Kampong Thum"
            },
            {
              "code": "7",
              "name": "Kampot"
            },
            {
              "code": "8",
              "name": "Kandaal"
            },
            {
              "code": "9",
              "name": "Kaoh Kong"
            },
            {
              "code": "10",
              "name": "Kracheh"
            },
            {
              "code": "23",
              "name": "Krong Kaeb"
            },
            {
              "code": "24",
              "name": "Krong Pailin"
            },
            {
              "code": "18",
              "name": "Krong Preah Sihanouk"
            },
            {
              "code": "11",
              "name": "Mondol Kiri"
            },
            {
              "code": "22",
              "name": "Otdar Mean Chey"
            },
            {
              "code": "12",
              "name": "Phnom Penh"
            },
            {
              "code": "15",
              "name": "Pousaat"
            },
            {
              "code": "13",
              "name": "Preah Vihear"
            },
            {
              "code": "14",
              "name": "Prey Veaeng"
            },
            {
              "code": "16",
              "name": "Rotanah Kiri"
            },
            {
              "code": "17",
              "name": "Siem Reab"
            },
            {
              "code": "19",
              "name": "Stueng Treng"
            },
            {
              "code": "20",
              "name": "Svaay Rieng"
            },
            {
              "code": "21",
              "name": "Taakaev"
            },
            {
              "code": "25",
              "name": "Tbong Khmum"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KI",
          "name": "Kiribati",
          "native": "Kiribati",
          "flag": "🇰🇮",
          "phonePrefix": "+686",
          "regions": [
            {
              "code": null,
              "name": "Abaiang"
            },
            {
              "code": null,
              "name": "Abemama"
            },
            {
              "code": null,
              "name": "Aranuka"
            },
            {
              "code": null,
              "name": "Arorae"
            },
            {
              "code": null,
              "name": "Banaba"
            },
            {
              "code": null,
              "name": "Beru"
            },
            {
              "code": null,
              "name": "Butaritari"
            },
            {
              "code": null,
              "name": "Central Gilberts"
            },
            {
              "code": "G",
              "name": "Gilbert Islands"
            },
            {
              "code": null,
              "name": "Kanton"
            },
            {
              "code": null,
              "name": "Kiritimati"
            },
            {
              "code": null,
              "name": "Kuria"
            },
            {
              "code": "L",
              "name": "Line Islands"
            },
            {
              "code": null,
              "name": "Maiana"
            },
            {
              "code": null,
              "name": "Makin"
            },
            {
              "code": null,
              "name": "Marakei"
            },
            {
              "code": null,
              "name": "Nikunau"
            },
            {
              "code": null,
              "name": "Nonouti"
            },
            {
              "code": null,
              "name": "Northern Gilberts"
            },
            {
              "code": null,
              "name": "Onotoa"
            },
            {
              "code": "P",
              "name": "Phoenix Islands"
            },
            {
              "code": null,
              "name": "Southern Gilberts"
            },
            {
              "code": null,
              "name": "Tabiteuea"
            },
            {
              "code": null,
              "name": "Tabuaeran"
            },
            {
              "code": null,
              "name": "Tamana"
            },
            {
              "code": null,
              "name": "Tarawa"
            },
            {
              "code": null,
              "name": "Teraina"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KM",
          "name": "Comoros",
          "native": "Komori",
          "flag": "🇰🇲",
          "phonePrefix": "+269",
          "regions": [
            {
              "code": "G",
              "name": "Andjazîdja"
            },
            {
              "code": "A",
              "name": "Andjouân"
            },
            {
              "code": "M",
              "name": "Moûhîlî"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KN",
          "name": "Saint Kitts and Nevis",
          "native": "Saint Kitts and Nevis",
          "flag": "🇰🇳",
          "phonePrefix": "+1869",
          "regions": [
            {
              "code": "K",
              "name": "Saint Kitts"
            },
            {
              "code": "N",
              "name": "Nevis"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KP",
          "name": "North Korea",
          "native": "북한",
          "flag": "🇰🇵",
          "phonePrefix": "+850",
          "regions": [
            {
              "code": "04",
              "name": "Chagang-do (Chagang Province)"
            },
            {
              "code": "09",
              "name": "Hamgyong-bukto (North Hamgyong Province)"
            },
            {
              "code": "08",
              "name": "Hamgyong-namdo (South Hamgyong Province)"
            },
            {
              "code": "06",
              "name": "Hwanghae-bukto (North Hwanghae Province)"
            },
            {
              "code": "05",
              "name": "Hwanghae-namdo (South Hwanghae Province)"
            },
            {
              "code": "07",
              "name": "Kangwon-do (Kangwon Province)"
            },
            {
              "code": "13",
              "name": "Nasŏn (Najin-Sŏnbong)"
            },
            {
              "code": "03",
              "name": "P'yongan-bukto (North P'yongan Province)"
            },
            {
              "code": "02",
              "name": "P'yongan-namdo (South P'yongan Province)"
            },
            {
              "code": "01",
              "name": "P'yongyang-si (P'yongyang City)"
            },
            {
              "code": "10",
              "name": "Yanggang-do (Yanggang Province)"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KR",
          "name": "South Korea",
          "native": "대한민국",
          "flag": "🇰🇷",
          "phonePrefix": "+82",
          "regions": [
            {
              "code": "43",
              "name": "Chungcheongbuk-do"
            },
            {
              "code": "44",
              "name": "Chungcheongnam-do"
            },
            {
              "code": "49",
              "name": "Jeju-teukbyeoljachido"
            },
            {
              "code": "45",
              "name": "Jeollabuk-do"
            },
            {
              "code": "46",
              "name": "Jeollanam-do"
            },
            {
              "code": "28",
              "name": "Incheon-gwangyeoksi"
            },
            {
              "code": "42",
              "name": "Gangwon-do"
            },
            {
              "code": "29",
              "name": "Gwangju-gwangyeoksi"
            },
            {
              "code": "41",
              "name": "Gyeonggi-do"
            },
            {
              "code": "47",
              "name": "Gyeongsangbuk-do"
            },
            {
              "code": "48",
              "name": "Gyeongsangnam-do"
            },
            {
              "code": "26",
              "name": "Busan-gwangyeoksi"
            },
            {
              "code": "11",
              "name": "Seoul-teukbyeolsi"
            },
            {
              "code": "50",
              "name": "Sejong-teukbyeoljachisi"
            },
            {
              "code": "27",
              "name": "Daegu-gwangyeoksi"
            },
            {
              "code": "30",
              "name": "Daejeon-gwangyeoksi"
            },
            {
              "code": "31",
              "name": "Ulsan-gwangyeoksi"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KW",
          "name": "Kuwait",
          "native": "الكويت",
          "flag": "🇰🇼",
          "phonePrefix": "+965",
          "regions": [
            {
              "code": "AH",
              "name": "Al Aḩmadi"
            },
            {
              "code": "FA",
              "name": "Al Farwānīyah"
            },
            {
              "code": "JA",
              "name": "Al Jahrā’"
            },
            {
              "code": "KU",
              "name": "Al ‘Āşimah"
            },
            {
              "code": "HA",
              "name": "Ḩawallī"
            },
            {
              "code": "MU",
              "name": "Mubārak al Kabir"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KY",
          "name": "Cayman Islands",
          "native": "Cayman Islands",
          "flag": "🇰🇾",
          "phonePrefix": "+1345",
          "regions": [
            {
              "code": null,
              "name": "Creek"
            },
            {
              "code": null,
              "name": "Eastern"
            },
            {
              "code": null,
              "name": "Midland"
            },
            {
              "code": null,
              "name": "South Town"
            },
            {
              "code": null,
              "name": "Spot Bay"
            },
            {
              "code": null,
              "name": "Stake Bay"
            },
            {
              "code": null,
              "name": "West End"
            },
            {
              "code": null,
              "name": "Western"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "KZ",
          "name": "Kazakhstan",
          "native": "Қазақстан",
          "flag": "🇰🇿",
          "phonePrefix": "+76,77",
          "regions": [
            {
              "code": "ALA",
              "name": "Almaty"
            },
            {
              "code": "AKM",
              "name": "Aqmola"
            },
            {
              "code": "AKT",
              "name": "Aqtobe"
            },
            {
              "code": "AST",
              "name": "Astana"
            },
            {
              "code": "ATY",
              "name": "Atyrau"
            },
            {
              "code": "ZAP",
              "name": "Batys Qazaqstan"
            },
            {
              "code": null,
              "name": "Bayqongyr"
            },
            {
              "code": "MAN",
              "name": "Mangghystau"
            },
            {
              "code": "YUZ",
              "name": "Ongtustik Qazaqstan"
            },
            {
              "code": "PAV",
              "name": "Pavlodar"
            },
            {
              "code": "KAR",
              "name": "Qaraghandy"
            },
            {
              "code": "KUS",
              "name": "Qostanay"
            },
            {
              "code": "KZY",
              "name": "Qyzylorda"
            },
            {
              "code": "VOS",
              "name": "Shyghys Qazaqstan"
            },
            {
              "code": "SEV",
              "name": "Soltustik Qazaqstan"
            },
            {
              "code": "ZHA",
              "name": "Zhambyl"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LA",
          "name": "Lao People's Democratic Republic",
          "native": "ສປປລາວ",
          "flag": "🇱🇦",
          "phonePrefix": "+856",
          "regions": [
            {
              "code": "AT",
              "name": "Attapu"
            },
            {
              "code": "BK",
              "name": "Bokèo"
            },
            {
              "code": "BL",
              "name": "Bolikhamxai"
            },
            {
              "code": "CH",
              "name": "Champasak"
            },
            {
              "code": "HO",
              "name": "Houaphan"
            },
            {
              "code": "KH",
              "name": "Khammouan"
            },
            {
              "code": "LM",
              "name": "Louang Namtha"
            },
            {
              "code": "LP",
              "name": "Louangphabang"
            },
            {
              "code": "OU",
              "name": "Oudômxai"
            },
            {
              "code": "PH",
              "name": "Phôngsali"
            },
            {
              "code": "SL",
              "name": "Salavan"
            },
            {
              "code": "SV",
              "name": "Savannakhét"
            },
            {
              "code": "VI",
              "name": "Vientiane"
            },
            {
              "code": "XA",
              "name": "Xaignabouli"
            },
            {
              "code": "XE",
              "name": "Xékong"
            },
            {
              "code": "XS",
              "name": "Xaisomboun"
            },
            {
              "code": "XI",
              "name": "Xiangkhouang"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LB",
          "name": "Lebanon",
          "native": "لبنان",
          "flag": "🇱🇧",
          "phonePrefix": "+961",
          "regions": [
            {
              "code": "AK",
              "name": "Aakkâr"
            },
            {
              "code": "BH",
              "name": "Baalbelk-Hermel"
            },
            {
              "code": "BI",
              "name": "Béqaa"
            },
            {
              "code": "BA",
              "name": "Beyrouth"
            },
            {
              "code": "AS",
              "name": "Liban-Nord"
            },
            {
              "code": "JA",
              "name": "Liban-Sud"
            },
            {
              "code": "JL",
              "name": "Mont-Liban"
            },
            {
              "code": "NA",
              "name": "Nabatîyé"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LC",
          "name": "Saint Lucia",
          "native": "Saint Lucia",
          "flag": "🇱🇨",
          "phonePrefix": "+1758",
          "regions": [
            {
              "code": "01",
              "name": "Anse-la-Raye"
            },
            {
              "code": "12",
              "name": "Canaries"
            },
            {
              "code": "02",
              "name": "Castries"
            },
            {
              "code": "03",
              "name": "Choiseul"
            },
            {
              "code": "05",
              "name": "Dennery"
            },
            {
              "code": "06",
              "name": "Gros Islet"
            },
            {
              "code": "07",
              "name": "Laborie"
            },
            {
              "code": "08",
              "name": "Micoud"
            },
            {
              "code": "10",
              "name": "Soufriere"
            },
            {
              "code": "11",
              "name": "Vieux Fort"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LI",
          "name": "Liechtenstein",
          "native": "Liechtenstein",
          "flag": "🇱🇮",
          "phonePrefix": "+423",
          "regions": [
            {
              "code": "01",
              "name": "Balzers"
            },
            {
              "code": "02",
              "name": "Eschen"
            },
            {
              "code": "03",
              "name": "Gamprin"
            },
            {
              "code": "04",
              "name": "Mauren"
            },
            {
              "code": "05",
              "name": "Planken"
            },
            {
              "code": "06",
              "name": "Ruggell"
            },
            {
              "code": "07",
              "name": "Schaan"
            },
            {
              "code": "08",
              "name": "Schellenberg"
            },
            {
              "code": "09",
              "name": "Triesen"
            },
            {
              "code": "10",
              "name": "Triesenberg"
            },
            {
              "code": "11",
              "name": "Vaduz"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LK",
          "name": "Sri Lanka",
          "native": "śrī laṃkāva",
          "flag": "🇱🇰",
          "phonePrefix": "+94",
          "regions": [
            {
              "code": "1",
              "name": "Basnahira"
            },
            {
              "code": "3",
              "name": "Dakunu"
            },
            {
              "code": "2",
              "name": "Madhyama"
            },
            {
              "code": "5",
              "name": "Naegenahira"
            },
            {
              "code": "9",
              "name": "Sabaragamuwa"
            },
            {
              "code": "4",
              "name": "Uturu"
            },
            {
              "code": "7",
              "name": "Uturumaeda"
            },
            {
              "code": "6",
              "name": "Vayamba"
            },
            {
              "code": "8",
              "name": "Uva"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LR",
          "name": "Liberia",
          "native": "Liberia",
          "flag": "🇱🇷",
          "phonePrefix": "+231",
          "regions": [
            {
              "code": "BM",
              "name": "Bomi"
            },
            {
              "code": "BG",
              "name": "Bong"
            },
            {
              "code": "GP",
              "name": "Gbarpolu"
            },
            {
              "code": "GB",
              "name": "Grand Bassa"
            },
            {
              "code": "CM",
              "name": "Grand Cape Mount"
            },
            {
              "code": "GG",
              "name": "Grand Gedeh"
            },
            {
              "code": "GK",
              "name": "Grand Kru"
            },
            {
              "code": "LO",
              "name": "Lofa"
            },
            {
              "code": "MG",
              "name": "Margibi"
            },
            {
              "code": "MY",
              "name": "Maryland"
            },
            {
              "code": "MO",
              "name": "Montserrado"
            },
            {
              "code": "NI",
              "name": "Nimba"
            },
            {
              "code": "RI",
              "name": "River Cess"
            },
            {
              "code": "RG",
              "name": "River Geee"
            },
            {
              "code": "SI",
              "name": "Sinoe"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LS",
          "name": "Lesotho",
          "native": "Lesotho",
          "flag": "🇱🇸",
          "phonePrefix": "+266",
          "regions": [
            {
              "code": "D",
              "name": "Berea"
            },
            {
              "code": "B",
              "name": "Butha-Buthe"
            },
            {
              "code": "C",
              "name": "Leribe"
            },
            {
              "code": "E",
              "name": "Mafeteng"
            },
            {
              "code": "A",
              "name": "Maseru"
            },
            {
              "code": "F",
              "name": "Mohales Hoek"
            },
            {
              "code": "J",
              "name": "Mokhotlong"
            },
            {
              "code": "H",
              "name": "Qacha's Nek"
            },
            {
              "code": "G",
              "name": "Quthing"
            },
            {
              "code": "K",
              "name": "Thaba-Tseka"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "LT",
          "name": "Lithuania",
          "native": "Lietuva",
          "flag": "🇱🇹",
          "phonePrefix": "+370",
          "regions": [
            {
              "code": "AL",
              "name": "Alytaus"
            },
            {
              "code": "KU",
              "name": "Kauno"
            },
            {
              "code": "KL",
              "name": "Klaipėdos"
            },
            {
              "code": "MR",
              "name": "Marijampolės"
            },
            {
              "code": "PN",
              "name": "Panevėžio"
            },
            {
              "code": "SA",
              "name": "Šiaulių"
            },
            {
              "code": "TA",
              "name": "Tauragės"
            },
            {
              "code": "TE",
              "name": "Telšių"
            },
            {
              "code": "UT",
              "name": "Utenos"
            },
            {
              "code": "VL",
              "name": "Vilniaus"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "LU",
          "name": "Luxembourg",
          "native": "Luxembourg",
          "flag": "🇱🇺",
          "phonePrefix": "+352",
          "regions": [
            {
              "code": "CA",
              "name": "Capellen"
            },
            {
              "code": "CL",
              "name": "Clevaux"
            },
            {
              "code": "DI",
              "name": "Diekirch"
            },
            {
              "code": "EC",
              "name": "Echternach"
            },
            {
              "code": "ES",
              "name": "Esch-sur-Alzette"
            },
            {
              "code": "GR",
              "name": "Grevenmacher"
            },
            {
              "code": "LU",
              "name": "Luxembourg"
            },
            {
              "code": "ME",
              "name": "Mersch"
            },
            {
              "code": "RD",
              "name": "Redange"
            },
            {
              "code": "RM",
              "name": "Remich"
            },
            {
              "code": "VD",
              "name": "Vianden"
            },
            {
              "code": "WI",
              "name": "Wiltz"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "LV",
          "name": "Latvia",
          "native": "Latvija",
          "flag": "🇱🇻",
          "phonePrefix": "+371",
          "regions": [
            {
              "code": "001",
              "name": "Aglona"
            },
            {
              "code": "002",
              "name": "Aizkraukle"
            },
            {
              "code": "003",
              "name": "Aizpute"
            },
            {
              "code": "004",
              "name": "Aknīste"
            },
            {
              "code": "005",
              "name": "Aloja"
            },
            {
              "code": "06",
              "name": "Alsunga"
            },
            {
              "code": "007",
              "name": "Alūksne"
            },
            {
              "code": "008",
              "name": "Amata"
            },
            {
              "code": "009",
              "name": "Ape"
            },
            {
              "code": "010",
              "name": "Auce"
            },
            {
              "code": "011",
              "name": "Ādaži"
            },
            {
              "code": "012",
              "name": "Babīte"
            },
            {
              "code": "013",
              "name": "Baldone"
            },
            {
              "code": "014",
              "name": "Baltinava"
            },
            {
              "code": "015",
              "name": "Balvi"
            },
            {
              "code": "016",
              "name": "Bauska"
            },
            {
              "code": "017",
              "name": "Beverīna"
            },
            {
              "code": "018",
              "name": "Brocēni"
            },
            {
              "code": "019",
              "name": "Burtnieki"
            },
            {
              "code": "020",
              "name": "Carnikava"
            },
            {
              "code": "021",
              "name": "Cesvaine"
            },
            {
              "code": "022",
              "name": "Cēsis"
            },
            {
              "code": "023",
              "name": "Cibla"
            },
            {
              "code": "024",
              "name": "Dagda"
            },
            {
              "code": "025",
              "name": "Daugavpils"
            },
            {
              "code": "DGV",
              "name": "Daugavpils (City)"
            },
            {
              "code": "026",
              "name": "Dobele"
            },
            {
              "code": "027",
              "name": "Dundaga"
            },
            {
              "code": "028",
              "name": "Durbe"
            },
            {
              "code": "029",
              "name": "Engure"
            },
            {
              "code": "030",
              "name": "Ērgļi"
            },
            {
              "code": "031",
              "name": "Garkalne"
            },
            {
              "code": "032",
              "name": "Grobiņa"
            },
            {
              "code": "033",
              "name": "Gulbene"
            },
            {
              "code": "034",
              "name": "Iecava"
            },
            {
              "code": "035",
              "name": "Ikšķile"
            },
            {
              "code": "036",
              "name": "Ilūkste"
            },
            {
              "code": "037",
              "name": "Inčukalns"
            },
            {
              "code": "038",
              "name": "Jaunjelgava"
            },
            {
              "code": "039",
              "name": "Jaunpiebalga"
            },
            {
              "code": "040",
              "name": "Jaunpils"
            },
            {
              "code": "041",
              "name": "Jelgava"
            },
            {
              "code": "JEL",
              "name": "Jelgava (City)"
            },
            {
              "code": "042",
              "name": "Jēkabpils"
            },
            {
              "code": "JKB",
              "name": "Jēkabpils (City)"
            },
            {
              "code": "JUR",
              "name": "Jūrmala (City)"
            },
            {
              "code": "043",
              "name": "Kandava"
            },
            {
              "code": "044",
              "name": "Kārsava"
            },
            {
              "code": "045",
              "name": "Kocēni"
            },
            {
              "code": "046",
              "name": "Koknese"
            },
            {
              "code": "047",
              "name": "Krāslava"
            },
            {
              "code": "048",
              "name": "Krimulda"
            },
            {
              "code": "049",
              "name": "Krustpils"
            },
            {
              "code": "050",
              "name": "Kuldīga"
            },
            {
              "code": "051",
              "name": "Ķegums"
            },
            {
              "code": "052",
              "name": "Ķekava"
            },
            {
              "code": "053",
              "name": "Lielvārde"
            },
            {
              "code": "LPX",
              "name": "Liepāja"
            },
            {
              "code": "054",
              "name": "Limbaži"
            },
            {
              "code": "055",
              "name": "Līgatne"
            },
            {
              "code": "056",
              "name": "Līvāni"
            },
            {
              "code": "057",
              "name": "Lubāna"
            },
            {
              "code": "058",
              "name": "Ludza"
            },
            {
              "code": "059",
              "name": "Madona"
            },
            {
              "code": "060",
              "name": "Mazsalaca"
            },
            {
              "code": "061",
              "name": "Mālpils"
            },
            {
              "code": "062",
              "name": "Mārupe"
            },
            {
              "code": "063",
              "name": "Mērsrags"
            },
            {
              "code": "064",
              "name": "Naukšēni"
            },
            {
              "code": "065",
              "name": "Nereta"
            },
            {
              "code": "066",
              "name": "Nīca"
            },
            {
              "code": "067",
              "name": "Ogre"
            },
            {
              "code": "068",
              "name": "Olaine"
            },
            {
              "code": "069",
              "name": "Ozolnieki"
            },
            {
              "code": "070",
              "name": "Pārgauja"
            },
            {
              "code": "071",
              "name": "Pāvilosta"
            },
            {
              "code": "072",
              "name": "Pļaviņas"
            },
            {
              "code": "073",
              "name": "Preiļi"
            },
            {
              "code": "074",
              "name": "Priekule"
            },
            {
              "code": "075",
              "name": "Priekuļi"
            },
            {
              "code": "076",
              "name": "Rauna"
            },
            {
              "code": "077",
              "name": "Rēzekne"
            },
            {
              "code": "REZ",
              "name": "Rēzekne (City)"
            },
            {
              "code": "078",
              "name": "Riebiņi"
            },
            {
              "code": "RIX",
              "name": "Rīga"
            },
            {
              "code": "079",
              "name": "Roja"
            },
            {
              "code": "080",
              "name": "Ropaži"
            },
            {
              "code": "081",
              "name": "Rucava"
            },
            {
              "code": "082",
              "name": "Rugāji"
            },
            {
              "code": "083",
              "name": "Rundāle"
            },
            {
              "code": "084",
              "name": "Rūjiena"
            },
            {
              "code": "085",
              "name": "Sala"
            },
            {
              "code": "086",
              "name": "Salacgrīva"
            },
            {
              "code": "087",
              "name": "Salaspils"
            },
            {
              "code": "088",
              "name": "Saldus"
            },
            {
              "code": "089",
              "name": "Saulkrasti"
            },
            {
              "code": "090",
              "name": "Sēja"
            },
            {
              "code": "091",
              "name": "Sigulda"
            },
            {
              "code": "092",
              "name": "Skrīveri"
            },
            {
              "code": "093",
              "name": "Skrunda"
            },
            {
              "code": "094",
              "name": "Smiltene"
            },
            {
              "code": "095",
              "name": "Stopiņi"
            },
            {
              "code": "096",
              "name": "Strenči"
            },
            {
              "code": "097",
              "name": "Talsi"
            },
            {
              "code": "098",
              "name": "Tērvete"
            },
            {
              "code": "099",
              "name": "Tukums"
            },
            {
              "code": "100",
              "name": "Vaiņode"
            },
            {
              "code": "101",
              "name": "Valka"
            },
            {
              "code": "VMR",
              "name": "Valmiera"
            },
            {
              "code": "102",
              "name": "Varakļāni"
            },
            {
              "code": "103",
              "name": "Vārkava"
            },
            {
              "code": "104",
              "name": "Vecpiebalga"
            },
            {
              "code": "105",
              "name": "Vecumnieki"
            },
            {
              "code": "106",
              "name": "Ventspils"
            },
            {
              "code": "VEN",
              "name": "Ventspils (City)"
            },
            {
              "code": "107",
              "name": "Viesīte"
            },
            {
              "code": "108",
              "name": "Viļaka"
            },
            {
              "code": "109",
              "name": "Viļāni"
            },
            {
              "code": "110",
              "name": "Zilupe"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "LY",
          "name": "Libya",
          "native": "‏ليبيا",
          "flag": "🇱🇾",
          "phonePrefix": "+218",
          "regions": [
            {
              "code": "BU",
              "name": "Al Buţnān"
            },
            {
              "code": "JA",
              "name": "Al Jabal al Akhḑar"
            },
            {
              "code": "JG",
              "name": "Al Jabal al Gharbī"
            },
            {
              "code": "JI",
              "name": "Al Jafārah"
            },
            {
              "code": "JU",
              "name": "Al Jufrah"
            },
            {
              "code": "FK",
              "name": "Al Kufrah"
            },
            {
              "code": "MJ",
              "name": "Al Marj"
            },
            {
              "code": "MB",
              "name": "Al Marquab"
            },
            {
              "code": "WA",
              "name": "Al Wāḩāt"
            },
            {
              "code": "NQ",
              "name": "An Nuqaţ al Khams"
            },
            {
              "code": "ZA",
              "name": "Az Zāwiyah"
            },
            {
              "code": "BA",
              "name": "Banghāzī"
            },
            {
              "code": "DR",
              "name": "Darnah"
            },
            {
              "code": "GH",
              "name": "Ghāt"
            },
            {
              "code": "MI",
              "name": "Mişrātah"
            },
            {
              "code": "MQ",
              "name": "Murzuq"
            },
            {
              "code": "NL",
              "name": "Nālūt"
            },
            {
              "code": "SB",
              "name": "Sabhā"
            },
            {
              "code": "SR",
              "name": "Surt"
            },
            {
              "code": "TB",
              "name": "Ţarābulus"
            },
            {
              "code": "WD",
              "name": "Yafran"
            },
            {
              "code": "WS",
              "name": "Wādī ash Shāţiʾ"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MA",
          "name": "Morocco",
          "native": "المغرب",
          "flag": "🇲🇦",
          "phonePrefix": "+212",
          "regions": [
            {
              "code": "MA-01",
              "name": "Tanger-Tétouan-Al Hoceïma"
            },
            {
              "code": "MA-02",
              "name": "L'Oriental"
            },
            {
              "code": "MA-03",
              "name": "Fès-Meknès"
            },
            {
              "code": "MA-04",
              "name": "Rabat-Salé-Kénitra"
            },
            {
              "code": "MA-05",
              "name": "Béni Mellal-Khénifra"
            },
            {
              "code": "MA-06",
              "name": "Casablanca-Settat"
            },
            {
              "code": "MA-07",
              "name": "Marrakech-Safi"
            },
            {
              "code": "MA-08",
              "name": "Drâa-Tafilalet"
            },
            {
              "code": "MA-09",
              "name": "Souss-Massa"
            },
            {
              "code": "MA-10",
              "name": "Guelmim-Oued Noun"
            },
            {
              "code": "MA-11",
              "name": "Laâyoune-Sakia El Hamra"
            },
            {
              "code": "MA-12",
              "name": "Dakhla-Oued Ed-Dahab"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MC",
          "name": "Monaco",
          "native": "Monaco",
          "flag": "🇲🇨",
          "phonePrefix": "+377",
          "regions": [
            {
              "code": "CL",
              "name": "Colle"
            },
            {
              "code": "CO",
              "name": "Condamine"
            },
            {
              "code": "FO",
              "name": "Fontvieille"
            },
            {
              "code": "GA",
              "name": "Gare"
            },
            {
              "code": "JE",
              "name": "Jardin Exotique"
            },
            {
              "code": "LA",
              "name": "Larvotto"
            },
            {
              "code": "MA",
              "name": "Malbousquet"
            },
            {
              "code": "MO",
              "name": "Monaco-Ville"
            },
            {
              "code": "MG",
              "name": "Moneghetti"
            },
            {
              "code": "MC",
              "name": "Monte-Carlo"
            },
            {
              "code": "MU",
              "name": "Moulins"
            },
            {
              "code": "PH",
              "name": "Port-Hercule"
            },
            {
              "code": "SR",
              "name": "Saint-Roman"
            },
            {
              "code": "SD",
              "name": "Sainte-Dévote"
            },
            {
              "code": "SO",
              "name": "Source"
            },
            {
              "code": "SP",
              "name": "Spélugues"
            },
            {
              "code": "VR",
              "name": "Vallon de la Rousse"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "MD",
          "name": "Moldova, Republic of",
          "native": "Moldova",
          "flag": "🇲🇩",
          "phonePrefix": "+373",
          "regions": [
            {
              "code": "AN",
              "name": "Aenii Noi"
            },
            {
              "code": "BS",
              "name": "Basarabeasca"
            },
            {
              "code": "BA",
              "name": "Bălți"
            },
            {
              "code": "BD",
              "name": "Bender"
            },
            {
              "code": "BR",
              "name": "Briceni"
            },
            {
              "code": "CA",
              "name": "Cahul"
            },
            {
              "code": "CT",
              "name": "Cantemir"
            },
            {
              "code": "CL",
              "name": "Călărași"
            },
            {
              "code": "CS",
              "name": "Căușeni"
            },
            {
              "code": "CU",
              "name": "Chișinău"
            },
            {
              "code": "CM",
              "name": "Cimișlia"
            },
            {
              "code": "CR",
              "name": "Criuleni"
            },
            {
              "code": "DO",
              "name": "Dondușeni"
            },
            {
              "code": "DR",
              "name": "Drochia"
            },
            {
              "code": "DU",
              "name": "Dubăsari"
            },
            {
              "code": "ED",
              "name": "Edineț"
            },
            {
              "code": "FA",
              "name": "Fălești"
            },
            {
              "code": "FL",
              "name": "Florești"
            },
            {
              "code": "GA",
              "name": "Găgăuzia"
            },
            {
              "code": "GL",
              "name": "Glodeni"
            },
            {
              "code": "HI",
              "name": "Hîncești"
            },
            {
              "code": "IA",
              "name": "Ialoveni"
            },
            {
              "code": "LE",
              "name": "Leova"
            },
            {
              "code": "NI",
              "name": "Nisporeni"
            },
            {
              "code": "OC",
              "name": "Ocnița"
            },
            {
              "code": "OR",
              "name": "Orhei"
            },
            {
              "code": "RE",
              "name": "Rezina"
            },
            {
              "code": "RI",
              "name": "Rîșcani"
            },
            {
              "code": "SI",
              "name": "Sîngerei"
            },
            {
              "code": "SO",
              "name": "Soroca"
            },
            {
              "code": "SN",
              "name": "Stânga Nistrului"
            },
            {
              "code": "ST",
              "name": "Strășeni"
            },
            {
              "code": "SD",
              "name": "Șoldănești"
            },
            {
              "code": "SV",
              "name": "Ștefan Vodă"
            },
            {
              "code": "TA",
              "name": "Taraclia"
            },
            {
              "code": "TE",
              "name": "Telenești"
            },
            {
              "code": "UN",
              "name": "Ungheni"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ME",
          "name": "Montenegro",
          "native": "Црна Гора",
          "flag": "🇲🇪",
          "phonePrefix": "+382",
          "regions": [
            {
              "code": "01",
              "name": "Andrijevica"
            },
            {
              "code": "02",
              "name": "Bar"
            },
            {
              "code": "03",
              "name": "Berane"
            },
            {
              "code": "04",
              "name": "Bijelo Polje"
            },
            {
              "code": "05",
              "name": "Budva"
            },
            {
              "code": "06",
              "name": "Cetinje"
            },
            {
              "code": "07",
              "name": "Danilovgrad"
            },
            {
              "code": "22",
              "name": "Gusinje"
            },
            {
              "code": "08",
              "name": "Herceg Novi"
            },
            {
              "code": "09",
              "name": "Kolašin"
            },
            {
              "code": "10",
              "name": "Kotor"
            },
            {
              "code": "11",
              "name": "Mojkovac"
            },
            {
              "code": "12",
              "name": "Nikšić"
            },
            {
              "code": "23",
              "name": "Petnica"
            },
            {
              "code": "13",
              "name": "Plav"
            },
            {
              "code": "14",
              "name": "Plužine"
            },
            {
              "code": "15",
              "name": "Pljevlja"
            },
            {
              "code": "16",
              "name": "Podgorica"
            },
            {
              "code": "17",
              "name": "Rožaje"
            },
            {
              "code": "18",
              "name": "Šavnik"
            },
            {
              "code": "19",
              "name": "Tivat"
            },
            {
              "code": "20",
              "name": "Ulcinj"
            },
            {
              "code": "21",
              "name": "Žabljak"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "MF",
          "name": "Saint Martin (French part)",
          "native": "Saint-Martin",
          "flag": "🇲🇫",
          "phonePrefix": "+590",
          "regions": [
            {
              "code": null,
              "name": "Saint Martin"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "MG",
          "name": "Madagascar",
          "native": "Madagasikara",
          "flag": "🇲🇬",
          "phonePrefix": "+261",
          "regions": [
            {
              "code": "T",
              "name": "Antananarivo"
            },
            {
              "code": "D",
              "name": "Antsiranana"
            },
            {
              "code": "F",
              "name": "Fianarantsoa"
            },
            {
              "code": "M",
              "name": "Mahajanga"
            },
            {
              "code": "A",
              "name": "Toamasina"
            },
            {
              "code": "U",
              "name": "Toliara"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MH",
          "name": "Marshall Islands",
          "native": "M̧ajeļ",
          "flag": "🇲🇭",
          "phonePrefix": "+692",
          "regions": [
            {
              "code": "ALL",
              "name": "Ailinglaplap"
            },
            {
              "code": "ALK",
              "name": "Ailuk"
            },
            {
              "code": "ARN",
              "name": "Arno"
            },
            {
              "code": "AUR",
              "name": "Aur"
            },
            {
              "code": "KIL",
              "name": "Bikini and Kili"
            },
            {
              "code": "EBO",
              "name": "Ebon"
            },
            {
              "code": "JAB",
              "name": "Jabat"
            },
            {
              "code": "JAL",
              "name": "Jaluit"
            },
            {
              "code": "KWA",
              "name": "Kwajalein"
            },
            {
              "code": "LAE",
              "name": "Lae"
            },
            {
              "code": "LIB",
              "name": "Lib"
            },
            {
              "code": "LIK",
              "name": "Likiep"
            },
            {
              "code": "MAJ",
              "name": "Majuro"
            },
            {
              "code": "MAL",
              "name": "Maloelap"
            },
            {
              "code": "MEJ",
              "name": "Mejit"
            },
            {
              "code": "NMK",
              "name": "Namdrik"
            },
            {
              "code": "NMU",
              "name": "Namu"
            },
            {
              "code": "RON",
              "name": "Rongelap"
            },
            {
              "code": "UJA",
              "name": "Ujae"
            },
            {
              "code": "UTI",
              "name": "Utrik"
            },
            {
              "code": "WTH",
              "name": "Wotho"
            },
            {
              "code": "WTJ",
              "name": "Wotje"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "MK",
          "name": "The Republic of North Macedonia",
          "native": "Северна Македонија",
          "flag": "🇲🇰",
          "phonePrefix": "+389",
          "regions": [
            {
              "code": "02",
              "name": "Aračinovo"
            },
            {
              "code": "03",
              "name": "Berovo"
            },
            {
              "code": "04",
              "name": "Bitola"
            },
            {
              "code": "05",
              "name": "Bogdanci"
            },
            {
              "code": "06",
              "name": "Bogovinje"
            },
            {
              "code": "07",
              "name": "Bosilovo"
            },
            {
              "code": "08",
              "name": "Brvenica"
            },
            {
              "code": "78",
              "name": "Centar Župa"
            },
            {
              "code": "80",
              "name": "Čaška"
            },
            {
              "code": "81",
              "name": "Češinovo-Obleševo"
            },
            {
              "code": "82",
              "name": "Čučer Sandevo"
            },
            {
              "code": "21",
              "name": "Debar"
            },
            {
              "code": "22",
              "name": "Debarca"
            },
            {
              "code": "23",
              "name": "Delčevo"
            },
            {
              "code": "25",
              "name": "Demir Hisar"
            },
            {
              "code": "24",
              "name": "Demir Kapija"
            },
            {
              "code": "26",
              "name": "Doran"
            },
            {
              "code": "27",
              "name": "Dolneni"
            },
            {
              "code": "18",
              "name": "Gevgelija"
            },
            {
              "code": "19",
              "name": "Gostivar"
            },
            {
              "code": "20",
              "name": "Gradsko"
            },
            {
              "code": "34",
              "name": "Ilinden"
            },
            {
              "code": "35",
              "name": "Jegunovce"
            },
            {
              "code": "37",
              "name": "Karbinci"
            },
            {
              "code": "36",
              "name": "Kavadarci"
            },
            {
              "code": "40",
              "name": "Kičevo"
            },
            {
              "code": "42",
              "name": "Kočani"
            },
            {
              "code": "41",
              "name": "Konče"
            },
            {
              "code": "43",
              "name": "Kratovo"
            },
            {
              "code": "44",
              "name": "Kriva Palanka"
            },
            {
              "code": "45",
              "name": "Krivogaštani"
            },
            {
              "code": "46",
              "name": "Kruševo"
            },
            {
              "code": "47",
              "name": "Kumanovo"
            },
            {
              "code": "48",
              "name": "Lipkovo"
            },
            {
              "code": "49",
              "name": "Lozovo"
            },
            {
              "code": "51",
              "name": "Makedonska Kamenica"
            },
            {
              "code": "52",
              "name": "Makedonski Brod"
            },
            {
              "code": "50",
              "name": "Mavrovo i Rostuša"
            },
            {
              "code": "53",
              "name": "Mogila"
            },
            {
              "code": "54",
              "name": "Negotino"
            },
            {
              "code": "55",
              "name": "Novaci"
            },
            {
              "code": "56",
              "name": "Novo Selo"
            },
            {
              "code": "58",
              "name": "Ohrid"
            },
            {
              "code": "60",
              "name": "Pehčevo"
            },
            {
              "code": "59",
              "name": "Petrovec"
            },
            {
              "code": "61",
              "name": "Plasnica"
            },
            {
              "code": "62",
              "name": "Prilep"
            },
            {
              "code": "63",
              "name": "Probištip"
            },
            {
              "code": "",
              "name": "Radoviš"
            },
            {
              "code": "65",
              "name": "Rankovce"
            },
            {
              "code": "66",
              "name": "Resen"
            },
            {
              "code": "67",
              "name": "Rosoman"
            },
            {
              "code": "85",
              "name": "Skopje"
            },
            {
              "code": "70",
              "name": "Sopište"
            },
            {
              "code": "71",
              "name": "Staro Nagoričane"
            },
            {
              "code": "72",
              "name": "Struga"
            },
            {
              "code": "73",
              "name": "Strumica"
            },
            {
              "code": "74",
              "name": "Studeničani"
            },
            {
              "code": "69",
              "name": "Sveti Nikole"
            },
            {
              "code": "83",
              "name": "Štip"
            },
            {
              "code": "75",
              "name": "Tearce"
            },
            {
              "code": "76",
              "name": "Tetovo"
            },
            {
              "code": "10",
              "name": "Valandovo"
            },
            {
              "code": "11",
              "name": "Vasilevo"
            },
            {
              "code": "13",
              "name": "Veles"
            },
            {
              "code": "12",
              "name": "Vevčani"
            },
            {
              "code": "14",
              "name": "Vinica"
            },
            {
              "code": "16",
              "name": "Vrapčište"
            },
            {
              "code": "32",
              "name": "Zelenikovo"
            },
            {
              "code": "33",
              "name": "Zrnovci"
            },
            {
              "code": "30",
              "name": "Želino"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ML",
          "name": "Mali",
          "native": "Mali",
          "flag": "🇲🇱",
          "phonePrefix": "+223",
          "regions": [
            {
              "code": "BKO",
              "name": "Bamako"
            },
            {
              "code": "7",
              "name": "Gao"
            },
            {
              "code": "1",
              "name": "Kayes"
            },
            {
              "code": "8",
              "name": "Kidal"
            },
            {
              "code": "2",
              "name": "Koulikoro"
            },
            {
              "code": "5",
              "name": "Mopti"
            },
            {
              "code": "4",
              "name": "Segou"
            },
            {
              "code": "3",
              "name": "Sikasso"
            },
            {
              "code": "6",
              "name": "Tombouctou"
            },
            {
              "code": "9",
              "name": "Taoudénit"
            },
            {
              "code": "10",
              "name": "Ménaka"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MM",
          "name": "Myanmar",
          "native": "မြန်မာ",
          "flag": "🇲🇲",
          "phonePrefix": "+95",
          "regions": [
            {
              "code": "07",
              "name": "Ayeyarwady"
            },
            {
              "code": "02",
              "name": "Bago"
            },
            {
              "code": "14",
              "name": "Chin"
            },
            {
              "code": "11",
              "name": "Kachin"
            },
            {
              "code": "12",
              "name": "Kayah"
            },
            {
              "code": "13",
              "name": "Kayin"
            },
            {
              "code": "03",
              "name": "Magway"
            },
            {
              "code": "04",
              "name": "Mandalay"
            },
            {
              "code": "15",
              "name": "Mon"
            },
            {
              "code": "18",
              "name": "Nay Pyi Taw"
            },
            {
              "code": "16",
              "name": "Rakhine"
            },
            {
              "code": "01",
              "name": "Sagaing"
            },
            {
              "code": "17",
              "name": "Shan"
            },
            {
              "code": "05",
              "name": "Tanintharyi"
            },
            {
              "code": "06",
              "name": "Yangon"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MN",
          "name": "Mongolia",
          "native": "Монгол улс",
          "flag": "🇲🇳",
          "phonePrefix": "+976",
          "regions": [
            {
              "code": "073",
              "name": "Arhangay"
            },
            {
              "code": "071",
              "name": "Bayan-Olgiy"
            },
            {
              "code": "069",
              "name": "Bayanhongor"
            },
            {
              "code": "067",
              "name": "Bulgan"
            },
            {
              "code": "037",
              "name": "Darhan"
            },
            {
              "code": "061",
              "name": "Dornod"
            },
            {
              "code": "063",
              "name": "Dornogovi"
            },
            {
              "code": "059",
              "name": "Dundgovi"
            },
            {
              "code": "057",
              "name": "Dzavhan"
            },
            {
              "code": "065",
              "name": "Govi-Altay"
            },
            {
              "code": "064",
              "name": "Govi-Sumber"
            },
            {
              "code": "043",
              "name": "Hovd"
            },
            {
              "code": "041",
              "name": "Hovsgol"
            },
            {
              "code": "053",
              "name": "Omnogovi"
            },
            {
              "code": "055",
              "name": "Ovorhangay"
            },
            {
              "code": "049",
              "name": "Selenge"
            },
            {
              "code": "051",
              "name": "Suhbaatar"
            },
            {
              "code": "047",
              "name": "Tov"
            },
            {
              "code": "1",
              "name": "Ulaanbaatar"
            },
            {
              "code": "046",
              "name": "Uvs"
            },
            {
              "code": "074",
              "name": "Erdenet"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MO",
          "name": "Macao",
          "native": "澳門",
          "flag": "🇲🇴",
          "phonePrefix": "+853",
          "regions": [
            {
              "code": null,
              "name": "Macao"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MP",
          "name": "Northern Mariana Islands",
          "native": "Northern Mariana Islands",
          "flag": "🇲🇵",
          "phonePrefix": "+1670",
          "regions": [
            {
              "code": null,
              "name": "Northern Islands"
            },
            {
              "code": null,
              "name": "Rota"
            },
            {
              "code": null,
              "name": "Saipan"
            },
            {
              "code": null,
              "name": "Tinian"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "MQ",
          "name": "Martinique",
          "native": "Martinique",
          "flag": "🇲🇶",
          "phonePrefix": "+596",
          "regions": [
            {
              "code": null,
              "name": "Martinique"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "MR",
          "name": "Mauritania",
          "native": "موريتانيا",
          "flag": "🇲🇷",
          "phonePrefix": "+222",
          "regions": [
            {
              "code": "07",
              "name": "Adrar"
            },
            {
              "code": "03",
              "name": "Assaba"
            },
            {
              "code": "05",
              "name": "Brakna"
            },
            {
              "code": "08",
              "name": "Dakhlet Nouadhibou"
            },
            {
              "code": "04",
              "name": "Gorgol"
            },
            {
              "code": "10",
              "name": "Guidimaka"
            },
            {
              "code": "01",
              "name": "Hodh Ech Chargui"
            },
            {
              "code": "02",
              "name": "Hodh El Gharbi"
            },
            {
              "code": "12",
              "name": "Inchiri"
            },
            {
              "code": "14",
              "name": "Nouakchott Nord"
            },
            {
              "code": "13",
              "name": "Nouakchott Ouest"
            },
            {
              "code": "15",
              "name": "Nouakchott Sud"
            },
            {
              "code": "09",
              "name": "Tagant"
            },
            {
              "code": "11",
              "name": "Tiris Zemmour"
            },
            {
              "code": "06",
              "name": "Trarza"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MS",
          "name": "Montserrat",
          "native": "Montserrat",
          "flag": "🇲🇸",
          "phonePrefix": "+1664",
          "regions": [
            {
              "code": null,
              "name": "Saint Anthony"
            },
            {
              "code": null,
              "name": "Saint Georges"
            },
            {
              "code": null,
              "name": "Saint Peter's"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MT",
          "name": "Malta",
          "native": "Malta",
          "flag": "🇲🇹",
          "phonePrefix": "+356",
          "regions": [
            {
              "code": "01",
              "name": "Attard"
            },
            {
              "code": "02",
              "name": "Balzan"
            },
            {
              "code": "03",
              "name": "Birgu"
            },
            {
              "code": "04",
              "name": "Birkirkara"
            },
            {
              "code": "05",
              "name": "Birżebbuġa"
            },
            {
              "code": "06",
              "name": "Bormla"
            },
            {
              "code": "07",
              "name": "Dingli"
            },
            {
              "code": "08",
              "name": "Fgura"
            },
            {
              "code": "09",
              "name": "Floriana"
            },
            {
              "code": "10",
              "name": "Fontana"
            },
            {
              "code": "11",
              "name": "Guda"
            },
            {
              "code": "12",
              "name": "Gżira"
            },
            {
              "code": "13",
              "name": "Għajnsielem"
            },
            {
              "code": "14",
              "name": "Għarb"
            },
            {
              "code": "15",
              "name": "Għargħur"
            },
            {
              "code": "16",
              "name": "Għasri"
            },
            {
              "code": "17",
              "name": "Għaxaq"
            },
            {
              "code": "18",
              "name": "Ħamrun"
            },
            {
              "code": "19",
              "name": "Iklin"
            },
            {
              "code": "20",
              "name": "Isla"
            },
            {
              "code": "21",
              "name": "Kalkara"
            },
            {
              "code": "22",
              "name": "Kerċem"
            },
            {
              "code": "23",
              "name": "Kirkop"
            },
            {
              "code": "24",
              "name": "Lija"
            },
            {
              "code": "25",
              "name": "Luqa"
            },
            {
              "code": "26",
              "name": "Marsa"
            },
            {
              "code": "27",
              "name": "Marsaskala"
            },
            {
              "code": "28",
              "name": "Marsaxlokk"
            },
            {
              "code": "29",
              "name": "Mdina"
            },
            {
              "code": "30",
              "name": "Mellieħa"
            },
            {
              "code": "31",
              "name": "Mġarr"
            },
            {
              "code": "32",
              "name": "Mosta"
            },
            {
              "code": "33",
              "name": "Mqabba"
            },
            {
              "code": "34",
              "name": "Msida"
            },
            {
              "code": "35",
              "name": "Mtarfa"
            },
            {
              "code": "36",
              "name": "Munxar"
            },
            {
              "code": "37",
              "name": "Nadur"
            },
            {
              "code": "38",
              "name": "Naxxar"
            },
            {
              "code": "39",
              "name": "Paola"
            },
            {
              "code": "40",
              "name": "Pembroke"
            },
            {
              "code": "41",
              "name": "Pietà"
            },
            {
              "code": "42",
              "name": "Qala"
            },
            {
              "code": "43",
              "name": "Qormi"
            },
            {
              "code": "44",
              "name": "Qrendi"
            },
            {
              "code": "45",
              "name": "Rabat Għawdex"
            },
            {
              "code": "46",
              "name": "Rabat Malta"
            },
            {
              "code": "47",
              "name": "Safi"
            },
            {
              "code": "48",
              "name": "San Ġiljan"
            },
            {
              "code": "49",
              "name": "San Ġwann"
            },
            {
              "code": "50",
              "name": "San Lawrenz"
            },
            {
              "code": "51",
              "name": "San Pawl il-Baħar"
            },
            {
              "code": "52",
              "name": "Sannat"
            },
            {
              "code": "53",
              "name": "Santa Luċija"
            },
            {
              "code": "54",
              "name": "Santa Venera"
            },
            {
              "code": "55",
              "name": "Siġġiewi"
            },
            {
              "code": "56",
              "name": "Sliema"
            },
            {
              "code": "57",
              "name": "Swieqi"
            },
            {
              "code": "58",
              "name": "Tai Xbiex"
            },
            {
              "code": "59",
              "name": "Tarzien"
            },
            {
              "code": "60",
              "name": "Valletta"
            },
            {
              "code": "61",
              "name": "Xagħra"
            },
            {
              "code": "62",
              "name": "Xewkija"
            },
            {
              "code": "63",
              "name": "Xgħajra"
            },
            {
              "code": "64",
              "name": "Żabbar"
            },
            {
              "code": "65",
              "name": "Żebbuġ Għawde"
            },
            {
              "code": "66",
              "name": "Żebbuġ Malta"
            },
            {
              "code": "67",
              "name": "Żejtun"
            },
            {
              "code": "68",
              "name": "Żurrieq"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "MU",
          "name": "Mauritius",
          "native": "Maurice",
          "flag": "🇲🇺",
          "phonePrefix": "+230",
          "regions": [
            {
              "code": "AG",
              "name": "Agalega Islands"
            },
            {
              "code": "BR",
              "name": "Beau Bassin-Rose Hill"
            },
            {
              "code": "BL",
              "name": "Black River"
            },
            {
              "code": "CC",
              "name": "Cargados Carajos Shoals"
            },
            {
              "code": "CU",
              "name": "Curepipe"
            },
            {
              "code": "FL",
              "name": "Flacq"
            },
            {
              "code": "GP",
              "name": "Grand Port"
            },
            {
              "code": "MO",
              "name": "Moka"
            },
            {
              "code": "PA",
              "name": "Pamplemousses"
            },
            {
              "code": "PW",
              "name": "Plaines Wilhems"
            },
            {
              "code": "PU",
              "name": "Port Louis (City)"
            },
            {
              "code": "PL",
              "name": "Port Louis"
            },
            {
              "code": "RR",
              "name": "Riviere du Rempart"
            },
            {
              "code": "RO",
              "name": "Rodrigues Island"
            },
            {
              "code": "SA",
              "name": "Savanne"
            },
            {
              "code": "CP",
              "name": "Vacoas-Phoenix"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MV",
          "name": "Maldives",
          "native": "Maldives",
          "flag": "🇲🇻",
          "phonePrefix": "+960",
          "regions": [
            {
              "code": "02",
              "name": "Alifu Alifu"
            },
            {
              "code": "00",
              "name": "Alifu Dhaalu"
            },
            {
              "code": "20",
              "name": "Baa"
            },
            {
              "code": "17",
              "name": "Dhaalu"
            },
            {
              "code": "14",
              "name": "Faafu"
            },
            {
              "code": "27",
              "name": "Gaafu Alifu"
            },
            {
              "code": "28",
              "name": "Gaafu Dhaalu"
            },
            {
              "code": "29",
              "name": "Gnaviyani"
            },
            {
              "code": "07",
              "name": "Haa Alifu"
            },
            {
              "code": "23",
              "name": "Haa Dhaalu"
            },
            {
              "code": "26",
              "name": "Kaafu"
            },
            {
              "code": "05",
              "name": "Laamu"
            },
            {
              "code": "03",
              "name": "Lhaviyani"
            },
            {
              "code": "MLE",
              "name": "Malé"
            },
            {
              "code": "12",
              "name": "Meemu"
            },
            {
              "code": "25",
              "name": "Noonu"
            },
            {
              "code": "13",
              "name": "Raa"
            },
            {
              "code": "01",
              "name": "Seenu"
            },
            {
              "code": "24",
              "name": "Shaviyani"
            },
            {
              "code": "08",
              "name": "Thaa"
            },
            {
              "code": "04",
              "name": "Vaavu"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MW",
          "name": "Malawi",
          "native": "Malawi",
          "flag": "🇲🇼",
          "phonePrefix": "+265",
          "regions": [
            {
              "code": "BA",
              "name": "Balaka"
            },
            {
              "code": "BL",
              "name": "Blantyre"
            },
            {
              "code": "CK",
              "name": "Chikwawa"
            },
            {
              "code": "CR",
              "name": "Chiradzulu"
            },
            {
              "code": "CT",
              "name": "Chitipa"
            },
            {
              "code": "DE",
              "name": "Dedza"
            },
            {
              "code": "DO",
              "name": "Dowa"
            },
            {
              "code": "KR",
              "name": "Karonga"
            },
            {
              "code": "KS",
              "name": "Kasungu"
            },
            {
              "code": "LK",
              "name": "Likoma"
            },
            {
              "code": "LI",
              "name": "Lilongwe"
            },
            {
              "code": "MH",
              "name": "Machinga"
            },
            {
              "code": "MG",
              "name": "Mangochi"
            },
            {
              "code": "MC",
              "name": "Mchinji"
            },
            {
              "code": "MU",
              "name": "Mulanje"
            },
            {
              "code": "MW",
              "name": "Mwanza"
            },
            {
              "code": "MZ",
              "name": "Mzimba"
            },
            {
              "code": "NE",
              "name": "Nkhata Bay"
            },
            {
              "code": "NB",
              "name": "Nkhotakota"
            },
            {
              "code": "NS",
              "name": "Nsanje"
            },
            {
              "code": "NU",
              "name": "Ntcheu"
            },
            {
              "code": "NI",
              "name": "Ntchisi"
            },
            {
              "code": "PH",
              "name": "Phalombe"
            },
            {
              "code": "RU",
              "name": "Rumphi"
            },
            {
              "code": "SA",
              "name": "Salima"
            },
            {
              "code": "TH",
              "name": "Thyolo"
            },
            {
              "code": "ZO",
              "name": "Zomba"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MX",
          "name": "Mexico",
          "native": "México",
          "flag": "🇲🇽",
          "phonePrefix": "+52",
          "regions": [
            {
              "code": "AGU",
              "name": "Aguascalientes"
            },
            {
              "code": "BCN",
              "name": "Baja California"
            },
            {
              "code": "BCS",
              "name": "Baja California Sur"
            },
            {
              "code": "CAM",
              "name": "Campeche"
            },
            {
              "code": "CMX",
              "name": "Ciudad de México"
            },
            {
              "code": "CHP",
              "name": "Chiapas"
            },
            {
              "code": "CHH",
              "name": "Chihuahua"
            },
            {
              "code": "COA",
              "name": "Coahuila de Zaragoza"
            },
            {
              "code": "COL",
              "name": "Colima"
            },
            {
              "code": "DUR",
              "name": "Durango"
            },
            {
              "code": "MEX",
              "name": "Estado de México"
            },
            {
              "code": "GUA",
              "name": "Guanajuato"
            },
            {
              "code": "GRO",
              "name": "Guerrero"
            },
            {
              "code": "HID",
              "name": "Hidalgo"
            },
            {
              "code": "JAL",
              "name": "Jalisco"
            },
            {
              "code": "MIC",
              "name": "Michoacán de Ocampo"
            },
            {
              "code": "MOR",
              "name": "Morelos"
            },
            {
              "code": "NAY",
              "name": "Nayarit"
            },
            {
              "code": "NLE",
              "name": "Nuevo León"
            },
            {
              "code": "OAX",
              "name": "Oaxaca"
            },
            {
              "code": "PUE",
              "name": "Puebla"
            },
            {
              "code": "QUE",
              "name": "Querétaro de Arteaga"
            },
            {
              "code": "ROO",
              "name": "Quintana Roo"
            },
            {
              "code": "SLP",
              "name": "San Luis Potosí"
            },
            {
              "code": "SIN",
              "name": "Sinaloa"
            },
            {
              "code": "SON",
              "name": "Sonora"
            },
            {
              "code": "TAB",
              "name": "Tabasco"
            },
            {
              "code": "TAM",
              "name": "Tamaulipas"
            },
            {
              "code": "TLA",
              "name": "Tlaxcala"
            },
            {
              "code": "VER",
              "name": "Veracruz"
            },
            {
              "code": "YUC",
              "name": "Yucatán"
            },
            {
              "code": "ZAC",
              "name": "Zacatecas"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MY",
          "name": "Malaysia",
          "native": "Malaysia",
          "flag": "🇲🇾",
          "phonePrefix": "+60",
          "regions": [
            {
              "code": "01",
              "name": "Johor"
            },
            {
              "code": "02",
              "name": "Kedah"
            },
            {
              "code": "03",
              "name": "Kelantan"
            },
            {
              "code": "04",
              "name": "Melaka"
            },
            {
              "code": "05",
              "name": "Negeri Sembilan"
            },
            {
              "code": "06",
              "name": "Pahang"
            },
            {
              "code": "08",
              "name": "Perak"
            },
            {
              "code": "09",
              "name": "Perlis"
            },
            {
              "code": "07",
              "name": "Pulau Pinang"
            },
            {
              "code": "12",
              "name": "Sabah"
            },
            {
              "code": "13",
              "name": "Sarawak"
            },
            {
              "code": "10",
              "name": "Selangor"
            },
            {
              "code": "11",
              "name": "Terengganu"
            },
            {
              "code": "14",
              "name": "Wilayah Persekutuan (Kuala Lumpur)"
            },
            {
              "code": "15",
              "name": "Wilayah Persekutuan (Labuan)"
            },
            {
              "code": "16",
              "name": "Wilayah Persekutuan (Putrajaya)"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "MZ",
          "name": "Mozambique",
          "native": "Moçambique",
          "flag": "🇲🇿",
          "phonePrefix": "+258",
          "regions": [
            {
              "code": "P",
              "name": "Cabo Delgado"
            },
            {
              "code": "G",
              "name": "Gaza"
            },
            {
              "code": "I",
              "name": "Inhambane"
            },
            {
              "code": "B",
              "name": "Manica"
            },
            {
              "code": "L",
              "name": "Maputo"
            },
            {
              "code": "MPM",
              "name": "Maputo (City)"
            },
            {
              "code": "N",
              "name": "Nampula"
            },
            {
              "code": "A",
              "name": "Niassa"
            },
            {
              "code": "S",
              "name": "Sofala"
            },
            {
              "code": "T",
              "name": "Tete"
            },
            {
              "code": "Q",
              "name": "Zambezia"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NA",
          "name": "Namibia",
          "native": "Namibia",
          "flag": "🇳🇦",
          "phonePrefix": "+264",
          "regions": [
            {
              "code": "ER",
              "name": "Erongo"
            },
            {
              "code": "HA",
              "name": "Hardap"
            },
            {
              "code": "KE",
              "name": "Kavango East"
            },
            {
              "code": "KW",
              "name": "Kavango West"
            },
            {
              "code": "KA",
              "name": "Karas"
            },
            {
              "code": "KH",
              "name": "Khomas"
            },
            {
              "code": "KU",
              "name": "Kunene"
            },
            {
              "code": "OW",
              "name": "Ohangwena"
            },
            {
              "code": "OH",
              "name": "Omaheke"
            },
            {
              "code": "OS",
              "name": "Omusati"
            },
            {
              "code": "ON",
              "name": "Oshana"
            },
            {
              "code": "OT",
              "name": "Oshikoto"
            },
            {
              "code": "OD",
              "name": "Otjozondjupa"
            },
            {
              "code": "CA",
              "name": "Zambezi"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NC",
          "name": "New Caledonia",
          "native": "Nouvelle-Calédonie",
          "flag": "🇳🇨",
          "phonePrefix": "+687",
          "regions": [
            {
              "code": null,
              "name": "Iles Loyaute"
            },
            {
              "code": null,
              "name": "Nord"
            },
            {
              "code": null,
              "name": "Sud"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NE",
          "name": "Niger",
          "native": "Niger",
          "flag": "🇳🇪",
          "phonePrefix": "+227",
          "regions": [
            {
              "code": "1",
              "name": "Agadez"
            },
            {
              "code": "2",
              "name": "Diffa"
            },
            {
              "code": "3",
              "name": "Dosso"
            },
            {
              "code": "4",
              "name": "Maradi"
            },
            {
              "code": "8",
              "name": "Niamey"
            },
            {
              "code": "5",
              "name": "Tahoua"
            },
            {
              "code": "6",
              "name": "Tillabéri"
            },
            {
              "code": "7",
              "name": "Zinder"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NF",
          "name": "Norfolk Island",
          "native": "Norfolk Island",
          "flag": "🇳🇫",
          "phonePrefix": "+672",
          "regions": [
            {
              "code": null,
              "name": "Norfolk Island"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NG",
          "name": "Nigeria",
          "native": "Nigeria",
          "flag": "🇳🇬",
          "phonePrefix": "+234",
          "regions": [
            {
              "code": "AB",
              "name": "Abia"
            },
            {
              "code": "FC",
              "name": "Abuja Federal Capital Territory"
            },
            {
              "code": "AD",
              "name": "Adamawa"
            },
            {
              "code": "AK",
              "name": "Akwa Ibom"
            },
            {
              "code": "AN",
              "name": "Anambra"
            },
            {
              "code": "BA",
              "name": "Bauchi"
            },
            {
              "code": "BY",
              "name": "Bayelsa"
            },
            {
              "code": "BE",
              "name": "Benue"
            },
            {
              "code": "BO",
              "name": "Borno"
            },
            {
              "code": "CR",
              "name": "Cross River"
            },
            {
              "code": "DE",
              "name": "Delta"
            },
            {
              "code": "EB",
              "name": "Ebonyi"
            },
            {
              "code": "ED",
              "name": "Edo"
            },
            {
              "code": "EK",
              "name": "Ekiti"
            },
            {
              "code": "EN",
              "name": "Enugu"
            },
            {
              "code": "GO",
              "name": "Gombe"
            },
            {
              "code": "IM",
              "name": "Imo"
            },
            {
              "code": "JI",
              "name": "Jigawa"
            },
            {
              "code": "KD",
              "name": "Kaduna"
            },
            {
              "code": "KN",
              "name": "Kano"
            },
            {
              "code": "KT",
              "name": "Katsina"
            },
            {
              "code": "KE",
              "name": "Kebbi"
            },
            {
              "code": "KO",
              "name": "Kogi"
            },
            {
              "code": "KW",
              "name": "Kwara"
            },
            {
              "code": "LA",
              "name": "Lagos"
            },
            {
              "code": "NA",
              "name": "Nassarawa"
            },
            {
              "code": "NI",
              "name": "Niger"
            },
            {
              "code": "OG",
              "name": "Ogun"
            },
            {
              "code": "ON",
              "name": "Ondo"
            },
            {
              "code": "OS",
              "name": "Osun"
            },
            {
              "code": "OY",
              "name": "Oyo"
            },
            {
              "code": "PL",
              "name": "Plateau"
            },
            {
              "code": "RI",
              "name": "Rivers"
            },
            {
              "code": "SO",
              "name": "Sokoto"
            },
            {
              "code": "TA",
              "name": "Taraba"
            },
            {
              "code": "YO",
              "name": "Yobe"
            },
            {
              "code": "ZA",
              "name": "Zamfara"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NI",
          "name": "Nicaragua",
          "native": "Nicaragua",
          "flag": "🇳🇮",
          "phonePrefix": "+505",
          "regions": [
            {
              "code": "BO",
              "name": "Boaco"
            },
            {
              "code": "CA",
              "name": "Carazo"
            },
            {
              "code": "CI",
              "name": "Chinandega"
            },
            {
              "code": "CO",
              "name": "Chontales"
            },
            {
              "code": "ES",
              "name": "Estelí"
            },
            {
              "code": "GR",
              "name": "Granada"
            },
            {
              "code": "JI",
              "name": "Jinotega"
            },
            {
              "code": "LE",
              "name": "León"
            },
            {
              "code": "MD",
              "name": "Madriz"
            },
            {
              "code": "MN",
              "name": "Managua"
            },
            {
              "code": "MS",
              "name": "Masaya"
            },
            {
              "code": "MT",
              "name": "Matagalpa"
            },
            {
              "code": "NS",
              "name": "Nueva Segovia"
            },
            {
              "code": "SJ",
              "name": "Río San Juan"
            },
            {
              "code": "RI",
              "name": "Rivas"
            },
            {
              "code": "AN",
              "name": "Atlántico Norte"
            },
            {
              "code": "AS",
              "name": "Atlántico Sur"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NL",
          "name": "Netherlands",
          "native": "Nederland",
          "flag": "🇳🇱",
          "phonePrefix": "+31",
          "regions": [
            {
              "code": "DR",
              "name": "Drenthe"
            },
            {
              "code": "FL",
              "name": "Flevoland"
            },
            {
              "code": "FR",
              "name": "Friesland"
            },
            {
              "code": "GE",
              "name": "Gelderland"
            },
            {
              "code": "GR",
              "name": "Groningen"
            },
            {
              "code": "LI",
              "name": "Limburg"
            },
            {
              "code": "NB",
              "name": "Noord-Brabant"
            },
            {
              "code": "NH",
              "name": "Noord-Holland"
            },
            {
              "code": "OV",
              "name": "Overijssel"
            },
            {
              "code": "UT",
              "name": "Utrecht"
            },
            {
              "code": "ZE",
              "name": "Zeeland"
            },
            {
              "code": "ZH",
              "name": "Zuid-Holland"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "NO",
          "name": "Norway",
          "native": "Norge",
          "flag": "🇳🇴",
          "phonePrefix": "+47",
          "regions": [
            {
              "code": "02",
              "name": "Akershus"
            },
            {
              "code": "09",
              "name": "Aust-Agder"
            },
            {
              "code": "06",
              "name": "Buskerud"
            },
            {
              "code": "20",
              "name": "Finnmark"
            },
            {
              "code": "04",
              "name": "Hedmark"
            },
            {
              "code": "12",
              "name": "Hordaland"
            },
            {
              "code": "15",
              "name": "Møre og Romsdal"
            },
            {
              "code": "18",
              "name": "Nordland"
            },
            {
              "code": "17",
              "name": "Nord-Trøndelag"
            },
            {
              "code": "05",
              "name": "Oppland"
            },
            {
              "code": "03",
              "name": "Oslo"
            },
            {
              "code": "11",
              "name": "Rogaland"
            },
            {
              "code": "14",
              "name": "Sogn og Fjordane"
            },
            {
              "code": "16",
              "name": "Sør-Trøndelag"
            },
            {
              "code": "08",
              "name": "Telemark"
            },
            {
              "code": "19",
              "name": "Troms"
            },
            {
              "code": "10",
              "name": "Vest-Agder"
            },
            {
              "code": "07",
              "name": "Vestfold"
            },
            {
              "code": "01",
              "name": "Østfold"
            },
            {
              "code": "22",
              "name": "Jan Mayen"
            },
            {
              "code": "21",
              "name": "Svalbard"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NP",
          "name": "Nepal",
          "native": "नपल",
          "flag": "🇳🇵",
          "phonePrefix": "+977",
          "regions": [
            {
              "code": "1",
              "name": "Province No. 1"
            },
            {
              "code": "2",
              "name": "Madhesh Province"
            },
            {
              "code": "3",
              "name": "Bagmati Province"
            },
            {
              "code": "4",
              "name": "Gandaki Province"
            },
            {
              "code": "5",
              "name": "Lumbini Province"
            },
            {
              "code": "6",
              "name": "Karnali Province"
            },
            {
              "code": "7",
              "name": "Sudurpashchim Province"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NR",
          "name": "Nauru",
          "native": "Nauru",
          "flag": "🇳🇷",
          "phonePrefix": "+674",
          "regions": [
            {
              "code": "01",
              "name": "Aiwo"
            },
            {
              "code": "02",
              "name": "Anabar"
            },
            {
              "code": "03",
              "name": "Anetan"
            },
            {
              "code": "04",
              "name": "Anibare"
            },
            {
              "code": "05",
              "name": "Baiti"
            },
            {
              "code": "06",
              "name": "Boe"
            },
            {
              "code": "07",
              "name": "Buada"
            },
            {
              "code": "08",
              "name": "Denigomodu"
            },
            {
              "code": "09",
              "name": "Ewa"
            },
            {
              "code": "10",
              "name": "Ijuw"
            },
            {
              "code": "11",
              "name": "Meneng"
            },
            {
              "code": "12",
              "name": "Nibok"
            },
            {
              "code": "13",
              "name": "Uaboe"
            },
            {
              "code": "14",
              "name": "Yaren"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NU",
          "name": "Niue",
          "native": "Niuē",
          "flag": "🇳🇺",
          "phonePrefix": "+683",
          "regions": [
            {
              "code": null,
              "name": "Niue"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "NZ",
          "name": "New Zealand",
          "native": "New Zealand",
          "flag": "🇳🇿",
          "phonePrefix": "+64",
          "regions": [
            {
              "code": "AUK",
              "name": "Auckland"
            },
            {
              "code": "BOP",
              "name": "Bay of Plenty"
            },
            {
              "code": "CAN",
              "name": "Canterbury"
            },
            {
              "code": "GIS",
              "name": "Gisborne"
            },
            {
              "code": "HKB",
              "name": "Hawke's Bay"
            },
            {
              "code": "MBH",
              "name": "Marlborough"
            },
            {
              "code": "MWT",
              "name": "Manawatu-Wanganui"
            },
            {
              "code": "NTL",
              "name": "Northland"
            },
            {
              "code": "NSN",
              "name": "Nelson"
            },
            {
              "code": "OTA",
              "name": "Otago"
            },
            {
              "code": "STL",
              "name": "Southland"
            },
            {
              "code": "TKI",
              "name": "Taranaki"
            },
            {
              "code": "TAS",
              "name": "Tasman"
            },
            {
              "code": "WKO",
              "name": "Waikato"
            },
            {
              "code": "WGN",
              "name": "Wellington"
            },
            {
              "code": "WTC",
              "name": "West Coast"
            },
            {
              "code": "CIT",
              "name": "Chatham Islands Territory"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "OM",
          "name": "Oman",
          "native": "عمان",
          "flag": "🇴🇲",
          "phonePrefix": "+968",
          "regions": [
            {
              "code": "DA",
              "name": "Ad Dakhiliyah"
            },
            {
              "code": "BU",
              "name": "Al Buraymi"
            },
            {
              "code": "WU",
              "name": "Al Wusta"
            },
            {
              "code": "ZA",
              "name": "Az Zahirah"
            },
            {
              "code": "BS",
              "name": "Janub al Batinah"
            },
            {
              "code": "SS",
              "name": "Janub ash Sharqiyah"
            },
            {
              "code": "MA",
              "name": "Masqat"
            },
            {
              "code": "MU",
              "name": "Musandam"
            },
            {
              "code": "BJ",
              "name": "Shamal al Batinah"
            },
            {
              "code": "SJ",
              "name": "Shamal ash Sharqiyah"
            },
            {
              "code": "ZU",
              "name": "Zufar"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PA",
          "name": "Panama",
          "native": "Panamá",
          "flag": "🇵🇦",
          "phonePrefix": "+507",
          "regions": [
            {
              "code": "1",
              "name": "Bocas del Toro"
            },
            {
              "code": "4",
              "name": "Chiriquí"
            },
            {
              "code": "2",
              "name": "Coclé"
            },
            {
              "code": "3",
              "name": "Colón"
            },
            {
              "code": "5",
              "name": "Darién"
            },
            {
              "code": "EM",
              "name": "Emberá"
            },
            {
              "code": "6",
              "name": "Herrera"
            },
            {
              "code": "KY",
              "name": "Kuna Yala"
            },
            {
              "code": "7",
              "name": "Los Santos"
            },
            {
              "code": "NB",
              "name": "Ngäbe-Buglé"
            },
            {
              "code": "8",
              "name": "Panamá"
            },
            {
              "code": "10",
              "name": "Panamá Oeste"
            },
            {
              "code": "9",
              "name": "Veraguas"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "PE",
          "name": "Peru",
          "native": "Perú",
          "flag": "🇵🇪",
          "phonePrefix": "+51",
          "regions": [
            {
              "code": "AMA",
              "name": "Amazonas"
            },
            {
              "code": "ANC",
              "name": "Ancash"
            },
            {
              "code": "APU",
              "name": "Apurimac"
            },
            {
              "code": "ARE",
              "name": "Arequipa"
            },
            {
              "code": "AYA",
              "name": "Ayacucho"
            },
            {
              "code": "CAJ",
              "name": "Cajamarca"
            },
            {
              "code": "CAL",
              "name": "Callao"
            },
            {
              "code": "CUS",
              "name": "Cusco"
            },
            {
              "code": "HUV",
              "name": "Huancavelica"
            },
            {
              "code": "HUC",
              "name": "Huanuco"
            },
            {
              "code": "ICA",
              "name": "Ica"
            },
            {
              "code": "JUN",
              "name": "Junin"
            },
            {
              "code": "LAL",
              "name": "La Libertad"
            },
            {
              "code": "LAM",
              "name": "Lambayeque"
            },
            {
              "code": "LIM",
              "name": "Lima"
            },
            {
              "code": "LOR",
              "name": "Loreto"
            },
            {
              "code": "MDD",
              "name": "Madre de Dios"
            },
            {
              "code": "MOQ",
              "name": "Moquegua"
            },
            {
              "code": "LMA",
              "name": "Municipalidad Metropolitana de Lima"
            },
            {
              "code": "PAS",
              "name": "Pasco"
            },
            {
              "code": "PIU",
              "name": "Piura"
            },
            {
              "code": "PUN",
              "name": "Puno"
            },
            {
              "code": "SAM",
              "name": "San Martin"
            },
            {
              "code": "TAC",
              "name": "Tacna"
            },
            {
              "code": "TUM",
              "name": "Tumbes"
            },
            {
              "code": "UCA",
              "name": "Ucayali"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PF",
          "name": "French Polynesia",
          "native": "Polynésie française",
          "flag": "🇵🇫",
          "phonePrefix": "+689",
          "regions": [
            {
              "code": null,
              "name": "Archipel des Marquises"
            },
            {
              "code": null,
              "name": "Archipel des Tuamotu"
            },
            {
              "code": null,
              "name": "Archipel des Tubuai"
            },
            {
              "code": null,
              "name": "Iles du Vent"
            },
            {
              "code": null,
              "name": "Iles Sous-le-Vent"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PG",
          "name": "Papua New Guinea",
          "native": "Papua Niugini",
          "flag": "🇵🇬",
          "phonePrefix": "+675",
          "regions": [
            {
              "code": "NSB",
              "name": "Bougainville"
            },
            {
              "code": "CPM",
              "name": "Central"
            },
            {
              "code": "CPK",
              "name": "Chimbu"
            },
            {
              "code": "EBR",
              "name": "East New Britain"
            },
            {
              "code": "ESW",
              "name": "East Sepik"
            },
            {
              "code": "EHG",
              "name": "Eastern Highlands"
            },
            {
              "code": "EPW",
              "name": "Enga"
            },
            {
              "code": "GPK",
              "name": "Gulf"
            },
            {
              "code": "HLA",
              "name": "Hela"
            },
            {
              "code": "JWK",
              "name": "Jiwaka"
            },
            {
              "code": "MOM",
              "name": "Madang"
            },
            {
              "code": "MRL",
              "name": "Manus"
            },
            {
              "code": "MBA",
              "name": "Milne Bay"
            },
            {
              "code": "MPL",
              "name": "Morobe"
            },
            {
              "code": "NCD",
              "name": "Port Moresby"
            },
            {
              "code": "NIK",
              "name": "New Ireland"
            },
            {
              "code": "NPP",
              "name": "Northern"
            },
            {
              "code": "SHM",
              "name": "Southern Highlands"
            },
            {
              "code": "WBK",
              "name": "West New Britain"
            },
            {
              "code": "SAN",
              "name": "West Sepik"
            },
            {
              "code": "WPD",
              "name": "Western"
            },
            {
              "code": "WHM",
              "name": "Western Highlands"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PH",
          "name": "Philippines",
          "native": "Pilipinas",
          "flag": "🇵🇭",
          "phonePrefix": "+63",
          "regions": [
            {
              "code": "ABR",
              "name": "Abra"
            },
            {
              "code": "AGN",
              "name": "Agusan del Norte"
            },
            {
              "code": "AGS",
              "name": "Agusan del Sur"
            },
            {
              "code": "AKL",
              "name": "Aklan"
            },
            {
              "code": "ALB",
              "name": "Albay"
            },
            {
              "code": "ANT",
              "name": "Antique"
            },
            {
              "code": "APA",
              "name": "Apayao"
            },
            {
              "code": "AUR",
              "name": "Aurora"
            },
            {
              "code": "BAS",
              "name": "Basilan"
            },
            {
              "code": "BAN",
              "name": "Bataan"
            },
            {
              "code": "BTN",
              "name": "Batanes"
            },
            {
              "code": "BTG",
              "name": "Batangas"
            },
            {
              "code": "BEN",
              "name": "Benguet"
            },
            {
              "code": "BIL",
              "name": "Biliran"
            },
            {
              "code": "BOH",
              "name": "Bohol"
            },
            {
              "code": "BUK",
              "name": "Bukidnon"
            },
            {
              "code": "BUL",
              "name": "Bulacan"
            },
            {
              "code": "CAG",
              "name": "Cagayan"
            },
            {
              "code": "CAN",
              "name": "Camarines Norte"
            },
            {
              "code": "CAS",
              "name": "Camarines Sur"
            },
            {
              "code": "CAM",
              "name": "Camiguin"
            },
            {
              "code": "CAP",
              "name": "Capiz"
            },
            {
              "code": "CAT",
              "name": "Catanduanes"
            },
            {
              "code": "CAV",
              "name": "Cavite"
            },
            {
              "code": "CEB",
              "name": "Cebu"
            },
            {
              "code": "NCO",
              "name": "Cotabato"
            },
            {
              "code": "DAV",
              "name": "Davao del Norte"
            },
            {
              "code": "DAS",
              "name": "Davao del Sur"
            },
            {
              "code": "DVO",
              "name": "Davao Occidental"
            },
            {
              "code": "DAO",
              "name": "Davao Oriental"
            },
            {
              "code": "COM",
              "name": "Davao de Oro"
            },
            {
              "code": "DIN",
              "name": "Dinagat Islands"
            },
            {
              "code": "EAS",
              "name": "Eastern Samar"
            },
            {
              "code": "GUI",
              "name": "Guimaras"
            },
            {
              "code": "IFU",
              "name": "Ifugao"
            },
            {
              "code": "ILN",
              "name": "Ilocos Norte"
            },
            {
              "code": "ILS",
              "name": "Ilocos Sur"
            },
            {
              "code": "ILI",
              "name": "Iloilo"
            },
            {
              "code": "ISA",
              "name": "Isabela"
            },
            {
              "code": "KAL",
              "name": "Kalinga"
            },
            {
              "code": "LUN",
              "name": "La Union"
            },
            {
              "code": "LAG",
              "name": "Laguna"
            },
            {
              "code": "LAN",
              "name": "Lanao del Norte"
            },
            {
              "code": "LAS",
              "name": "Lanao del Sur"
            },
            {
              "code": "LEY",
              "name": "Leyte"
            },
            {
              "code": "MAG",
              "name": "Maguindanao"
            },
            {
              "code": "MAD",
              "name": "Marinduque"
            },
            {
              "code": "MAS",
              "name": "Masbate"
            },
            {
              "code": "00",
              "name": "Metro Manila"
            },
            {
              "code": "MDC",
              "name": "Mindoro Occidental"
            },
            {
              "code": "MDR",
              "name": "Mindoro Oriental"
            },
            {
              "code": "MSC",
              "name": "Misamis Occidental"
            },
            {
              "code": "MSR",
              "name": "Misamis Oriental"
            },
            {
              "code": "MOU",
              "name": "Mountain Province"
            },
            {
              "code": "NEC",
              "name": "Negros Occidental"
            },
            {
              "code": "NER",
              "name": "Negros Oriental"
            },
            {
              "code": "NSA",
              "name": "Northern Samar"
            },
            {
              "code": "NUE",
              "name": "Nueva Ecija"
            },
            {
              "code": "NUV",
              "name": "Nueva Vizcaya"
            },
            {
              "code": "PLW",
              "name": "Palawan"
            },
            {
              "code": "PAM",
              "name": "Pampanga"
            },
            {
              "code": "PAN",
              "name": "Pangasinan"
            },
            {
              "code": "QUE",
              "name": "Quezon"
            },
            {
              "code": "QUI",
              "name": "Quirino"
            },
            {
              "code": "RIZ",
              "name": "Rizal"
            },
            {
              "code": "ROM",
              "name": "Romblon"
            },
            {
              "code": "WSA",
              "name": "Samar"
            },
            {
              "code": "SAR",
              "name": "Sarangani"
            },
            {
              "code": "SIG",
              "name": "Siquijor"
            },
            {
              "code": "SOR",
              "name": "Sorsogon"
            },
            {
              "code": "SCO",
              "name": "South Cotabato"
            },
            {
              "code": "SLE",
              "name": "Southern Leyte"
            },
            {
              "code": "AUK",
              "name": "Sultan Kudarat"
            },
            {
              "code": "SLU",
              "name": "Sulu"
            },
            {
              "code": "SUN",
              "name": "Surigao del Norte"
            },
            {
              "code": "SUR",
              "name": "Surigao del Sur"
            },
            {
              "code": "TAR",
              "name": "Tarlac"
            },
            {
              "code": "TAW",
              "name": "Tawi-Tawi"
            },
            {
              "code": "ZMB",
              "name": "Zambales"
            },
            {
              "code": "ZAN",
              "name": "Zamboanga del Norte"
            },
            {
              "code": "ZAS",
              "name": "Zamboanga del Sur"
            },
            {
              "code": "ZSI",
              "name": "Zamboanga Sibugay"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PK",
          "name": "Pakistan",
          "native": "Pakistan",
          "flag": "🇵🇰",
          "phonePrefix": "+92",
          "regions": [
            {
              "code": "JK",
              "name": "Āzād Kashmīr"
            },
            {
              "code": "BA",
              "name": "Balōchistān"
            },
            {
              "code": "GB",
              "name": "Gilgit-Baltistān"
            },
            {
              "code": "IS",
              "name": "Islāmābād"
            },
            {
              "code": "KP",
              "name": "Khaībar Pakhtūnkhwās"
            },
            {
              "code": "PB",
              "name": "Punjāb"
            },
            {
              "code": "SD",
              "name": "Sindh"
            },
            {
              "code": "TA",
              "name": "Federally Administered Tribal Areas"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PL",
          "name": "Poland",
          "native": "Polska",
          "flag": "🇵🇱",
          "phonePrefix": "+48",
          "regions": [
            {
              "code": "02",
              "name": "Dolnośląskie"
            },
            {
              "code": "04",
              "name": "Kujawsko-pomorskie"
            },
            {
              "code": "10",
              "name": "Łódzkie"
            },
            {
              "code": "06",
              "name": "Lubelskie"
            },
            {
              "code": "08",
              "name": "Lubuskie"
            },
            {
              "code": "12",
              "name": "Małopolskie"
            },
            {
              "code": "14",
              "name": "Mazowieckie"
            },
            {
              "code": "16",
              "name": "Opolskie"
            },
            {
              "code": "18",
              "name": "Podkarpackie"
            },
            {
              "code": "20",
              "name": "Podlaskie"
            },
            {
              "code": "22",
              "name": "Pomorskie"
            },
            {
              "code": "24",
              "name": "Śląskie"
            },
            {
              "code": "26",
              "name": "Świętokrzyskie"
            },
            {
              "code": "28",
              "name": "Warmińsko-mazurskie"
            },
            {
              "code": "30",
              "name": "Wielkopolskie"
            },
            {
              "code": "32",
              "name": "Zachodniopomorskie"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PM",
          "name": "Saint Pierre and Miquelon",
          "native": "Saint-Pierre-et-Miquelon",
          "flag": "🇵🇲",
          "phonePrefix": "+508",
          "regions": [
            {
              "code": null,
              "name": "Miquelon"
            },
            {
              "code": null,
              "name": "Saint Pierre"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "PN",
          "name": "Pitcairn",
          "native": "Pitcairn Islands",
          "flag": "🇵🇳",
          "phonePrefix": "+64",
          "regions": [
            {
              "code": null,
              "name": "Pitcairn Islands"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PR",
          "name": "Puerto Rico",
          "native": "Puerto Rico",
          "flag": "🇵🇷",
          "phonePrefix": "+1787,1939",
          "regions": [
            {
              "code": null,
              "name": "Adjuntas"
            },
            {
              "code": null,
              "name": "Aguada"
            },
            {
              "code": null,
              "name": "Aguadilla"
            },
            {
              "code": null,
              "name": "Aguas Buenas"
            },
            {
              "code": null,
              "name": "Aibonito"
            },
            {
              "code": null,
              "name": "Anasco"
            },
            {
              "code": null,
              "name": "Arecibo"
            },
            {
              "code": null,
              "name": "Arroyo"
            },
            {
              "code": null,
              "name": "Barceloneta"
            },
            {
              "code": null,
              "name": "Barranquitas"
            },
            {
              "code": null,
              "name": "Bayamon"
            },
            {
              "code": null,
              "name": "Cabo Rojo"
            },
            {
              "code": null,
              "name": "Caguas"
            },
            {
              "code": null,
              "name": "Camuy"
            },
            {
              "code": null,
              "name": "Canovanas"
            },
            {
              "code": null,
              "name": "Carolina"
            },
            {
              "code": null,
              "name": "Cat"
            },
            {
              "code": null,
              "name": "Ceiba"
            },
            {
              "code": null,
              "name": "Ciales"
            },
            {
              "code": null,
              "name": "Cidra"
            },
            {
              "code": null,
              "name": "Coamo"
            },
            {
              "code": null,
              "name": "Comerio"
            },
            {
              "code": null,
              "name": "Corozal"
            },
            {
              "code": null,
              "name": "Culebra"
            },
            {
              "code": null,
              "name": "Dorado"
            },
            {
              "code": null,
              "name": "Fajardo"
            },
            {
              "code": null,
              "name": "Florida"
            },
            {
              "code": null,
              "name": "Guanica"
            },
            {
              "code": null,
              "name": "Guayama"
            },
            {
              "code": null,
              "name": "Guayanilla"
            },
            {
              "code": null,
              "name": "Guaynabo"
            },
            {
              "code": null,
              "name": "Gurabo"
            },
            {
              "code": null,
              "name": "Hatillo"
            },
            {
              "code": null,
              "name": "Hormigueros"
            },
            {
              "code": null,
              "name": "Humacao"
            },
            {
              "code": null,
              "name": "Isabe"
            },
            {
              "code": null,
              "name": "Juana Diaz"
            },
            {
              "code": null,
              "name": "Juncos"
            },
            {
              "code": null,
              "name": "Lajas"
            },
            {
              "code": null,
              "name": "Lares"
            },
            {
              "code": null,
              "name": "Las Marias"
            },
            {
              "code": null,
              "name": "Las oiza"
            },
            {
              "code": null,
              "name": "Luquillo"
            },
            {
              "code": null,
              "name": "Manati"
            },
            {
              "code": null,
              "name": "Maricao"
            },
            {
              "code": null,
              "name": "Maunabo"
            },
            {
              "code": null,
              "name": "Mayaguez"
            },
            {
              "code": null,
              "name": "Moca"
            },
            {
              "code": null,
              "name": "Morovis"
            },
            {
              "code": null,
              "name": "Naguabo"
            },
            {
              "code": null,
              "name": "Naranjito"
            },
            {
              "code": null,
              "name": "Orocovis"
            },
            {
              "code": null,
              "name": "Patillas"
            },
            {
              "code": null,
              "name": "Penuelas"
            },
            {
              "code": null,
              "name": "Ponce"
            },
            {
              "code": null,
              "name": "Quebradillas"
            },
            {
              "code": null,
              "name": "Rincon"
            },
            {
              "code": null,
              "name": "Rio Grande"
            },
            {
              "code": null,
              "name": "Sabana linas"
            },
            {
              "code": null,
              "name": "San German"
            },
            {
              "code": null,
              "name": "San Juan"
            },
            {
              "code": null,
              "name": "San Lorenzo"
            },
            {
              "code": null,
              "name": "San Sebastian"
            },
            {
              "code": null,
              "name": "Santa Isabel"
            },
            {
              "code": null,
              "name": "Toa Alta"
            },
            {
              "code": null,
              "name": "Toa Baja"
            },
            {
              "code": null,
              "name": "Trujillo Alto"
            },
            {
              "code": null,
              "name": "Utuado"
            },
            {
              "code": null,
              "name": "Vega Alta"
            },
            {
              "code": null,
              "name": "Vega ues"
            },
            {
              "code": null,
              "name": "Villalba"
            },
            {
              "code": null,
              "name": "Yabucoa"
            },
            {
              "code": null,
              "name": "Yauco"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "PS",
          "name": "State of Palestine",
          "native": "فلسطين",
          "flag": "🇵🇸",
          "phonePrefix": "+970",
          "regions": [
            {
              "code": "HBN",
              "name": "Ak Khalīl"
            },
            {
              "code": "JEM",
              "name": "Al Quds"
            },
            {
              "code": "JRH",
              "name": "Arīḩā wal Aghwār"
            },
            {
              "code": "BTH",
              "name": "Bayt Laḩm"
            },
            {
              "code": "DEB",
              "name": "Dayr al Balaḩ"
            },
            {
              "code": "GZA",
              "name": "Ghazzah"
            },
            {
              "code": "JEN",
              "name": "Janīn"
            },
            {
              "code": "KYS",
              "name": "Khān Yūnis"
            },
            {
              "code": "NBS",
              "name": "Nāblus"
            },
            {
              "code": "QQA",
              "name": "Qalqīyah"
            },
            {
              "code": "RFH",
              "name": "Rafaḩ"
            },
            {
              "code": "RBH",
              "name": "Rām Allāh wal Bīrah"
            },
            {
              "code": "SLT",
              "name": "Salfīt"
            },
            {
              "code": "NGZ",
              "name": "Shamāl Ghazzah"
            },
            {
              "code": "TBS",
              "name": "Ţūbās"
            },
            {
              "code": "TKM",
              "name": "Ţūlkarm"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "PT",
          "name": "Portugal",
          "native": "Portugal",
          "flag": "🇵🇹",
          "phonePrefix": "+351",
          "regions": [
            {
              "code": "20",
              "name": "Açores"
            },
            {
              "code": "01",
              "name": "Aveiro"
            },
            {
              "code": "02",
              "name": "Beja"
            },
            {
              "code": "03",
              "name": "Braga"
            },
            {
              "code": "04",
              "name": "Bragança"
            },
            {
              "code": "05",
              "name": "Castelo Branco"
            },
            {
              "code": "06",
              "name": "Coimbra"
            },
            {
              "code": "07",
              "name": "Évora"
            },
            {
              "code": "08",
              "name": "Faro"
            },
            {
              "code": "09",
              "name": "Guarda"
            },
            {
              "code": "10",
              "name": "Leiria"
            },
            {
              "code": "11",
              "name": "Lisboa"
            },
            {
              "code": "30",
              "name": "Madeira"
            },
            {
              "code": "12",
              "name": "Portalegre"
            },
            {
              "code": "13",
              "name": "Porto"
            },
            {
              "code": "14",
              "name": "Santarém"
            },
            {
              "code": "15",
              "name": "Setúbal"
            },
            {
              "code": "16",
              "name": "Viana do Castelo"
            },
            {
              "code": "17",
              "name": "Vila Real"
            },
            {
              "code": "18",
              "name": "Viseu"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "PW",
          "name": "Palau",
          "native": "Palau",
          "flag": "🇵🇼",
          "phonePrefix": "+680",
          "regions": [
            {
              "code": "002",
              "name": "Aimeliik"
            },
            {
              "code": "004",
              "name": "Airai"
            },
            {
              "code": "010",
              "name": "Angaur"
            },
            {
              "code": "050",
              "name": "Hatobohei"
            },
            {
              "code": "100",
              "name": "Kayangel"
            },
            {
              "code": "150",
              "name": "Koror"
            },
            {
              "code": "212",
              "name": "Melekeok"
            },
            {
              "code": "214",
              "name": "Ngaraard"
            },
            {
              "code": "218",
              "name": "Ngarchelong"
            },
            {
              "code": "222",
              "name": "Ngardmau"
            },
            {
              "code": "224",
              "name": "Ngatpang"
            },
            {
              "code": "226",
              "name": "Ngchesar"
            },
            {
              "code": "227",
              "name": "Ngeremlengui"
            },
            {
              "code": "228",
              "name": "Ngiwal"
            },
            {
              "code": "350",
              "name": "Peleliu"
            },
            {
              "code": "370",
              "name": "Sonsoral"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "PY",
          "name": "Paraguay",
          "native": "Paraguay",
          "flag": "🇵🇾",
          "phonePrefix": "+595",
          "regions": [
            {
              "code": "16",
              "name": "Alto Paraguay"
            },
            {
              "code": "10",
              "name": "Alto Parana"
            },
            {
              "code": "13",
              "name": "Amambay"
            },
            {
              "code": "ASU",
              "name": "Asuncion"
            },
            {
              "code": "5",
              "name": "Caaguazu"
            },
            {
              "code": "6",
              "name": "Caazapa"
            },
            {
              "code": "14",
              "name": "Canindeyu"
            },
            {
              "code": "11",
              "name": "Central"
            },
            {
              "code": "1",
              "name": "Concepcion"
            },
            {
              "code": "3",
              "name": "Cordillera"
            },
            {
              "code": "17",
              "name": "Boqueron"
            },
            {
              "code": "4",
              "name": "Guaira"
            },
            {
              "code": "7",
              "name": "Itapua"
            },
            {
              "code": "8",
              "name": "Misiones"
            },
            {
              "code": "12",
              "name": "Neembucu"
            },
            {
              "code": "9",
              "name": "Paraguari"
            },
            {
              "code": "15",
              "name": "Presidente Hayes"
            },
            {
              "code": "2",
              "name": "San Pedro"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "QA",
          "name": "Qatar",
          "native": "قطر",
          "flag": "🇶🇦",
          "phonePrefix": "+974",
          "regions": [
            {
              "code": "DA",
              "name": "Ad Dawḩah"
            },
            {
              "code": "KH",
              "name": "Al Khawr wa adh Dhakhīrah"
            },
            {
              "code": "WA",
              "name": "Al Wakrah"
            },
            {
              "code": "RA",
              "name": "Ar Rayyān"
            },
            {
              "code": "MS",
              "name": "Ash Shamāl"
            },
            {
              "code": "ZA",
              "name": "Az̧ Za̧`āyin"
            },
            {
              "code": "US",
              "name": "Umm Şalāl"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "RE",
          "name": "Reunion",
          "native": "La Réunion",
          "flag": "🇷🇪",
          "phonePrefix": "+262",
          "regions": [
            {
              "code": null,
              "name": "Réunion"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "RO",
          "name": "Romania",
          "native": "România",
          "flag": "🇷🇴",
          "phonePrefix": "+40",
          "regions": [
            {
              "code": "RO-AB",
              "name": "Alba"
            },
            {
              "code": "RO-AR",
              "name": "Arad"
            },
            {
              "code": "RO-AG",
              "name": "Arges"
            },
            {
              "code": "RO-BC",
              "name": "Bacau"
            },
            {
              "code": "RO-BH",
              "name": "Bihor"
            },
            {
              "code": "RO-BN",
              "name": "Bistrita-Nasaud"
            },
            {
              "code": "RO-BT",
              "name": "Botosani"
            },
            {
              "code": "RO-BR",
              "name": "Braila"
            },
            {
              "code": "RO-BV",
              "name": "Brasov"
            },
            {
              "code": "RO-B",
              "name": "Bucharest"
            },
            {
              "code": "RO-BZ",
              "name": "Buzau"
            },
            {
              "code": "RO-CL",
              "name": "Calarasi"
            },
            {
              "code": "RO-CS",
              "name": "Caras-Severin"
            },
            {
              "code": "RO-CJ",
              "name": "Cluj"
            },
            {
              "code": "RO-CT",
              "name": "Constanta"
            },
            {
              "code": "RO-CV",
              "name": "Covasna"
            },
            {
              "code": "RO-DB",
              "name": "Dambovita"
            },
            {
              "code": "RO-DJ",
              "name": "Dolj"
            },
            {
              "code": "RO-GL",
              "name": "Galati"
            },
            {
              "code": "RO-GR",
              "name": "Giurgiu"
            },
            {
              "code": "RO-GJ",
              "name": "Gorj"
            },
            {
              "code": "RO-HR",
              "name": "Harghita"
            },
            {
              "code": "RO-HD",
              "name": "Hunedoara"
            },
            {
              "code": "RO-IL",
              "name": "Ialomita"
            },
            {
              "code": "RO-IS",
              "name": "Iasi"
            },
            {
              "code": "RO-IF",
              "name": "Iifov"
            },
            {
              "code": "RO-MM",
              "name": "Maramures"
            },
            {
              "code": "RO-MH",
              "name": "Mehedinti"
            },
            {
              "code": "RO-MS",
              "name": "Mures"
            },
            {
              "code": "RO-NT",
              "name": "Neamt"
            },
            {
              "code": "RO-OT",
              "name": "Olt"
            },
            {
              "code": "RO-PH",
              "name": "Prahova"
            },
            {
              "code": "RO-SJ",
              "name": "Salaj"
            },
            {
              "code": "RO-SM",
              "name": "Satu Mare"
            },
            {
              "code": "RO-SB",
              "name": "Sibiu"
            },
            {
              "code": "RO-SV",
              "name": "Suceava"
            },
            {
              "code": "RO-TR",
              "name": "Teleorman"
            },
            {
              "code": "RO-TM",
              "name": "Timis"
            },
            {
              "code": "RO-TL",
              "name": "Tulcea"
            },
            {
              "code": "RO-VL",
              "name": "Valcea"
            },
            {
              "code": "RO-VS",
              "name": "Vaslui"
            },
            {
              "code": "RO-VN",
              "name": "Vrancea"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "RS",
          "name": "Serbia",
          "native": "Србија",
          "flag": "🇷🇸",
          "phonePrefix": "+381",
          "regions": [
            {
              "code": "00",
              "name": "Beograd (Belgrade)"
            },
            {
              "code": "14",
              "name": "Borski"
            },
            {
              "code": "11",
              "name": "Braničevski"
            },
            {
              "code": "23",
              "name": "Jablanički"
            },
            {
              "code": "06",
              "name": "Južnobački"
            },
            {
              "code": "04",
              "name": "Južnobanatski"
            },
            {
              "code": "09",
              "name": "Kolubarski"
            },
            {
              "code": "25",
              "name": "Kosovski"
            },
            {
              "code": "28",
              "name": "Kosovsko-Mitrovački"
            },
            {
              "code": "29",
              "name": "Kosovsko-Pomoravski"
            },
            {
              "code": "08",
              "name": "Mačvanski"
            },
            {
              "code": "17",
              "name": "Moravički"
            },
            {
              "code": "20",
              "name": "Nišavski"
            },
            {
              "code": "24",
              "name": "Pčinjski"
            },
            {
              "code": "26",
              "name": "Pećki"
            },
            {
              "code": "22",
              "name": "Pirotski"
            },
            {
              "code": "10",
              "name": "Podunavski"
            },
            {
              "code": "13",
              "name": "Pomoravski"
            },
            {
              "code": "27",
              "name": "Prizrenski"
            },
            {
              "code": "19",
              "name": "Rasinski"
            },
            {
              "code": "18",
              "name": "Raški"
            },
            {
              "code": "01",
              "name": "Severnobački"
            },
            {
              "code": "03",
              "name": "Severnobanatski"
            },
            {
              "code": "02",
              "name": "Srednjebanatski"
            },
            {
              "code": "07",
              "name": "Sremski"
            },
            {
              "code": "12",
              "name": "Šumadijski"
            },
            {
              "code": "21",
              "name": "Toplički"
            },
            {
              "code": "15",
              "name": "Zaječarski"
            },
            {
              "code": "05",
              "name": "Zapadnobački"
            },
            {
              "code": "16",
              "name": "Zlatiborski"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "RU",
          "name": "Russian Federation",
          "native": "Россия",
          "flag": "🇷🇺",
          "phonePrefix": "+7",
          "regions": [
            {
              "code": "AD",
              "name": "Republic of Adygea"
            },
            {
              "code": "AL",
              "name": "Republic of Altai (Gorno-Altaysk)"
            },
            {
              "code": "ALT",
              "name": "Altai Krai"
            },
            {
              "code": "AMU",
              "name": "Amur Oblast"
            },
            {
              "code": "ARK",
              "name": "Arkhangelsk Oblast"
            },
            {
              "code": "AST",
              "name": "Astrakhan Oblast"
            },
            {
              "code": "BA",
              "name": "Republic of Bashkortostan"
            },
            {
              "code": "BEL",
              "name": "Belgorod Oblast"
            },
            {
              "code": "BRY",
              "name": "Bryansk Oblast"
            },
            {
              "code": "BU",
              "name": "Republic of Buryatia"
            },
            {
              "code": "CE",
              "name": "Chechen Republic"
            },
            {
              "code": "CHE",
              "name": "Chelyabinsk Oblast"
            },
            {
              "code": "CHU",
              "name": "Chukotka Autonomous Okrug"
            },
            {
              "code": "CU",
              "name": "Chuvash Republic"
            },
            {
              "code": "DA",
              "name": "Republic of Dagestan"
            },
            {
              "code": "IN",
              "name": "Republic of Ingushetia"
            },
            {
              "code": "IRK",
              "name": "Irkutsk Oblast"
            },
            {
              "code": "IVA",
              "name": "Ivanovo Oblast"
            },
            {
              "code": "JEW",
              "name": "Jewish Autonomous Oblast"
            },
            {
              "code": "KB",
              "name": "Kabardino-Balkar Republic"
            },
            {
              "code": "KLN",
              "name": "Kaliningrad Oblast"
            },
            {
              "code": "KL",
              "name": "Republic of Kalmykia"
            },
            {
              "code": "KLU",
              "name": "Kaluga Oblast"
            },
            {
              "code": "KAM",
              "name": "Kamchatka Krai"
            },
            {
              "code": "KC",
              "name": "Karachay-Cherkess Republic"
            },
            {
              "code": "KR",
              "name": "Republic of Karelia"
            },
            {
              "code": "KHA",
              "name": "Khabarovsk Krai"
            },
            {
              "code": "KK",
              "name": "Republic of Khakassia"
            },
            {
              "code": "KHM",
              "name": "Khanty-Mansi Autonomous Okrug - Yugra"
            },
            {
              "code": "KEM",
              "name": "Kemerovo Oblast"
            },
            {
              "code": "KIR",
              "name": "Kirov Oblast"
            },
            {
              "code": "KO",
              "name": "Komi Republic"
            },
            {
              "code": "KOS",
              "name": "Kostroma Oblast"
            },
            {
              "code": "KDA",
              "name": "Krasnodar Krai"
            },
            {
              "code": "KYA",
              "name": "Krasnoyarsk Krai"
            },
            {
              "code": "KGN",
              "name": "Kurgan Oblast"
            },
            {
              "code": "KRS",
              "name": "Kursk Oblast"
            },
            {
              "code": "LEN",
              "name": "Leningrad Oblast"
            },
            {
              "code": "LIP",
              "name": "Lipetsk Oblast"
            },
            {
              "code": "MAG",
              "name": "Magadan Oblast"
            },
            {
              "code": "ME",
              "name": "Mari El Republic"
            },
            {
              "code": "MO",
              "name": "Republic of Mordovia"
            },
            {
              "code": "MOS",
              "name": "Moscow Oblast"
            },
            {
              "code": "MOW",
              "name": "Moscow"
            },
            {
              "code": "MU",
              "name": "Murmansk Oblast"
            },
            {
              "code": "NEN",
              "name": "Nenets Autonomous Okrug"
            },
            {
              "code": "NIZ",
              "name": "Nizhny Novgorod Oblast"
            },
            {
              "code": "NGR",
              "name": "Novgorod Oblast"
            },
            {
              "code": "NVS",
              "name": "Novosibirsk Oblast"
            },
            {
              "code": "OMS",
              "name": "Omsk Oblast"
            },
            {
              "code": "ORE",
              "name": "Orenburg Oblast"
            },
            {
              "code": "ORL",
              "name": "Oryol Oblast"
            },
            {
              "code": "PNZ",
              "name": "Penza Oblast"
            },
            {
              "code": "PER",
              "name": "Perm Krai"
            },
            {
              "code": "PRI",
              "name": "Primorsky Krai"
            },
            {
              "code": "PSK",
              "name": "Pskov Oblast"
            },
            {
              "code": "ROS",
              "name": "Rostov Oblast"
            },
            {
              "code": "RYA",
              "name": "Ryazan Oblast"
            },
            {
              "code": "SPE",
              "name": "Saint Petersburg"
            },
            {
              "code": "SA",
              "name": "Sakha (Yakutia) Republic"
            },
            {
              "code": "SAK",
              "name": "Sakhalin Oblast"
            },
            {
              "code": "SAM",
              "name": "Samara Oblast"
            },
            {
              "code": "SAR",
              "name": "Saratov Oblast"
            },
            {
              "code": "NOA",
              "name": "Republic of North Ossetia-Alania"
            },
            {
              "code": "SMO",
              "name": "Smolensk Oblast"
            },
            {
              "code": "STA",
              "name": "Stavropol Krai"
            },
            {
              "code": "SVE",
              "name": "Sverdlovsk Oblast"
            },
            {
              "code": "TAM",
              "name": "Tambov Oblast"
            },
            {
              "code": "TA",
              "name": "Republic of Tatarstan"
            },
            {
              "code": "TOM",
              "name": "Tomsk Oblast"
            },
            {
              "code": "TU",
              "name": "Tuva Republic"
            },
            {
              "code": "TUL",
              "name": "Tula Oblast"
            },
            {
              "code": "TVE",
              "name": "Tver Oblast"
            },
            {
              "code": "TYU",
              "name": "Tyumen Oblast"
            },
            {
              "code": "UD",
              "name": "Udmurt Republic"
            },
            {
              "code": "ULY",
              "name": "Ulyanovsk Oblast"
            },
            {
              "code": "VLA",
              "name": "Vladimir Oblast"
            },
            {
              "code": "VGG",
              "name": "Volgograd Oblast"
            },
            {
              "code": "VLG",
              "name": "Vologda Oblast"
            },
            {
              "code": "VOR",
              "name": "Voronezh Oblast"
            },
            {
              "code": "YAN",
              "name": "Yamalo-Nenets Autonomous Okrug"
            },
            {
              "code": "YAR",
              "name": "Yaroslavl Oblast"
            },
            {
              "code": "ZAB",
              "name": "Zabaykalsky Krai"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "RW",
          "name": "Rwanda",
          "native": "Rwanda",
          "flag": "🇷🇼",
          "phonePrefix": "+250",
          "regions": [
            {
              "code": "01",
              "name": "Kigali"
            },
            {
              "code": "02",
              "name": "Eastern"
            },
            {
              "code": "03",
              "name": "Northern"
            },
            {
              "code": "04",
              "name": "Western"
            },
            {
              "code": "05",
              "name": "Southern"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SA",
          "name": "Saudi Arabia",
          "native": "العربية السعودية",
          "flag": "🇸🇦",
          "phonePrefix": "+966",
          "regions": [
            {
              "code": "14",
              "name": "'Asir"
            },
            {
              "code": "11",
              "name": "Al Bahah"
            },
            {
              "code": "08",
              "name": "Al Hudud ash Shamaliyah"
            },
            {
              "code": "12",
              "name": "Al Jawf"
            },
            {
              "code": "03",
              "name": "Al Madinah al Munawwarah"
            },
            {
              "code": "05",
              "name": "Al Qasim"
            },
            {
              "code": "01",
              "name": "Ar Riyad"
            },
            {
              "code": "04",
              "name": "Ash Sharqiyah"
            },
            {
              "code": "06",
              "name": "Ha'il"
            },
            {
              "code": "09",
              "name": "Jazan"
            },
            {
              "code": "02",
              "name": "Makkah al Mukarramah"
            },
            {
              "code": "10",
              "name": "Najran"
            },
            {
              "code": "07",
              "name": "Tabuk"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SB",
          "name": "Solomon Islands",
          "native": "Solomon Islands",
          "flag": "🇸🇧",
          "phonePrefix": "+677",
          "regions": [
            {
              "code": "CE",
              "name": "Central"
            },
            {
              "code": "CH",
              "name": "Choiseul"
            },
            {
              "code": "GU",
              "name": "Guadalcanal"
            },
            {
              "code": "CT",
              "name": "Honiara"
            },
            {
              "code": "IS",
              "name": "Isabel"
            },
            {
              "code": "MK",
              "name": "Makira-Ulawa"
            },
            {
              "code": "ML",
              "name": "Malaita"
            },
            {
              "code": "RB",
              "name": "Rennell and Bellona"
            },
            {
              "code": "TE",
              "name": "Temotu"
            },
            {
              "code": "WE",
              "name": "Western"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SC",
          "name": "Seychelles",
          "native": "Seychelles",
          "flag": "🇸🇨",
          "phonePrefix": "+248",
          "regions": [
            {
              "code": "01",
              "name": "Anse aux Pins"
            },
            {
              "code": "02",
              "name": "Anse Boileau"
            },
            {
              "code": "03",
              "name": "Anse Etoile"
            },
            {
              "code": "05",
              "name": "Anse Royale"
            },
            {
              "code": "04",
              "name": "Anu Cap"
            },
            {
              "code": "06",
              "name": "Baie Lazare"
            },
            {
              "code": "07",
              "name": "Baie Sainte Anne"
            },
            {
              "code": "08",
              "name": "Beau Vallon"
            },
            {
              "code": "09",
              "name": "Bel Air"
            },
            {
              "code": "10",
              "name": "Bel Ombre"
            },
            {
              "code": "11",
              "name": "Cascade"
            },
            {
              "code": "12",
              "name": "Glacis"
            },
            {
              "code": "13",
              "name": "Grand'Anse Mahe"
            },
            {
              "code": "14",
              "name": "Grand'Anse Praslin"
            },
            {
              "code": "15",
              "name": "La Digue"
            },
            {
              "code": "16",
              "name": "La Riviere Anglaise"
            },
            {
              "code": "24",
              "name": "Les Mamelles"
            },
            {
              "code": "17",
              "name": "Mont Buxton"
            },
            {
              "code": "18",
              "name": "Mont Fleuri"
            },
            {
              "code": "19",
              "name": "Plaisance"
            },
            {
              "code": "20",
              "name": "Pointe La Rue"
            },
            {
              "code": "21",
              "name": "Port Glaud"
            },
            {
              "code": "25",
              "name": "Roche Caiman"
            },
            {
              "code": "22",
              "name": "Saint Louis"
            },
            {
              "code": "23",
              "name": "Takamaka"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SD",
          "name": "Sudan",
          "native": "السودان",
          "flag": "🇸🇩",
          "phonePrefix": "+249",
          "regions": [
            {
              "code": "RS",
              "name": "Al Bahr al Ahmar"
            },
            {
              "code": "GZ",
              "name": "Al Jazirah"
            },
            {
              "code": "KH",
              "name": "Al Khartum"
            },
            {
              "code": "GD",
              "name": "Al Qadarif"
            },
            {
              "code": "NW",
              "name": "An Nil al Abyad"
            },
            {
              "code": "NB",
              "name": "An Nil al Azraq"
            },
            {
              "code": "NO",
              "name": "Ash Shamaliyah"
            },
            {
              "code": "DW",
              "name": "Gharb Darfur"
            },
            {
              "code": "GK",
              "name": "Gharb Kurdufan"
            },
            {
              "code": "DS",
              "name": "Janub Darfur"
            },
            {
              "code": "KS",
              "name": "Janub Kurdufan"
            },
            {
              "code": "KA",
              "name": "Kassala"
            },
            {
              "code": "NR",
              "name": "Nahr an Nil"
            },
            {
              "code": "DN",
              "name": "Shamal Darfur"
            },
            {
              "code": "DE",
              "name": "Sharq Darfur"
            },
            {
              "code": "KN",
              "name": "Shiamal Kurdufan"
            },
            {
              "code": "SI",
              "name": "Sinnar"
            },
            {
              "code": "DC",
              "name": "Wasat Darfur Zalinjay"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SE",
          "name": "Sweden",
          "native": "Sverige",
          "flag": "🇸🇪",
          "phonePrefix": "+46",
          "regions": [
            {
              "code": "K",
              "name": "Blekinge"
            },
            {
              "code": "W",
              "name": "Dalarna"
            },
            {
              "code": "X",
              "name": "Gävleborg"
            },
            {
              "code": "I",
              "name": "Gotland"
            },
            {
              "code": "N",
              "name": "Halland"
            },
            {
              "code": "Z",
              "name": "Jämtland"
            },
            {
              "code": "F",
              "name": "Jönköping"
            },
            {
              "code": "H",
              "name": "Kalmar"
            },
            {
              "code": "G",
              "name": "Kronoberg"
            },
            {
              "code": "BD",
              "name": "Norrbotten"
            },
            {
              "code": "T",
              "name": "Örebro"
            },
            {
              "code": "E",
              "name": "Östergötland"
            },
            {
              "code": "M",
              "name": "Skåne"
            },
            {
              "code": "D",
              "name": "Södermanland"
            },
            {
              "code": "AB",
              "name": "Stockholm"
            },
            {
              "code": "C",
              "name": "Uppsala"
            },
            {
              "code": "S",
              "name": "Värmland"
            },
            {
              "code": "AC",
              "name": "Västerbotten"
            },
            {
              "code": "Y",
              "name": "Västernorrland"
            },
            {
              "code": "U",
              "name": "Västmanland"
            },
            {
              "code": "O",
              "name": "Västra Götaland"
            }
          ],
          "languages": [
            {
              "isoCode": "sv",
              "name": "Swedish",
              "native": "Svenska"
            }
          ],
          "currencies": [
            {
              "isoCode": "SEK",
              "name": "Swedish Krona"
            }
          ]
        },
        {
          "isoCode": "SG",
          "name": "Singapore",
          "native": "Singapore",
          "flag": "🇸🇬",
          "phonePrefix": "+65",
          "regions": [
            {
              "code": "01",
              "name": "Central Singapore"
            },
            {
              "code": "02",
              "name": "North East"
            },
            {
              "code": "03",
              "name": "North West"
            },
            {
              "code": "04",
              "name": "South East"
            },
            {
              "code": "05",
              "name": "South West"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SH",
          "name": "Saint Helena",
          "native": "Saint Helena",
          "flag": "🇸🇭",
          "phonePrefix": "+290",
          "regions": [
            {
              "code": "AC",
              "name": "Ascension"
            },
            {
              "code": "HL",
              "name": "Saint Helena"
            },
            {
              "code": "TA",
              "name": "Tristan da Cunha"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SI",
          "name": "Slovenia",
          "native": "Slovenija",
          "flag": "🇸🇮",
          "phonePrefix": "+386",
          "regions": [
            {
              "code": "001",
              "name": "Ajdovscina"
            },
            {
              "code": "195",
              "name": "Apace"
            },
            {
              "code": "002",
              "name": "Beltinci"
            },
            {
              "code": "148",
              "name": "Benedikt"
            },
            {
              "code": "149",
              "name": "Bistrica ob Sotli"
            },
            {
              "code": "003",
              "name": "Bled"
            },
            {
              "code": "150",
              "name": "Bloke"
            },
            {
              "code": "004",
              "name": "Bohinj"
            },
            {
              "code": "005",
              "name": "Borovnica"
            },
            {
              "code": "006",
              "name": "Bovec"
            },
            {
              "code": "151",
              "name": "Braslovce"
            },
            {
              "code": "007",
              "name": "Brda"
            },
            {
              "code": "009",
              "name": "Brezice"
            },
            {
              "code": "008",
              "name": "Brezovica"
            },
            {
              "code": "152",
              "name": "Cankova"
            },
            {
              "code": "011",
              "name": "Celje"
            },
            {
              "code": "012",
              "name": "Cerklje na Gorenjskem"
            },
            {
              "code": "013",
              "name": "Cerknica"
            },
            {
              "code": "014",
              "name": "Cerkno"
            },
            {
              "code": "153",
              "name": "Cerkvenjak"
            },
            {
              "code": "196",
              "name": "Cirkulane"
            },
            {
              "code": "015",
              "name": "Crensovci"
            },
            {
              "code": "016",
              "name": "Crna na Koroskem"
            },
            {
              "code": "017",
              "name": "Crnomelj"
            },
            {
              "code": "018",
              "name": "Destrnik"
            },
            {
              "code": "019",
              "name": "Divaca"
            },
            {
              "code": "154",
              "name": "Dobje"
            },
            {
              "code": "020",
              "name": "Dobrepolje"
            },
            {
              "code": "155",
              "name": "Dobrna"
            },
            {
              "code": "021",
              "name": "Dobrova-Polhov Gradec"
            },
            {
              "code": "156",
              "name": "Dobrovnik"
            },
            {
              "code": "022",
              "name": "Dol pri Ljubljani"
            },
            {
              "code": "157",
              "name": "Dolenjske Toplice"
            },
            {
              "code": "023",
              "name": "Domzale"
            },
            {
              "code": "024",
              "name": "Dornava"
            },
            {
              "code": "025",
              "name": "Dravograd"
            },
            {
              "code": "026",
              "name": "Duplek"
            },
            {
              "code": "027",
              "name": "Gorenja Vas-Poljane"
            },
            {
              "code": "028",
              "name": "Gorisnica"
            },
            {
              "code": "207",
              "name": "Gorje"
            },
            {
              "code": "029",
              "name": "Gornja Radgona"
            },
            {
              "code": "030",
              "name": "Gornji Grad"
            },
            {
              "code": "031",
              "name": "Gornji Petrovci"
            },
            {
              "code": "158",
              "name": "Grad"
            },
            {
              "code": "032",
              "name": "Grosuplje"
            },
            {
              "code": "159",
              "name": "Hajdina"
            },
            {
              "code": "160",
              "name": "Hoce-Slivnica"
            },
            {
              "code": "161",
              "name": "Hodos"
            },
            {
              "code": "162",
              "name": "Horjul"
            },
            {
              "code": "034",
              "name": "Hrastnik"
            },
            {
              "code": "035",
              "name": "Hrpelje-Kozina"
            },
            {
              "code": "036",
              "name": "Idrija"
            },
            {
              "code": "037",
              "name": "Ig"
            },
            {
              "code": "038",
              "name": "Ilirska Bistrica"
            },
            {
              "code": "039",
              "name": "Ivancna Gorica"
            },
            {
              "code": "040",
              "name": "Izola"
            },
            {
              "code": "041",
              "name": "Jesenice"
            },
            {
              "code": "042",
              "name": "Jursinci"
            },
            {
              "code": "043",
              "name": "Kamnik"
            },
            {
              "code": "044",
              "name": "Kanal"
            },
            {
              "code": "045",
              "name": "Kidricevo"
            },
            {
              "code": "046",
              "name": "Kobarid"
            },
            {
              "code": "047",
              "name": "Kobilje"
            },
            {
              "code": "048",
              "name": "Kocevje"
            },
            {
              "code": "049",
              "name": "Komen"
            },
            {
              "code": "164",
              "name": "Komenda"
            },
            {
              "code": "050",
              "name": "Koper"
            },
            {
              "code": "197",
              "name": "Kodanjevica na Krki"
            },
            {
              "code": "165",
              "name": "Kostel"
            },
            {
              "code": "051",
              "name": "Kozje"
            },
            {
              "code": "052",
              "name": "Kranj"
            },
            {
              "code": "053",
              "name": "Kranjska Gora"
            },
            {
              "code": "166",
              "name": "Krizevci"
            },
            {
              "code": "054",
              "name": "Krsko"
            },
            {
              "code": "055",
              "name": "Kungota"
            },
            {
              "code": "056",
              "name": "Kuzma"
            },
            {
              "code": "057",
              "name": "Lasko"
            },
            {
              "code": "058",
              "name": "Lenart"
            },
            {
              "code": "059",
              "name": "Lendava"
            },
            {
              "code": "060",
              "name": "Litija"
            },
            {
              "code": "061",
              "name": "Ljubljana"
            },
            {
              "code": "062",
              "name": "Ljubno"
            },
            {
              "code": "063",
              "name": "Ljutomer"
            },
            {
              "code": "208",
              "name": "Log-Dragomer"
            },
            {
              "code": "064",
              "name": "Logatec"
            },
            {
              "code": "065",
              "name": "Loska Dolina"
            },
            {
              "code": "066",
              "name": "Loski Potok"
            },
            {
              "code": "167",
              "name": "Lovrenc na Pohorju"
            },
            {
              "code": "068",
              "name": "Lukovica"
            },
            {
              "code": "067",
              "name": "Luce"
            },
            {
              "code": "069",
              "name": "Majsperk"
            },
            {
              "code": "198",
              "name": "Makole"
            },
            {
              "code": "070",
              "name": "Maribor"
            },
            {
              "code": "168",
              "name": "Markovci"
            },
            {
              "code": "071",
              "name": "Medvode"
            },
            {
              "code": "072",
              "name": "Menges"
            },
            {
              "code": "073",
              "name": "Metlika"
            },
            {
              "code": "074",
              "name": "Mezica"
            },
            {
              "code": "169",
              "name": "Miklavz na Dravskem Polju"
            },
            {
              "code": "075",
              "name": "Miren-Kostanjevica"
            },
            {
              "code": "212",
              "name": "Mirna"
            },
            {
              "code": "170",
              "name": "Mirna Pec"
            },
            {
              "code": "076",
              "name": "Mislinja"
            },
            {
              "code": "199",
              "name": "Mokronog-Trebelno"
            },
            {
              "code": "077",
              "name": "Moravce"
            },
            {
              "code": "078",
              "name": "Moravske Toplice"
            },
            {
              "code": "079",
              "name": "Mozirje"
            },
            {
              "code": "080",
              "name": "Murska Sobota"
            },
            {
              "code": "082",
              "name": "Naklo"
            },
            {
              "code": "083",
              "name": "Nazarje"
            },
            {
              "code": "084",
              "name": "Nova Gorica"
            },
            {
              "code": "085",
              "name": "Novo Mesto"
            },
            {
              "code": "086",
              "name": "Odranci"
            },
            {
              "code": "087",
              "name": "Ormoz"
            },
            {
              "code": "088",
              "name": "Osilnica"
            },
            {
              "code": "089",
              "name": "Pesnica"
            },
            {
              "code": "090",
              "name": "Piran"
            },
            {
              "code": "091",
              "name": "Pivka"
            },
            {
              "code": "092",
              "name": "Podcetrtek"
            },
            {
              "code": "172",
              "name": "Podlehnik"
            },
            {
              "code": "093",
              "name": "Podvelka"
            },
            {
              "code": "200",
              "name": "Poljcane"
            },
            {
              "code": "094",
              "name": "Postojna"
            },
            {
              "code": "174",
              "name": "Prebold"
            },
            {
              "code": "095",
              "name": "Preddvor"
            },
            {
              "code": "175",
              "name": "Prevalje"
            },
            {
              "code": "096",
              "name": "Ptuj"
            },
            {
              "code": "098",
              "name": "Race-Fram"
            },
            {
              "code": "099",
              "name": "Radece"
            },
            {
              "code": "100",
              "name": "Radenci"
            },
            {
              "code": "101",
              "name": "Radlje ob Dravi"
            },
            {
              "code": "102",
              "name": "Radovljica"
            },
            {
              "code": "103",
              "name": "Ravne na Koroskem"
            },
            {
              "code": "176",
              "name": "Razkrizje"
            },
            {
              "code": "209",
              "name": "Recica ob Savinji"
            },
            {
              "code": "201",
              "name": "Rence-Vogrsko"
            },
            {
              "code": "104",
              "name": "Ribnica"
            },
            {
              "code": "177",
              "name": "Ribnica na Poboriu"
            },
            {
              "code": "106",
              "name": "Rogaska Slatina"
            },
            {
              "code": "105",
              "name": "Rogasovci"
            },
            {
              "code": "107",
              "name": "Rogatec"
            },
            {
              "code": "108",
              "name": "Ruse"
            },
            {
              "code": "033",
              "name": "Salovci"
            },
            {
              "code": "178",
              "name": "Selnica ob Dravi"
            },
            {
              "code": "109",
              "name": "Semic"
            },
            {
              "code": "183",
              "name": "Sempeter-Vrtojba"
            },
            {
              "code": "117",
              "name": "Sencur"
            },
            {
              "code": "118",
              "name": "Sentilj"
            },
            {
              "code": "119",
              "name": "Sentjernej"
            },
            {
              "code": "120",
              "name": "Sentjur"
            },
            {
              "code": "211",
              "name": "Sentrupert"
            },
            {
              "code": "110",
              "name": "Sevnica"
            },
            {
              "code": "111",
              "name": "Sezana"
            },
            {
              "code": "121",
              "name": "Skocjan"
            },
            {
              "code": "122",
              "name": "Skofja Loka"
            },
            {
              "code": "123",
              "name": "Skofljica"
            },
            {
              "code": "112",
              "name": "Slovenj Gradec"
            },
            {
              "code": "113",
              "name": "Slovenska Bistrica"
            },
            {
              "code": "114",
              "name": "Slovenske Konjice"
            },
            {
              "code": "124",
              "name": "Smarje pri elsah"
            },
            {
              "code": "206",
              "name": "Smarjeske Toplice"
            },
            {
              "code": "125",
              "name": "Smartno ob Paki"
            },
            {
              "code": "194",
              "name": "Smartno pri Litiji"
            },
            {
              "code": "179",
              "name": "Sodrazica"
            },
            {
              "code": "180",
              "name": "Solcava"
            },
            {
              "code": "126",
              "name": "Sostanj"
            },
            {
              "code": "202",
              "name": "Sredisce ob Dravi"
            },
            {
              "code": "115",
              "name": "Starse"
            },
            {
              "code": "127",
              "name": "Store"
            },
            {
              "code": "203",
              "name": "Straza"
            },
            {
              "code": "181",
              "name": "Sveta Ana"
            },
            {
              "code": "204",
              "name": "Sveta Trojica v Slovenskih Goricah"
            },
            {
              "code": "182",
              "name": "Sveta Andraz v Slovenskih Goricah"
            },
            {
              "code": "116",
              "name": "Sveti Jurij"
            },
            {
              "code": "210",
              "name": "Sveti Jurij v Slovenskih Goricah"
            },
            {
              "code": "205",
              "name": "Sveti Tomaz"
            },
            {
              "code": "184",
              "name": "Tabor"
            },
            {
              "code": "010",
              "name": "Tisina"
            },
            {
              "code": "128",
              "name": "Tolmin"
            },
            {
              "code": "129",
              "name": "Trbovlje"
            },
            {
              "code": "130",
              "name": "Trebnje"
            },
            {
              "code": "185",
              "name": "Trnovska Vas"
            },
            {
              "code": "186",
              "name": "Trzin"
            },
            {
              "code": "131",
              "name": "Trzic"
            },
            {
              "code": "132",
              "name": "Turnisce"
            },
            {
              "code": "133",
              "name": "Velenje"
            },
            {
              "code": "187",
              "name": "Velika Polana"
            },
            {
              "code": "134",
              "name": "Velike Lasce"
            },
            {
              "code": "188",
              "name": "Verzej"
            },
            {
              "code": "135",
              "name": "Videm"
            },
            {
              "code": "136",
              "name": "Vipava"
            },
            {
              "code": "137",
              "name": "Vitanje"
            },
            {
              "code": "138",
              "name": "Vodice"
            },
            {
              "code": "139",
              "name": "Vojnik"
            },
            {
              "code": "189",
              "name": "Vransko"
            },
            {
              "code": "140",
              "name": "Vrhnika"
            },
            {
              "code": "141",
              "name": "Vuzenica"
            },
            {
              "code": "142",
              "name": "Zagorje ob Savi"
            },
            {
              "code": "143",
              "name": "Zavrc"
            },
            {
              "code": "144",
              "name": "Zrece"
            },
            {
              "code": "190",
              "name": "Zalec"
            },
            {
              "code": "146",
              "name": "Zelezniki"
            },
            {
              "code": "191",
              "name": "Zetale"
            },
            {
              "code": "147",
              "name": "Ziri"
            },
            {
              "code": "192",
              "name": "Zirovnica"
            },
            {
              "code": "193",
              "name": "Zuzemberk"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "SJ",
          "name": "Svalbard and Jan Mayen",
          "native": "Svalbard og Jan Mayen",
          "flag": "🇸🇯",
          "phonePrefix": "+4779",
          "regions": [],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SK",
          "name": "Slovakia",
          "native": "Slovensko",
          "flag": "🇸🇰",
          "phonePrefix": "+421",
          "regions": [
            {
              "code": "BC",
              "name": "Banskobystricky"
            },
            {
              "code": "BL",
              "name": "Bratislavsky"
            },
            {
              "code": "KI",
              "name": "Kosicky"
            },
            {
              "code": "NI",
              "name": "Nitriansky"
            },
            {
              "code": "PV",
              "name": "Presovsky"
            },
            {
              "code": "TC",
              "name": "Trenciansky"
            },
            {
              "code": "TA",
              "name": "Trnavsky"
            },
            {
              "code": "ZI",
              "name": "Zilinsky"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "SL",
          "name": "Sierra Leone",
          "native": "Sierra Leone",
          "flag": "🇸🇱",
          "phonePrefix": "+232",
          "regions": [
            {
              "code": "E",
              "name": "Eastern"
            },
            {
              "code": "N",
              "name": "Northern"
            },
            {
              "code": "S",
              "name": "Southern"
            },
            {
              "code": "W",
              "name": "Western"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SM",
          "name": "San Marino",
          "native": "San Marino",
          "flag": "🇸🇲",
          "phonePrefix": "+378",
          "regions": [
            {
              "code": "01",
              "name": "Acquaviva"
            },
            {
              "code": "06",
              "name": "Borgo Maggiore"
            },
            {
              "code": "02",
              "name": "Chiesanuova"
            },
            {
              "code": "03",
              "name": "Domagnano"
            },
            {
              "code": "04",
              "name": "Faetano"
            },
            {
              "code": "05",
              "name": "Fiorentino"
            },
            {
              "code": "08",
              "name": "Montegiardino"
            },
            {
              "code": "07",
              "name": "San Marino"
            },
            {
              "code": "09",
              "name": "Serravalle"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "SN",
          "name": "Senegal",
          "native": "Sénégal",
          "flag": "🇸🇳",
          "phonePrefix": "+221",
          "regions": [
            {
              "code": "DK",
              "name": "Dakar"
            },
            {
              "code": "DB",
              "name": "Diourbel"
            },
            {
              "code": "FK",
              "name": "Fatick"
            },
            {
              "code": "KA",
              "name": "Kaffrine"
            },
            {
              "code": "KL",
              "name": "Kaolack"
            },
            {
              "code": "KE",
              "name": "Kedougou"
            },
            {
              "code": "KD",
              "name": "Kolda"
            },
            {
              "code": "LG",
              "name": "Louga"
            },
            {
              "code": "MT",
              "name": "Matam"
            },
            {
              "code": "SL",
              "name": "Saint-Louis"
            },
            {
              "code": "SE",
              "name": "Sedhiou"
            },
            {
              "code": "TC",
              "name": "Tambacounda"
            },
            {
              "code": "TH",
              "name": "Thies"
            },
            {
              "code": "ZG",
              "name": "Ziguinchor"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SO",
          "name": "Somalia",
          "native": "Soomaaliya",
          "flag": "🇸🇴",
          "phonePrefix": "+252",
          "regions": [
            {
              "code": "AW",
              "name": "Awdal"
            },
            {
              "code": "BK",
              "name": "Bakool"
            },
            {
              "code": "BN",
              "name": "Banaadir"
            },
            {
              "code": "BR",
              "name": "Bari"
            },
            {
              "code": "BY",
              "name": "Bay"
            },
            {
              "code": "GA",
              "name": "Galguduud"
            },
            {
              "code": "GE",
              "name": "Gedo"
            },
            {
              "code": "HI",
              "name": "Hiiraan"
            },
            {
              "code": "JD",
              "name": "Jubbada Dhexe"
            },
            {
              "code": "JH",
              "name": "Jubbada Hoose"
            },
            {
              "code": "MU",
              "name": "Mudug"
            },
            {
              "code": "NU",
              "name": "Nugaal"
            },
            {
              "code": "SA",
              "name": "Sanaag"
            },
            {
              "code": "SD",
              "name": "Shabeellaha Dhexe"
            },
            {
              "code": "SH",
              "name": "Shabeellaha Hoose"
            },
            {
              "code": "SO",
              "name": "Sool"
            },
            {
              "code": "TO",
              "name": "Togdheer"
            },
            {
              "code": "WO",
              "name": "Woqooyi Galbeed"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SR",
          "name": "Suriname",
          "native": "Suriname",
          "flag": "🇸🇷",
          "phonePrefix": "+597",
          "regions": [
            {
              "code": "BR",
              "name": "Brokopondo"
            },
            {
              "code": "CM",
              "name": "Commewijne"
            },
            {
              "code": "CR",
              "name": "Coronie"
            },
            {
              "code": "MA",
              "name": "Marowijne"
            },
            {
              "code": "NI",
              "name": "Nickerie"
            },
            {
              "code": "PR",
              "name": "Para"
            },
            {
              "code": "PM",
              "name": "Paramaribo"
            },
            {
              "code": "SA",
              "name": "Saramacca"
            },
            {
              "code": "SI",
              "name": "Sipaliwini"
            },
            {
              "code": "WA",
              "name": "Wanica"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SS",
          "name": "South Sudan",
          "native": "South Sudan",
          "flag": "🇸🇸",
          "phonePrefix": "+211",
          "regions": [
            {
              "code": "CE",
              "name": "Central Equatoria"
            },
            {
              "code": "EE",
              "name": "Eastern Equatoria"
            },
            {
              "code": "JG",
              "name": "Jonglei"
            },
            {
              "code": "LK",
              "name": "Lakes"
            },
            {
              "code": "BN",
              "name": "Northern Bahr el Ghazal"
            },
            {
              "code": "UY",
              "name": "Unity"
            },
            {
              "code": "NU",
              "name": "Upper Nile"
            },
            {
              "code": "WR",
              "name": "Warrap"
            },
            {
              "code": "BW",
              "name": "Western Bahr el Ghazal"
            },
            {
              "code": "EW",
              "name": "Western Equatoria"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ST",
          "name": "Sao Tome and Principe",
          "native": "São Tomé e Príncipe",
          "flag": "🇸🇹",
          "phonePrefix": "+239",
          "regions": [
            {
              "code": "P",
              "name": "Principe"
            },
            {
              "code": "S",
              "name": "Sao Tome"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SV",
          "name": "El Salvador",
          "native": "El Salvador",
          "flag": "🇸🇻",
          "phonePrefix": "+503",
          "regions": [
            {
              "code": "AH",
              "name": "Ahuachapán"
            },
            {
              "code": "CA",
              "name": "Cabañas"
            },
            {
              "code": "CU",
              "name": "Cuscatlán"
            },
            {
              "code": "CH",
              "name": "Chalatenango"
            },
            {
              "code": "LI",
              "name": "La Libertad"
            },
            {
              "code": "PA",
              "name": "La Paz"
            },
            {
              "code": "UN",
              "name": "La Unión"
            },
            {
              "code": "MO",
              "name": "Morazán"
            },
            {
              "code": "SM",
              "name": "San Miguel"
            },
            {
              "code": "SS",
              "name": "San Salvador"
            },
            {
              "code": "SA",
              "name": "Santa Ana"
            },
            {
              "code": "SV",
              "name": "San Vicente"
            },
            {
              "code": "SO",
              "name": "Sonsonate"
            },
            {
              "code": "US",
              "name": "Usulután"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "SX",
          "name": "Sint Maarten (Dutch part)",
          "native": "Sint Maarten",
          "flag": "🇸🇽",
          "phonePrefix": "+1721",
          "regions": [
            {
              "code": null,
              "name": "Sint Maarten"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SY",
          "name": "Syrian Arab Republic",
          "native": "سوريا",
          "flag": "🇸🇾",
          "phonePrefix": "+963",
          "regions": [
            {
              "code": "HA",
              "name": "Al Hasakah"
            },
            {
              "code": "LA",
              "name": "Al Ladhiqiyah"
            },
            {
              "code": "QU",
              "name": "Al Qunaytirah"
            },
            {
              "code": "RA",
              "name": "Ar Raqqah"
            },
            {
              "code": "SU",
              "name": "As Suwayda'"
            },
            {
              "code": "DR",
              "name": "Dar'a"
            },
            {
              "code": "DY",
              "name": "Dayr az Zawr"
            },
            {
              "code": "DI",
              "name": "Dimashq"
            },
            {
              "code": "HL",
              "name": "Halab"
            },
            {
              "code": "HM",
              "name": "Hamah"
            },
            {
              "code": "HI",
              "name": "Hims"
            },
            {
              "code": "ID",
              "name": "Idlib"
            },
            {
              "code": "RD",
              "name": "Rif Dimashq"
            },
            {
              "code": "TA",
              "name": "Tartus"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "SZ",
          "name": "Eswatini",
          "native": "Swaziland",
          "flag": "🇸🇿",
          "phonePrefix": "+268",
          "regions": [
            {
              "code": "HH",
              "name": "Hhohho"
            },
            {
              "code": "LU",
              "name": "Lubombo"
            },
            {
              "code": "MA",
              "name": "Manzini"
            },
            {
              "code": "SH",
              "name": "Shiselweni"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TC",
          "name": "Turks and Caicos Islands",
          "native": "Turks and Caicos Islands",
          "flag": "🇹🇨",
          "phonePrefix": "+1649",
          "regions": [
            {
              "code": null,
              "name": "Turks and Caicos Islands"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "TD",
          "name": "Chad",
          "native": "Tchad",
          "flag": "🇹🇩",
          "phonePrefix": "+235",
          "regions": [
            {
              "code": "BG",
              "name": "Bahr el Ghazal"
            },
            {
              "code": "BA",
              "name": "Batha"
            },
            {
              "code": "BO",
              "name": "Borkou"
            },
            {
              "code": "CB",
              "name": "Chari-Baguirmi"
            },
            {
              "code": "EE",
              "name": "Ennedi-Est"
            },
            {
              "code": "EO",
              "name": "Ennedi-Ouest"
            },
            {
              "code": "GR",
              "name": "Guéra"
            },
            {
              "code": "HL",
              "name": "Hadjer Lamis"
            },
            {
              "code": "KA",
              "name": "Kanem"
            },
            {
              "code": "LC",
              "name": "Lac"
            },
            {
              "code": "LO",
              "name": "Logone Occidental"
            },
            {
              "code": "LR",
              "name": "Logone Oriental"
            },
            {
              "code": "MA",
              "name": "Mondoul"
            },
            {
              "code": "ME",
              "name": "Mayo-Kébbi-Est"
            },
            {
              "code": "MC",
              "name": "Moyen-Chari"
            },
            {
              "code": "OD",
              "name": "Ouaddai"
            },
            {
              "code": "SA",
              "name": "Salamat"
            },
            {
              "code": "SI",
              "name": "Sila"
            },
            {
              "code": "TA",
              "name": "Tandjilé"
            },
            {
              "code": "TI",
              "name": "Tibesti"
            },
            {
              "code": "ND",
              "name": "Ville de Ndjamena"
            },
            {
              "code": "WF",
              "name": "Wadi Fira"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TF",
          "name": "French Southern Territories",
          "native": "Territoire des Terres australes et antarctiques fr",
          "flag": "🇹🇫",
          "phonePrefix": "+262",
          "regions": [
            {
              "code": null,
              "name": "Adelie Land"
            },
            {
              "code": null,
              "name": "Ile Crozet"
            },
            {
              "code": null,
              "name": "Iles Kerguelen"
            },
            {
              "code": null,
              "name": "Iles Saint-Paul et Amsterdam"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "TG",
          "name": "Togo",
          "native": "Togo",
          "flag": "🇹🇬",
          "phonePrefix": "+228",
          "regions": [
            {
              "code": "C",
              "name": "Centre"
            },
            {
              "code": "K",
              "name": "Kara"
            },
            {
              "code": "M",
              "name": "Maritime"
            },
            {
              "code": "P",
              "name": "Plateaux"
            },
            {
              "code": "S",
              "name": "Savannes"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TH",
          "name": "Thailand",
          "native": "ประเทศไทย",
          "flag": "🇹🇭",
          "phonePrefix": "+66",
          "regions": [
            {
              "code": "37",
              "name": "Amnat Charoen"
            },
            {
              "code": "15",
              "name": "Ang Thong"
            },
            {
              "code": "38",
              "name": "Bueng Kan"
            },
            {
              "code": "31",
              "name": "Buri Ram"
            },
            {
              "code": "24",
              "name": "Chachoengsao"
            },
            {
              "code": "18",
              "name": "Chai Nat"
            },
            {
              "code": "36",
              "name": "Chaiyaphum"
            },
            {
              "code": "22",
              "name": "Chanthaburi"
            },
            {
              "code": "50",
              "name": "Chiang Mai"
            },
            {
              "code": "57",
              "name": "Chiang Rai"
            },
            {
              "code": "20",
              "name": "Chon Buri"
            },
            {
              "code": "86",
              "name": "Chumphon"
            },
            {
              "code": "46",
              "name": "Kalasin"
            },
            {
              "code": "62",
              "name": "Kamphaeng Phet"
            },
            {
              "code": "71",
              "name": "Kanchanaburi"
            },
            {
              "code": "40",
              "name": "Khon Kaen"
            },
            {
              "code": "81",
              "name": "Krabi"
            },
            {
              "code": "10",
              "name": "Krung Thep Mahanakhon (Bangkok)"
            },
            {
              "code": "52",
              "name": "Lampang"
            },
            {
              "code": "51",
              "name": "Lamphun"
            },
            {
              "code": "42",
              "name": "Loei"
            },
            {
              "code": "16",
              "name": "Lop Buri"
            },
            {
              "code": "58",
              "name": "Mae Hong Son"
            },
            {
              "code": "44",
              "name": "Maha Sarakham"
            },
            {
              "code": "49",
              "name": "Mukdahan"
            },
            {
              "code": "26",
              "name": "Nakhon Nayok"
            },
            {
              "code": "73",
              "name": "Nakhon Phathom"
            },
            {
              "code": "48",
              "name": "Nakhon Phanom"
            },
            {
              "code": "30",
              "name": "Nakhon Ratchasima"
            },
            {
              "code": "60",
              "name": "Nakhon Sawan"
            },
            {
              "code": "80",
              "name": "Nakhon Si Thammarat"
            },
            {
              "code": "55",
              "name": "Nan"
            },
            {
              "code": "96",
              "name": "Narathiwat"
            },
            {
              "code": "39",
              "name": "Nong Bua Lam Phu"
            },
            {
              "code": "43",
              "name": "Nong Khai"
            },
            {
              "code": "12",
              "name": "Nonthaburi"
            },
            {
              "code": "13",
              "name": "Pathum Thani"
            },
            {
              "code": "94",
              "name": "Pattani"
            },
            {
              "code": "82",
              "name": "Phangnga"
            },
            {
              "code": "93",
              "name": "Phatthalung"
            },
            {
              "code": "56",
              "name": "Phayao"
            },
            {
              "code": "67",
              "name": "Phetchabun"
            },
            {
              "code": "76",
              "name": "Phetchaburi"
            },
            {
              "code": "66",
              "name": "Phichit"
            },
            {
              "code": "65",
              "name": "Phitsanulok"
            },
            {
              "code": "14",
              "name": "Phra Nakhon Si Ayutthaya"
            },
            {
              "code": "54",
              "name": "Phrae"
            },
            {
              "code": "83",
              "name": "Phuket"
            },
            {
              "code": "25",
              "name": "Prachin Buri"
            },
            {
              "code": "77",
              "name": "Prachuap Khiri Khan"
            },
            {
              "code": "85",
              "name": "Ranong"
            },
            {
              "code": "70",
              "name": "Ratchaburi"
            },
            {
              "code": "21",
              "name": "Rayong"
            },
            {
              "code": "45",
              "name": "Roi Et"
            },
            {
              "code": "27",
              "name": "Sa Kaeo"
            },
            {
              "code": "47",
              "name": "Sakon Nakhon"
            },
            {
              "code": "11",
              "name": "Samut Prakan"
            },
            {
              "code": "74",
              "name": "Samut Sakhon"
            },
            {
              "code": "75",
              "name": "Samut Songkhram"
            },
            {
              "code": "19",
              "name": "Saraburi"
            },
            {
              "code": "91",
              "name": "Satun"
            },
            {
              "code": "17",
              "name": "Sing Buri"
            },
            {
              "code": "33",
              "name": "Si Sa ket"
            },
            {
              "code": "90",
              "name": "Songkhla"
            },
            {
              "code": "64",
              "name": "Sukhothai"
            },
            {
              "code": "72",
              "name": "Suphan Buri"
            },
            {
              "code": "84",
              "name": "Surat Thani"
            },
            {
              "code": "32",
              "name": "Surin"
            },
            {
              "code": "63",
              "name": "Tak"
            },
            {
              "code": "92",
              "name": "Trang"
            },
            {
              "code": "23",
              "name": "Trat"
            },
            {
              "code": "34",
              "name": "Ubon Ratchathani"
            },
            {
              "code": "41",
              "name": "Udon Thani"
            },
            {
              "code": "61",
              "name": "Uthai Thani"
            },
            {
              "code": "53",
              "name": "Uttaradit"
            },
            {
              "code": "95",
              "name": "Yala"
            },
            {
              "code": "35",
              "name": "Yasothon"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TJ",
          "name": "Tajikistan",
          "native": "Тоҷикистон",
          "flag": "🇹🇯",
          "phonePrefix": "+992",
          "regions": [
            {
              "code": "DU",
              "name": "Dushanbe"
            },
            {
              "code": "GB",
              "name": "Kŭhistoni Badakhshon"
            },
            {
              "code": "KT",
              "name": "Khatlon"
            },
            {
              "code": "SU",
              "name": "Sughd"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TK",
          "name": "Tokelau",
          "native": "Tokelau",
          "flag": "🇹🇰",
          "phonePrefix": "+690",
          "regions": [
            {
              "code": null,
              "name": "Atafu"
            },
            {
              "code": null,
              "name": "Fakaofo"
            },
            {
              "code": null,
              "name": "Nukunonu"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TL",
          "name": "Timor-Leste",
          "native": "Timor-Leste",
          "flag": "🇹🇱",
          "phonePrefix": "+670",
          "regions": [
            {
              "code": "AL",
              "name": "Aileu"
            },
            {
              "code": "AN",
              "name": "Ainaro"
            },
            {
              "code": "BA",
              "name": "Baucau"
            },
            {
              "code": "BO",
              "name": "Bobonaro"
            },
            {
              "code": "CO",
              "name": "Cova Lima"
            },
            {
              "code": "DI",
              "name": "Dili"
            },
            {
              "code": "ER",
              "name": "Ermera"
            },
            {
              "code": "LA",
              "name": "Lautem"
            },
            {
              "code": "LI",
              "name": "Liquica"
            },
            {
              "code": "MT",
              "name": "Manatuto"
            },
            {
              "code": "MF",
              "name": "Manufahi"
            },
            {
              "code": "OE",
              "name": "Oecussi"
            },
            {
              "code": "VI",
              "name": "Viqueque"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "TM",
          "name": "Turkmenistan",
          "native": "Türkmenistan",
          "flag": "🇹🇲",
          "phonePrefix": "+993",
          "regions": [
            {
              "code": "A",
              "name": "Ahal"
            },
            {
              "code": "S",
              "name": "Asgabat"
            },
            {
              "code": "B",
              "name": "Balkan"
            },
            {
              "code": "D",
              "name": "Dashoguz"
            },
            {
              "code": "L",
              "name": "Lebap"
            },
            {
              "code": "M",
              "name": "Mary"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TN",
          "name": "Tunisia",
          "native": "تونس",
          "flag": "🇹🇳",
          "phonePrefix": "+216",
          "regions": [
            {
              "code": "12",
              "name": "Ariana"
            },
            {
              "code": "31",
              "name": "Beja"
            },
            {
              "code": "13",
              "name": "Ben Arous"
            },
            {
              "code": "23",
              "name": "Bizerte"
            },
            {
              "code": "81",
              "name": "Gabes"
            },
            {
              "code": "71",
              "name": "Gafsa"
            },
            {
              "code": "32",
              "name": "Jendouba"
            },
            {
              "code": "41",
              "name": "Kairouan"
            },
            {
              "code": "42",
              "name": "Kasserine"
            },
            {
              "code": "73",
              "name": "Kebili"
            },
            {
              "code": "33",
              "name": "Kef"
            },
            {
              "code": "53",
              "name": "Mahdia"
            },
            {
              "code": "82",
              "name": "Medenine"
            },
            {
              "code": "52",
              "name": "Monastir"
            },
            {
              "code": "21",
              "name": "Nabeul"
            },
            {
              "code": "61",
              "name": "Sfax"
            },
            {
              "code": "43",
              "name": "Sidi Bouzid"
            },
            {
              "code": "34",
              "name": "Siliana"
            },
            {
              "code": "51",
              "name": "Sousse"
            },
            {
              "code": "83",
              "name": "Tataouine"
            },
            {
              "code": "72",
              "name": "Tozeur"
            },
            {
              "code": "11",
              "name": "Tunis"
            },
            {
              "code": "22",
              "name": "Zaghouan"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TO",
          "name": "Tonga",
          "native": "Tonga",
          "flag": "🇹🇴",
          "phonePrefix": "+676",
          "regions": [
            {
              "code": "01",
              "name": "'Eua"
            },
            {
              "code": "02",
              "name": "Ha'apai"
            },
            {
              "code": "03",
              "name": "Niuas"
            },
            {
              "code": "04",
              "name": "Tongatapu"
            },
            {
              "code": "05",
              "name": "Vava'u"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TR",
          "name": "Türkiye",
          "native": "Türkiye",
          "flag": "🇹🇷",
          "phonePrefix": "+90",
          "regions": [
            {
              "code": "01",
              "name": "Adana"
            },
            {
              "code": "02",
              "name": "Adiyaman"
            },
            {
              "code": "03",
              "name": "Afyonkarahisar"
            },
            {
              "code": "04",
              "name": "Agri"
            },
            {
              "code": "68",
              "name": "Aksaray"
            },
            {
              "code": "05",
              "name": "Amasya"
            },
            {
              "code": "06",
              "name": "Ankara"
            },
            {
              "code": "07",
              "name": "Antalya"
            },
            {
              "code": "75",
              "name": "Ardahan"
            },
            {
              "code": "08",
              "name": "Artvin"
            },
            {
              "code": "09",
              "name": "Aydin"
            },
            {
              "code": "10",
              "name": "Balikesir"
            },
            {
              "code": "74",
              "name": "Bartin"
            },
            {
              "code": "72",
              "name": "Batman"
            },
            {
              "code": "69",
              "name": "Bayburt"
            },
            {
              "code": "11",
              "name": "Bilecik"
            },
            {
              "code": "12",
              "name": "Bingol"
            },
            {
              "code": "13",
              "name": "Bitlis"
            },
            {
              "code": "14",
              "name": "Bolu"
            },
            {
              "code": "15",
              "name": "Burdur"
            },
            {
              "code": "16",
              "name": "Bursa"
            },
            {
              "code": "17",
              "name": "Canakkale"
            },
            {
              "code": "18",
              "name": "Cankiri"
            },
            {
              "code": "19",
              "name": "Corum"
            },
            {
              "code": "20",
              "name": "Denizli"
            },
            {
              "code": "21",
              "name": "Diyarbakir"
            },
            {
              "code": "81",
              "name": "Duzce"
            },
            {
              "code": "22",
              "name": "Edirne"
            },
            {
              "code": "23",
              "name": "Elazig"
            },
            {
              "code": "24",
              "name": "Erzincan"
            },
            {
              "code": "25",
              "name": "Erzurum"
            },
            {
              "code": "26",
              "name": "Eskisehir"
            },
            {
              "code": "27",
              "name": "Gaziantep"
            },
            {
              "code": "28",
              "name": "Giresun"
            },
            {
              "code": "29",
              "name": "Gumushane"
            },
            {
              "code": "30",
              "name": "Hakkari"
            },
            {
              "code": "31",
              "name": "Hatay"
            },
            {
              "code": "76",
              "name": "Igdir"
            },
            {
              "code": "32",
              "name": "Isparta"
            },
            {
              "code": "34",
              "name": "Istanbul"
            },
            {
              "code": "35",
              "name": "Izmir"
            },
            {
              "code": "46",
              "name": "Kahramanmaras"
            },
            {
              "code": "78",
              "name": "Karabuk"
            },
            {
              "code": "70",
              "name": "Karaman"
            },
            {
              "code": "36",
              "name": "Kars"
            },
            {
              "code": "37",
              "name": "Kastamonu"
            },
            {
              "code": "38",
              "name": "Kayseri"
            },
            {
              "code": "79",
              "name": "Kilis"
            },
            {
              "code": "71",
              "name": "Kirikkale"
            },
            {
              "code": "39",
              "name": "Kirklareli"
            },
            {
              "code": "40",
              "name": "Kirsehir"
            },
            {
              "code": "41",
              "name": "Kocaeli"
            },
            {
              "code": "42",
              "name": "Konya"
            },
            {
              "code": "43",
              "name": "Kutahya"
            },
            {
              "code": "44",
              "name": "Malatya"
            },
            {
              "code": "45",
              "name": "Manisa"
            },
            {
              "code": "47",
              "name": "Mardin"
            },
            {
              "code": "33",
              "name": "Mersin"
            },
            {
              "code": "48",
              "name": "Mugla"
            },
            {
              "code": "49",
              "name": "Mus"
            },
            {
              "code": "50",
              "name": "Nevsehir"
            },
            {
              "code": "51",
              "name": "Nigde"
            },
            {
              "code": "52",
              "name": "Ordu"
            },
            {
              "code": "80",
              "name": "Osmaniye"
            },
            {
              "code": "53",
              "name": "Rize"
            },
            {
              "code": "54",
              "name": "Sakarya"
            },
            {
              "code": "55",
              "name": "Samsun"
            },
            {
              "code": "63",
              "name": "Sanliurfa"
            },
            {
              "code": "56",
              "name": "Siirt"
            },
            {
              "code": "57",
              "name": "Sinop"
            },
            {
              "code": "73",
              "name": "Sirnak"
            },
            {
              "code": "58",
              "name": "Sivas"
            },
            {
              "code": "59",
              "name": "Tekirdag"
            },
            {
              "code": "60",
              "name": "Tokat"
            },
            {
              "code": "61",
              "name": "Trabzon"
            },
            {
              "code": "62",
              "name": "Tunceli"
            },
            {
              "code": "64",
              "name": "Usak"
            },
            {
              "code": "65",
              "name": "Van"
            },
            {
              "code": "77",
              "name": "Yalova"
            },
            {
              "code": "66",
              "name": "Yozgat"
            },
            {
              "code": "67",
              "name": "Zonguldak"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TT",
          "name": "Trinidad and Tobago",
          "native": "Trinidad and Tobago",
          "flag": "🇹🇹",
          "phonePrefix": "+1868",
          "regions": [
            {
              "code": "ARI",
              "name": "Arima"
            },
            {
              "code": "CHA",
              "name": "Chaguanas"
            },
            {
              "code": "CTT",
              "name": "Couva-Tabaquite-Talparo"
            },
            {
              "code": "DMN",
              "name": "Diefo Martin"
            },
            {
              "code": "MRC",
              "name": "Mayaro-Rio Claro"
            },
            {
              "code": "PED",
              "name": "Penal-Debe"
            },
            {
              "code": "PTF",
              "name": "Point Fortin"
            },
            {
              "code": "POS",
              "name": "Port-of-Spain"
            },
            {
              "code": "PRT",
              "name": "Princes Town"
            },
            {
              "code": "SFO",
              "name": "San Fernando"
            },
            {
              "code": "SJL",
              "name": "San Juan-Laventille"
            },
            {
              "code": "SGE",
              "name": "Sangre Grande"
            },
            {
              "code": "SIP",
              "name": "Siparia"
            },
            {
              "code": "TOB",
              "name": "Tobago"
            },
            {
              "code": "TUP",
              "name": "Tunapuna-Piarco"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TV",
          "name": "Tuvalu",
          "native": "Tuvalu",
          "flag": "🇹🇻",
          "phonePrefix": "+688",
          "regions": [
            {
              "code": "FUN",
              "name": "Funafuti"
            },
            {
              "code": "NMG",
              "name": "Nanumanga"
            },
            {
              "code": "NMA",
              "name": "Nanumea"
            },
            {
              "code": "NIT",
              "name": "Niutao"
            },
            {
              "code": "NUI",
              "name": "Nui"
            },
            {
              "code": "NKF",
              "name": "Nukufetau"
            },
            {
              "code": "NKL",
              "name": "Nukulaelae"
            },
            {
              "code": "VAU",
              "name": "Vaitupu"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TW",
          "name": "Taiwan, Province of China",
          "native": "臺灣",
          "flag": "🇹🇼",
          "phonePrefix": "+886",
          "regions": [
            {
              "code": "CHA",
              "name": "Changhua"
            },
            {
              "code": "CYQ",
              "name": "Chiayi"
            },
            {
              "code": "HSQ",
              "name": "Hsinchu"
            },
            {
              "code": "HUA",
              "name": "Hualien"
            },
            {
              "code": "KHH",
              "name": "Kaohsiung"
            },
            {
              "code": "KEE",
              "name": "Keelung"
            },
            {
              "code": "KIN",
              "name": "Kinmen"
            },
            {
              "code": "LIE",
              "name": "Lienchiang"
            },
            {
              "code": "MIA",
              "name": "Miaoli"
            },
            {
              "code": "NAN",
              "name": "Nantou"
            },
            {
              "code": "PEN",
              "name": "Penghu"
            },
            {
              "code": "NWT",
              "name": "New Taipei"
            },
            {
              "code": "PIF",
              "name": "Pingtung"
            },
            {
              "code": "TXG",
              "name": "Taichung"
            },
            {
              "code": "TNN",
              "name": "Tainan"
            },
            {
              "code": "TPE",
              "name": "Taipei"
            },
            {
              "code": "TTT",
              "name": "Taitung"
            },
            {
              "code": "TAO",
              "name": "Taoyuan"
            },
            {
              "code": "ILA",
              "name": "Yilan"
            },
            {
              "code": "YUN",
              "name": "Yunlin"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "TZ",
          "name": "United Republic of Tanzania",
          "native": "Tanzania",
          "flag": "🇹🇿",
          "phonePrefix": "+255",
          "regions": [
            {
              "code": "01",
              "name": "Arusha"
            },
            {
              "code": "19",
              "name": "Coast"
            },
            {
              "code": "02",
              "name": "Dar es Salaam"
            },
            {
              "code": "03",
              "name": "Dodoma"
            },
            {
              "code": "04",
              "name": "Iringa"
            },
            {
              "code": "05",
              "name": "Kagera"
            },
            {
              "code": "08",
              "name": "Kigoma"
            },
            {
              "code": "09",
              "name": "Kilimanjaro"
            },
            {
              "code": "12",
              "name": "Lindi"
            },
            {
              "code": "26",
              "name": "Manyara"
            },
            {
              "code": "13",
              "name": "Mara"
            },
            {
              "code": "14",
              "name": "Mbeya"
            },
            {
              "code": "16",
              "name": "Morogoro"
            },
            {
              "code": "17",
              "name": "Mtwara"
            },
            {
              "code": "18",
              "name": "Mwanza"
            },
            {
              "code": "06",
              "name": "Pemba North"
            },
            {
              "code": "10",
              "name": "Pemba South"
            },
            {
              "code": "20",
              "name": "Rukwa"
            },
            {
              "code": "21",
              "name": "Ruvuma"
            },
            {
              "code": "22",
              "name": "Shinyanga"
            },
            {
              "code": "23",
              "name": "Singida"
            },
            {
              "code": "24",
              "name": "Tabora"
            },
            {
              "code": "25",
              "name": "Tanga"
            },
            {
              "code": "07",
              "name": "Zanzibar North"
            },
            {
              "code": "11",
              "name": "Zanzibar Central/South"
            },
            {
              "code": "15",
              "name": "Zanzibar Urban/West"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "UA",
          "name": "Ukraine",
          "native": "Україна",
          "flag": "🇺🇦",
          "phonePrefix": "+380",
          "regions": [
            {
              "code": "71",
              "name": "Cherkaska oblast"
            },
            {
              "code": "74",
              "name": "Chernihivska oblast"
            },
            {
              "code": "77",
              "name": "Chernivetska oblast"
            },
            {
              "code": "12",
              "name": "Dnipropetrovska oblast"
            },
            {
              "code": "14",
              "name": "Donetska oblast"
            },
            {
              "code": "26",
              "name": "Ivano-Frankivska oblast"
            },
            {
              "code": "63",
              "name": "Kharkivska oblast"
            },
            {
              "code": "65",
              "name": "Khersonska oblast"
            },
            {
              "code": "68",
              "name": "Khmelnytska oblast"
            },
            {
              "code": "32",
              "name": "Kyivska oblast"
            },
            {
              "code": "35",
              "name": "Kirovohradska oblast"
            },
            {
              "code": "09",
              "name": "Luhanska oblast"
            },
            {
              "code": "46",
              "name": "Lvivska oblast"
            },
            {
              "code": "48",
              "name": "Mykolaivska oblast"
            },
            {
              "code": "51",
              "name": "Odeska oblast"
            },
            {
              "code": "53",
              "name": "Poltavska oblast"
            },
            {
              "code": "56",
              "name": "Rivnenska oblast"
            },
            {
              "code": "59",
              "name": "Sumska oblast"
            },
            {
              "code": "61",
              "name": "Ternopilska oblast"
            },
            {
              "code": "05",
              "name": "Vinnytska oblast"
            },
            {
              "code": "07",
              "name": "Volynska oblast"
            },
            {
              "code": "21",
              "name": "Zakarpatska oblast"
            },
            {
              "code": "23",
              "name": "Zaporizka oblast"
            },
            {
              "code": "18",
              "name": "Zhytomyrska oblast"
            },
            {
              "code": "43",
              "name": "Avtonomna Respublika Krym"
            },
            {
              "code": "30",
              "name": "Kyiv"
            },
            {
              "code": "40",
              "name": "Sevastopol"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "UG",
          "name": "Uganda",
          "native": "Uganda",
          "flag": "🇺🇬",
          "phonePrefix": "+256",
          "regions": [
            {
              "code": "317",
              "name": "Abim"
            },
            {
              "code": "301",
              "name": "Adjumani"
            },
            {
              "code": "314",
              "name": "Amolatar"
            },
            {
              "code": "216",
              "name": "Amuria"
            },
            {
              "code": "319",
              "name": "Amuru"
            },
            {
              "code": "302",
              "name": "Apac"
            },
            {
              "code": "303",
              "name": "Arua"
            },
            {
              "code": "217",
              "name": "Budaka"
            },
            {
              "code": "223",
              "name": "Bududa"
            },
            {
              "code": "201",
              "name": "Bugiri"
            },
            {
              "code": "224",
              "name": "Bukedea"
            },
            {
              "code": "218",
              "name": "Bukwa"
            },
            {
              "code": "419",
              "name": "Buliisa"
            },
            {
              "code": "401",
              "name": "Bundibugyo"
            },
            {
              "code": "402",
              "name": "Bushenyi"
            },
            {
              "code": "202",
              "name": "Busia"
            },
            {
              "code": "219",
              "name": "Butaleja"
            },
            {
              "code": "318",
              "name": "Dokolo"
            },
            {
              "code": "304",
              "name": "Gulu"
            },
            {
              "code": "403",
              "name": "Hoima"
            },
            {
              "code": "416",
              "name": "Ibanda"
            },
            {
              "code": "203",
              "name": "Iganga"
            },
            {
              "code": "417",
              "name": "Isingiro"
            },
            {
              "code": "204",
              "name": "Jinja"
            },
            {
              "code": "315",
              "name": "Kaabong"
            },
            {
              "code": "404",
              "name": "Kabale"
            },
            {
              "code": "405",
              "name": "Kabarole"
            },
            {
              "code": "213",
              "name": "Kaberamaido"
            },
            {
              "code": "101",
              "name": "Kalangala"
            },
            {
              "code": "220",
              "name": "Kaliro"
            },
            {
              "code": "102",
              "name": "Kampala"
            },
            {
              "code": "205",
              "name": "Kamuli"
            },
            {
              "code": "413",
              "name": "Kamwenge"
            },
            {
              "code": "414",
              "name": "Kanungu"
            },
            {
              "code": "206",
              "name": "Kapchorwa"
            },
            {
              "code": "406",
              "name": "Kasese"
            },
            {
              "code": "207",
              "name": "Katakwi"
            },
            {
              "code": "112",
              "name": "Kayunga"
            },
            {
              "code": "407",
              "name": "Kibaale"
            },
            {
              "code": "103",
              "name": "Kiboga"
            },
            {
              "code": "418",
              "name": "Kiruhura"
            },
            {
              "code": "408",
              "name": "Kisoro"
            },
            {
              "code": "305",
              "name": "Kitgum"
            },
            {
              "code": "316",
              "name": "Koboko"
            },
            {
              "code": "306",
              "name": "Kotido"
            },
            {
              "code": "208",
              "name": "Kumi"
            },
            {
              "code": "415",
              "name": "Kyenjojo"
            },
            {
              "code": "307",
              "name": "Lira"
            },
            {
              "code": "104",
              "name": "Luwero"
            },
            {
              "code": "116",
              "name": "Lyantonde"
            },
            {
              "code": "221",
              "name": "Manafwa"
            },
            {
              "code": "320",
              "name": "Maracha"
            },
            {
              "code": "105",
              "name": "Masaka"
            },
            {
              "code": "409",
              "name": "Masindi"
            },
            {
              "code": "214",
              "name": "Mayuge"
            },
            {
              "code": "209",
              "name": "Mbale"
            },
            {
              "code": "410",
              "name": "Mbarara"
            },
            {
              "code": "114",
              "name": "Mityana"
            },
            {
              "code": "308",
              "name": "Moroto"
            },
            {
              "code": "309",
              "name": "Moyo"
            },
            {
              "code": "106",
              "name": "Mpigi"
            },
            {
              "code": "107",
              "name": "Mubende"
            },
            {
              "code": "108",
              "name": "Mukono"
            },
            {
              "code": "311",
              "name": "Nakapiripirit"
            },
            {
              "code": "115",
              "name": "Nakaseke"
            },
            {
              "code": "109",
              "name": "Nakasongola"
            },
            {
              "code": "222",
              "name": "Namutumba"
            },
            {
              "code": "310",
              "name": "Nebbi"
            },
            {
              "code": "411",
              "name": "Ntungamo"
            },
            {
              "code": "321",
              "name": "Oyam"
            },
            {
              "code": "312",
              "name": "Pader"
            },
            {
              "code": "210",
              "name": "Pallisa"
            },
            {
              "code": "110",
              "name": "Rakai"
            },
            {
              "code": "412",
              "name": "Rukungiri"
            },
            {
              "code": "111",
              "name": "Sembabule"
            },
            {
              "code": "215",
              "name": "Sironko"
            },
            {
              "code": "211",
              "name": "Soroti"
            },
            {
              "code": "212",
              "name": "Tororo"
            },
            {
              "code": "113",
              "name": "Wakiso"
            },
            {
              "code": "313",
              "name": "Yumbe"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "UM",
          "name": "United States Minor Outlying Islands",
          "native": "United States Minor Outlying Islands",
          "flag": "🇺🇲",
          "phonePrefix": "+1",
          "regions": [
            {
              "code": "81",
              "name": "Baker Island"
            },
            {
              "code": "84",
              "name": "Howland Island"
            },
            {
              "code": "86",
              "name": "Jarvis Island"
            },
            {
              "code": "67",
              "name": "Johnston Atoll"
            },
            {
              "code": "89",
              "name": "Kingman Reef"
            },
            {
              "code": "71",
              "name": "Midway Islands"
            },
            {
              "code": "76",
              "name": "Navassa Island"
            },
            {
              "code": "95",
              "name": "Palmyra Atoll"
            },
            {
              "code": "79",
              "name": "Wake Island"
            },
            {
              "code": "BN",
              "name": "Bajo Nuevo Bank"
            },
            {
              "code": "SB",
              "name": "Serranilla Bank"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "US",
          "name": "United States of America",
          "native": "United States",
          "flag": "🇺🇸",
          "phonePrefix": "+1",
          "regions": [
            {
              "code": "AL",
              "name": "Alabama"
            },
            {
              "code": "AK",
              "name": "Alaska"
            },
            {
              "code": "AS",
              "name": "American Samoa"
            },
            {
              "code": "AZ",
              "name": "Arizona"
            },
            {
              "code": "AR",
              "name": "Arkansas"
            },
            {
              "code": "CA",
              "name": "California"
            },
            {
              "code": "CO",
              "name": "Colorado"
            },
            {
              "code": "CT",
              "name": "Connecticut"
            },
            {
              "code": "DE",
              "name": "Delaware"
            },
            {
              "code": "DC",
              "name": "District of Columbia"
            },
            {
              "code": "FM",
              "name": "Micronesia"
            },
            {
              "code": "FL",
              "name": "Florida"
            },
            {
              "code": "GA",
              "name": "Georgia"
            },
            {
              "code": "GU",
              "name": "Guam"
            },
            {
              "code": "HI",
              "name": "Hawaii"
            },
            {
              "code": "ID",
              "name": "Idaho"
            },
            {
              "code": "IL",
              "name": "Illinois"
            },
            {
              "code": "IN",
              "name": "Indiana"
            },
            {
              "code": "IA",
              "name": "Iowa"
            },
            {
              "code": "KS",
              "name": "Kansas"
            },
            {
              "code": "KY",
              "name": "Kentucky"
            },
            {
              "code": "LA",
              "name": "Louisiana"
            },
            {
              "code": "ME",
              "name": "Maine"
            },
            {
              "code": "MH",
              "name": "Marshall Islands"
            },
            {
              "code": "MD",
              "name": "Maryland"
            },
            {
              "code": "MA",
              "name": "Massachusetts"
            },
            {
              "code": "MI",
              "name": "Michigan"
            },
            {
              "code": "MN",
              "name": "Minnesota"
            },
            {
              "code": "MS",
              "name": "Mississippi"
            },
            {
              "code": "MO",
              "name": "Missouri"
            },
            {
              "code": "MT",
              "name": "Montana"
            },
            {
              "code": "NE",
              "name": "Nebraska"
            },
            {
              "code": "NV",
              "name": "Nevada"
            },
            {
              "code": "NH",
              "name": "New Hampshire"
            },
            {
              "code": "NJ",
              "name": "New Jersey"
            },
            {
              "code": "NM",
              "name": "New Mexico"
            },
            {
              "code": "NY",
              "name": "New York"
            },
            {
              "code": "NC",
              "name": "North Carolina"
            },
            {
              "code": "ND",
              "name": "North Dakota"
            },
            {
              "code": "MP",
              "name": "Northern Mariana Islands"
            },
            {
              "code": "OH",
              "name": "Ohio"
            },
            {
              "code": "OK",
              "name": "Oklahoma"
            },
            {
              "code": "OR",
              "name": "Oregon"
            },
            {
              "code": "PW",
              "name": "Palau"
            },
            {
              "code": "PA",
              "name": "Pennsylvania"
            },
            {
              "code": "PR",
              "name": "Puerto Rico"
            },
            {
              "code": "RI",
              "name": "Rhode Island"
            },
            {
              "code": "SC",
              "name": "South Carolina"
            },
            {
              "code": "SD",
              "name": "South Dakota"
            },
            {
              "code": "TN",
              "name": "Tennessee"
            },
            {
              "code": "TX",
              "name": "Texas"
            },
            {
              "code": "UT",
              "name": "Utah"
            },
            {
              "code": "VT",
              "name": "Vermont"
            },
            {
              "code": "VI",
              "name": "Virgin Islands"
            },
            {
              "code": "VA",
              "name": "Virginia"
            },
            {
              "code": "WA",
              "name": "Washington"
            },
            {
              "code": "WV",
              "name": "West Virginia"
            },
            {
              "code": "WI",
              "name": "Wisconsin"
            },
            {
              "code": "WY",
              "name": "Wyoming"
            },
            {
              "code": "AA",
              "name": "Armed Forces Americas"
            },
            {
              "code": "AE",
              "name": "Armed Forces Europe, Canada, Africa and Middle East"
            },
            {
              "code": "AP",
              "name": "Armed Forces Pacific"
            }
          ],
          "languages": [
            {
              "isoCode": "en",
              "name": "English",
              "native": "English"
            }
          ],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "UY",
          "name": "Uruguay",
          "native": "Uruguay",
          "flag": "🇺🇾",
          "phonePrefix": "+598",
          "regions": [
            {
              "code": "AR",
              "name": "Artigas"
            },
            {
              "code": "CA",
              "name": "Canelones"
            },
            {
              "code": "CL",
              "name": "Cerro Largo"
            },
            {
              "code": "CO",
              "name": "Colonia"
            },
            {
              "code": "DU",
              "name": "Durazno"
            },
            {
              "code": "FS",
              "name": "Flores"
            },
            {
              "code": "FD",
              "name": "Florida"
            },
            {
              "code": "LA",
              "name": "Lavalleja"
            },
            {
              "code": "MA",
              "name": "Maldonado"
            },
            {
              "code": "MO",
              "name": "Montevideo"
            },
            {
              "code": "PA",
              "name": "Paysandú"
            },
            {
              "code": "RN",
              "name": "Río Negro"
            },
            {
              "code": "RV",
              "name": "Rivera"
            },
            {
              "code": "RO",
              "name": "Rocha"
            },
            {
              "code": "SA",
              "name": "Salto"
            },
            {
              "code": "SJ",
              "name": "San José"
            },
            {
              "code": "SO",
              "name": "Soriano"
            },
            {
              "code": "TA",
              "name": "Tacuarembó"
            },
            {
              "code": "TT",
              "name": "Treinta y Tres"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "UZ",
          "name": "Uzbekistan",
          "native": "O‘zbekiston",
          "flag": "🇺🇿",
          "phonePrefix": "+998",
          "regions": [
            {
              "code": "TK",
              "name": "Toshkent shahri"
            },
            {
              "code": "AN",
              "name": "Andijon"
            },
            {
              "code": "BU",
              "name": "Buxoro"
            },
            {
              "code": "FA",
              "name": "Farg‘ona"
            },
            {
              "code": "JI",
              "name": "Jizzax"
            },
            {
              "code": "NG",
              "name": "Namangan"
            },
            {
              "code": "NW",
              "name": "Navoiy"
            },
            {
              "code": "QA",
              "name": "Qashqadaryo (Qarshi)"
            },
            {
              "code": "SA",
              "name": "Samarqand"
            },
            {
              "code": "SI",
              "name": "Sirdaryo (Guliston)"
            },
            {
              "code": "SU",
              "name": "Surxondaryo (Termiz)"
            },
            {
              "code": "TO",
              "name": "Toshkent wiloyati"
            },
            {
              "code": "XO",
              "name": "Xorazm (Urganch)"
            },
            {
              "code": "QR",
              "name": "Qoraqalpog‘iston Respublikasi (Nukus)"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "VA",
          "name": "Holy See (Vatican City State)",
          "native": "Vaticano",
          "flag": "🇻🇦",
          "phonePrefix": "+379",
          "regions": [
            {
              "code": "01",
              "name": "Holy See (Vatican City)"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "VC",
          "name": "Saint Vincent and the Grenadines",
          "native": "Saint Vincent and the Grenadines",
          "flag": "🇻🇨",
          "phonePrefix": "+1784",
          "regions": [
            {
              "code": "01",
              "name": "Charlotte"
            },
            {
              "code": "06",
              "name": "Grenadines"
            },
            {
              "code": "02",
              "name": "Saint Andrew"
            },
            {
              "code": "03",
              "name": "Saint David"
            },
            {
              "code": "04",
              "name": "Saint George"
            },
            {
              "code": "05",
              "name": "Saint Patrick"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "VE",
          "name": "Venezuela",
          "native": "Venezuela",
          "flag": "🇻🇪",
          "phonePrefix": "+58",
          "regions": [
            {
              "code": "W",
              "name": "Dependencias Federales"
            },
            {
              "code": "A",
              "name": "Distrito Federal"
            },
            {
              "code": "Z",
              "name": "Amazonas"
            },
            {
              "code": "B",
              "name": "Anzoátegui"
            },
            {
              "code": "C",
              "name": "Apure"
            },
            {
              "code": "D",
              "name": "Aragua"
            },
            {
              "code": "E",
              "name": "Barinas"
            },
            {
              "code": "F",
              "name": "Bolívar"
            },
            {
              "code": "G",
              "name": "Carabobo"
            },
            {
              "code": "H",
              "name": "Cojedes"
            },
            {
              "code": "Y",
              "name": "Delta Amacuro"
            },
            {
              "code": "I",
              "name": "Falcón"
            },
            {
              "code": "J",
              "name": "Guárico"
            },
            {
              "code": "K",
              "name": "Lara"
            },
            {
              "code": "L",
              "name": "Mérida"
            },
            {
              "code": "M",
              "name": "Miranda"
            },
            {
              "code": "N",
              "name": "Monagas"
            },
            {
              "code": "O",
              "name": "Nueva Esparta"
            },
            {
              "code": "P",
              "name": "Portuguesa"
            },
            {
              "code": "R",
              "name": "Sucre"
            },
            {
              "code": "S",
              "name": "Táchira"
            },
            {
              "code": "T",
              "name": "Trujillo"
            },
            {
              "code": "X",
              "name": "Vargas"
            },
            {
              "code": "U",
              "name": "Yaracuy"
            },
            {
              "code": "V",
              "name": "Zulia"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "VG",
          "name": "Virgin Islands, British",
          "native": "British Virgin Islands",
          "flag": "🇻🇬",
          "phonePrefix": "+1284",
          "regions": [
            {
              "code": "ANG",
              "name": "Anegada"
            },
            {
              "code": "JVD",
              "name": "Jost Van Dyke"
            },
            {
              "code": "TTA",
              "name": "Tortola"
            },
            {
              "code": "VGD",
              "name": "Virgin Gorda"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "VI",
          "name": "Virgin Islands, U.S.",
          "native": "United States Virgin Islands",
          "flag": "🇻🇮",
          "phonePrefix": "+1340",
          "regions": [
            {
              "code": "STH",
              "name": "St. Thomas"
            },
            {
              "code": "SJO",
              "name": "St. John"
            },
            {
              "code": "SCR",
              "name": "St. Croix"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            }
          ]
        },
        {
          "isoCode": "VN",
          "name": "Vietnam",
          "native": "Việt Nam",
          "flag": "🇻🇳",
          "phonePrefix": "+84",
          "regions": [
            {
              "code": "44",
              "name": "An Giang"
            },
            {
              "code": "43",
              "name": "Bà Rịa - Vũng Tàu"
            },
            {
              "code": "57",
              "name": "Bình Dương"
            },
            {
              "code": "58",
              "name": "Bình Phước"
            },
            {
              "code": "31",
              "name": "Bình Định"
            },
            {
              "code": "40",
              "name": "Bình Thuận"
            },
            {
              "code": "55",
              "name": "Bạc Liêu"
            },
            {
              "code": "54",
              "name": "Bắc Giang"
            },
            {
              "code": "53",
              "name": "Bắc Kạn"
            },
            {
              "code": "56",
              "name": "Bắc Ninh"
            },
            {
              "code": "50",
              "name": "Bến Tre"
            },
            {
              "code": "04",
              "name": "Cao Bằng"
            },
            {
              "code": "59",
              "name": "Cà Mau"
            },
            {
              "code": "33",
              "name": "Đắk Lắk"
            },
            {
              "code": "72",
              "name": "Đắk Nông"
            },
            {
              "code": "71",
              "name": "Điện Biên"
            },
            {
              "code": "39",
              "name": "Đồng Nai"
            },
            {
              "code": "45",
              "name": "Đồng Tháp"
            },
            {
              "code": "30",
              "name": "Gia Lai"
            },
            {
              "code": "03",
              "name": "Hà Giang"
            },
            {
              "code": "63",
              "name": "Hà Nam"
            },
            {
              "code": "15",
              "name": "Hà Tây"
            },
            {
              "code": "23",
              "name": "Hà Tĩnh"
            },
            {
              "code": "61",
              "name": "Hải Dương"
            },
            {
              "code": "73",
              "name": "Hậu Giang"
            },
            {
              "code": "14",
              "name": "Hòa Bình"
            },
            {
              "code": "66",
              "name": "Hưng Yên"
            },
            {
              "code": "34",
              "name": "Khánh Hòa"
            },
            {
              "code": "47",
              "name": "Kiên Giang"
            },
            {
              "code": "28",
              "name": "Kon Tum"
            },
            {
              "code": "01",
              "name": "Lai Châu"
            },
            {
              "code": "35",
              "name": "Lâm Đồng"
            },
            {
              "code": "09",
              "name": "Lạng Sơn"
            },
            {
              "code": "02",
              "name": "Lào Cai"
            },
            {
              "code": "41",
              "name": "Long An"
            },
            {
              "code": "67",
              "name": "Nam Định"
            },
            {
              "code": "22",
              "name": "Nghệ An"
            },
            {
              "code": "18",
              "name": "Ninh Bình"
            },
            {
              "code": "36",
              "name": "Ninh Thuận"
            },
            {
              "code": "68",
              "name": "Phú Thọ"
            },
            {
              "code": "32",
              "name": "Phú Yên"
            },
            {
              "code": "24",
              "name": "Quảng Bình"
            },
            {
              "code": "27",
              "name": "Quảng Nam"
            },
            {
              "code": "29",
              "name": "Quảng Ngãi"
            },
            {
              "code": "13",
              "name": "Quảng Ninh"
            },
            {
              "code": "25",
              "name": "Quảng Trị"
            },
            {
              "code": "52",
              "name": "Sóc Trăng"
            },
            {
              "code": "05",
              "name": "Sơn La"
            },
            {
              "code": "37",
              "name": "Tây Ninh"
            },
            {
              "code": "20",
              "name": "Thái Bình"
            },
            {
              "code": "69",
              "name": "Thái Nguyên"
            },
            {
              "code": "21",
              "name": "Thanh Hóa"
            },
            {
              "code": "26",
              "name": "Thừa Thiên–Huế"
            },
            {
              "code": "46",
              "name": "Tiền Giang"
            },
            {
              "code": "51",
              "name": "Trà Vinh"
            },
            {
              "code": "07",
              "name": "Tuyên Quang"
            },
            {
              "code": "49",
              "name": "Vĩnh Long"
            },
            {
              "code": "70",
              "name": "Vĩnh Phúc"
            },
            {
              "code": "06",
              "name": "Yên Bái"
            },
            {
              "code": "CT",
              "name": "Cần Thơ"
            },
            {
              "code": "DN",
              "name": "Đà Nẵng"
            },
            {
              "code": "HN",
              "name": "Hà Nội"
            },
            {
              "code": "HP",
              "name": "Hải Phòng"
            },
            {
              "code": "SG",
              "name": "Hồ Chí Minh (Sài Gòn)"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "VU",
          "name": "Vanuatu",
          "native": "Vanuatu",
          "flag": "🇻🇺",
          "phonePrefix": "+678",
          "regions": [
            {
              "code": "MAP",
              "name": "Malampa"
            },
            {
              "code": "PAM",
              "name": "Pénama"
            },
            {
              "code": "SAM",
              "name": "Sanma"
            },
            {
              "code": "SEE",
              "name": "Shéfa"
            },
            {
              "code": "TAE",
              "name": "Taféa"
            },
            {
              "code": "TOB",
              "name": "Torba"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "WF",
          "name": "Wallis and Futuna",
          "native": "Wallis et Futuna",
          "flag": "🇼🇫",
          "phonePrefix": "+681",
          "regions": [
            {
              "code": "ALO",
              "name": "Alo"
            },
            {
              "code": "SIG",
              "name": "Sigave"
            },
            {
              "code": "WAL",
              "name": "Wallis"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "WS",
          "name": "Samoa",
          "native": "Samoa",
          "flag": "🇼🇸",
          "phonePrefix": "+685",
          "regions": [
            {
              "code": "AA",
              "name": "A'ana"
            },
            {
              "code": "AL",
              "name": "Aiga-i-le-Tai"
            },
            {
              "code": "AT",
              "name": "Atua"
            },
            {
              "code": "FA",
              "name": "Fa'asaleleaga"
            },
            {
              "code": "GE",
              "name": "Gaga'emauga"
            },
            {
              "code": "GI",
              "name": "Gagaifomauga"
            },
            {
              "code": "PA",
              "name": "Palauli"
            },
            {
              "code": "SA",
              "name": "Satupa'itea"
            },
            {
              "code": "TU",
              "name": "Tuamasaga"
            },
            {
              "code": "VF",
              "name": "Va'a-o-Fonoti"
            },
            {
              "code": "VS",
              "name": "Vaisigano"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "XK",
          "name": "Kosovo",
          "native": "Republika e Kosovës",
          "flag": "🇽🇰",
          "phonePrefix": "+377,381,383,386",
          "regions": [
            {
              "code": "FZ",
              "name": "Farizaj"
            },
            {
              "code": "GK",
              "name": "Gjakova"
            },
            {
              "code": "GL",
              "name": "Gjilan"
            },
            {
              "code": "MI",
              "name": "Mitrovica"
            },
            {
              "code": "PE",
              "name": "Peja/Peć"
            },
            {
              "code": "PR",
              "name": "Pristina"
            },
            {
              "code": "PZ",
              "name": "Prizren"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "YE",
          "name": "Yemen",
          "native": "اليَمَن",
          "flag": "🇾🇪",
          "phonePrefix": "+967",
          "regions": [
            {
              "code": "AB",
              "name": "Abyān"
            },
            {
              "code": "AD",
              "name": "'Adan"
            },
            {
              "code": "DA",
              "name": "Aḑ Ḑāli'"
            },
            {
              "code": "BA",
              "name": "Al Bayḑā'"
            },
            {
              "code": "HU",
              "name": "Al Ḩudaydah"
            },
            {
              "code": "JA",
              "name": "Al Jawf"
            },
            {
              "code": "MR",
              "name": "Al Mahrah"
            },
            {
              "code": "MW",
              "name": "Al Maḩwīt"
            },
            {
              "code": "AM",
              "name": "'Amrān"
            },
            {
              "code": "DH",
              "name": "Dhamār"
            },
            {
              "code": "HD",
              "name": "Ḩaḑramawt"
            },
            {
              "code": "HJ",
              "name": "Ḩajjah"
            },
            {
              "code": "IB",
              "name": "Ibb"
            },
            {
              "code": "LA",
              "name": "Laḩij"
            },
            {
              "code": "MA",
              "name": "Ma'rib"
            },
            {
              "code": "RA",
              "name": "Raymah"
            },
            {
              "code": "SD",
              "name": "Şā‘dah"
            },
            {
              "code": "SN",
              "name": "Şan‘ā'"
            },
            {
              "code": "SH",
              "name": "Shabwah"
            },
            {
              "code": "TA",
              "name": "Tā‘izz"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "YT",
          "name": "Mayotte",
          "native": "Mayotte",
          "flag": "🇾🇹",
          "phonePrefix": "+262",
          "regions": [
            {
              "code": "01",
              "name": "Dzaoudzi"
            },
            {
              "code": "02",
              "name": "Pamandzi"
            },
            {
              "code": "03",
              "name": "Mamoudzou"
            },
            {
              "code": "04",
              "name": "Dembeni"
            },
            {
              "code": "05",
              "name": "Bandrélé"
            },
            {
              "code": "06",
              "name": "Kani-Kéli"
            },
            {
              "code": "07",
              "name": "Bouéni"
            },
            {
              "code": "08",
              "name": "Chirongui"
            },
            {
              "code": "09",
              "name": "Sada"
            },
            {
              "code": "10",
              "name": "Ouangani"
            },
            {
              "code": "11",
              "name": "Chiconi"
            },
            {
              "code": "12",
              "name": "Tsingoni"
            },
            {
              "code": "13",
              "name": "M'Tsangamouji"
            },
            {
              "code": "14",
              "name": "Acoua"
            },
            {
              "code": "15",
              "name": "Mtsamboro"
            },
            {
              "code": "16",
              "name": "Bandraboua"
            },
            {
              "code": "17",
              "name": "Koungou"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "EUR",
              "name": "Euro"
            }
          ]
        },
        {
          "isoCode": "ZA",
          "name": "South Africa",
          "native": "South Africa",
          "flag": "🇿🇦",
          "phonePrefix": "+27",
          "regions": [
            {
              "code": "EC",
              "name": "Eastern Cape"
            },
            {
              "code": "FS",
              "name": "Free State"
            },
            {
              "code": "GT",
              "name": "Gauteng"
            },
            {
              "code": "NL",
              "name": "KwaZulu-Natal"
            },
            {
              "code": "LP",
              "name": "Limpopo"
            },
            {
              "code": "MP",
              "name": "Mpumalanga"
            },
            {
              "code": "NC",
              "name": "Northern Cape"
            },
            {
              "code": "NW",
              "name": "North West"
            },
            {
              "code": "WC",
              "name": "Western Cape"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ZM",
          "name": "Zambia",
          "native": "Zambia",
          "flag": "🇿🇲",
          "phonePrefix": "+260",
          "regions": [
            {
              "code": "02",
              "name": "Central"
            },
            {
              "code": "08",
              "name": "Copperbelt"
            },
            {
              "code": "03",
              "name": "Eastern"
            },
            {
              "code": "04",
              "name": "Luapula"
            },
            {
              "code": "09",
              "name": "Lusaka"
            },
            {
              "code": "05",
              "name": "Northern"
            },
            {
              "code": "06",
              "name": "North-Western"
            },
            {
              "code": "07",
              "name": "Southern"
            },
            {
              "code": "01",
              "name": "Western"
            }
          ],
          "languages": [],
          "currencies": []
        },
        {
          "isoCode": "ZW",
          "name": "Zimbabwe",
          "native": "Zimbabwe",
          "flag": "🇿🇼",
          "phonePrefix": "+263",
          "regions": [
            {
              "code": "BU",
              "name": "Bulawayo"
            },
            {
              "code": "HA",
              "name": "Harare"
            },
            {
              "code": "MA",
              "name": "Manicaland"
            },
            {
              "code": "MC",
              "name": "Mashonaland Central"
            },
            {
              "code": "ME",
              "name": "Mashonaland East"
            },
            {
              "code": "MW",
              "name": "Mashonaland West"
            },
            {
              "code": "MV",
              "name": "Masvingo"
            },
            {
              "code": "MN",
              "name": "Matabeleland North"
            },
            {
              "code": "MS",
              "name": "Matabeleland South"
            },
            {
              "code": "MI",
              "name": "Midlands"
            }
          ],
          "languages": [],
          "currencies": [
            {
              "isoCode": "USD",
              "name": "US Dollar"
            },
            {
              "isoCode": "GBP",
              "name": "Pound Sterling"
            }
          ]
        }
      ]
    }
  }
  return {
    loading: false,
    error: false,
    data: data?.data.allCountries ?? [],

    // TODO: Can we get rid of this
    dataForForm: data?.data.allCountries?.map(c => ({
      name: c.name,
      value: c.isoCode,
      phonePrefix: c.phonePrefix,
      states: c.regions?.map(r => ({ name: r.name, value: r.code }))
    }))
  };
};